import { useEffect, useMemo, useRef, useState } from 'react'
import { Check, Edit2, Eye, FileText, Mail, MessageCircle, MoreHorizontal, Trash2, X, Paperclip, Search, ArrowUpDown, Calendar, Filter, FilterX, User, Send, RefreshCcw, Settings, ChevronDown, ChevronUp, Link2 } from 'lucide-react'
import { usePersistentTab } from '../../hooks/usePersistentTab'
import { supabase, uploadPublic } from '../../lib/supabaseClient'
import { isPartnerScopedRole } from '../../lib/authorization'
import { ALERT_EVENTS, buildDefaultAlertsMatrix } from '../../lib/communicationRoutineDefaults'
import DailyReportsList from '../../components/DailyReportsList.jsx'
import ConfirmModal from '../../components/ConfirmModal.jsx'
import InfoModal from '../../components/InfoModal.jsx'
import AdminEvaluations from './AdminEvaluations.jsx'
import AdminNotices from './AdminNotices.jsx'
import AdminSmsPanel from './communications/AdminSmsPanel.jsx'

const SYSTEM_VARIABLES = [
  { k: 'nomeUsuario', label: 'Usuário (primeiro nome)', token: '{nomeUsuario}', tokens: ['{nomeUsuario}', '{primeiro_nome}', '{{nome_usuario}}', '{{aluno}}'] },
  { k: 'nomeCompleto', label: 'Usuário (nome completo)', token: '{{nome_completo}}', tokens: ['{{nome_completo}}', '{{nome_usuario_completo}}'] },
  { k: 'emailUsuario', label: 'Usuário (e-mail)', token: '{email}', tokens: ['{email}'] },
  { k: 'cpfUsuario', label: 'Usuário (CPF)', token: '{cpf}', tokens: ['{cpf}'] },
  { k: 'nomeEmpresa', label: 'Empresa', token: '{nomeEmpresa}', tokens: ['{nomeEmpresa}', '{{empresa}}'] },
  { k: 'parceiroSas', label: 'Parceiro SAS', token: '{parceiro}', tokens: ['{parceiro}', '{partnerName}'] },
  { k: 'linkConvite', label: 'Link de convite', token: '{linkConvite}', tokens: ['{linkConvite}', '{{link_convite}}'] },
  { k: 'linkResetSenha', label: 'Link alterar senha', token: '{{link_reset}}', tokens: ['{{link_reset}}', '{linkRedefinicaoSenha}'] },
  { k: 'linkPlataforma', label: 'Link da plataforma', token: '{linkPlataforma}', tokens: ['{linkPlataforma}'] },
  { k: 'linkCertificado', label: 'Link do certificado', token: '{linkCertificado}', tokens: ['{linkCertificado}', '{{url_certificado}}'] },
  { k: 'curso', label: 'Curso', token: '{{curso}}', tokens: ['{{curso}}', '{{CURSO}}'] },
  { k: 'progresso', label: 'Progresso (%)', token: '{{progresso}}', tokens: ['{{progresso}}'] },
  { k: 'prazoConclusao', label: 'Prazo conclusão', token: '{{prazo_conclusao}}', tokens: ['{{prazo_conclusao}}'] },
  { k: 'urlCurso', label: 'Link do curso', token: '{{url_curso}}', tokens: ['{{url_curso}}'] },
  { k: 'urlAcessoCurso', label: 'URL de acesso ao curso (link + código)', token: '{{url_acesso_curso}}', tokens: ['{{url_acesso_curso}}'] },
  { k: 'numeroMatricula', label: 'Número da matrícula', token: '{{numero_matricula}}', tokens: ['{{numero_matricula}}'] },
  { k: 'codigoVerificacao', label: 'Código verificação', token: '{{codigo_verificacao}}', tokens: ['{{codigo_verificacao}}'] },
  { k: 'cargaHoraria', label: 'Carga horária', token: '{{carga_horaria}}', tokens: ['{{carga_horaria}}'] },
  { k: 'dataConclusao', label: 'Data conclusão', token: '{{data_conclusao}}', tokens: ['{{data_conclusao}}'] },
  { k: 'numeroOS', label: 'Nº OS', token: '{numeroOS}', tokens: ['{numeroOS}', '{os_number}', '{os_numero}'] },
  { k: 'linkOS', label: 'Link OS', token: '{linkOS}', tokens: ['{linkOS}', '{os_link}'] },
  { k: 'dataAbertura', label: 'Data abertura', token: '{dataAbertura}', tokens: ['{dataAbertura}', '{data_abertura}'] },
  { k: 'nomeAutorComentario', label: 'Autor comentário', token: '{nomeAutorComentario}', tokens: ['{nomeAutorComentario}', '{os_autor}'] },
  { k: 'textoComentario', label: 'Texto comentário', token: '{textoComentario}', tokens: ['{textoComentario}', '{os_comentario}'] },
  { k: 'ano', label: 'Ano', token: '{{ano}}', tokens: ['{{ano}}'] },
  { k: 'emailSuporte', label: 'E-mail suporte', token: '{{email_suporte}}', tokens: ['{{email_suporte}}'] },
  { k: 'whatsappSuporte', label: 'WhatsApp suporte', token: '{{whatsapp_suporte}}', tokens: ['{{whatsapp_suporte}}'] },
  { k: 'whatsappSuporteLink', label: 'Link WhatsApp suporte', token: '{{whatsapp_suporte_link}}', tokens: ['{{whatsapp_suporte_link}}'] },
  { k: 'horarioAtendimento', label: 'Horário de atendimento (dias e horários)', token: '{{horario_atendimento}}', tokens: ['{{horario_atendimento}}'] },
  { k: 'diasAtendimento', label: 'Dias de atendimento', token: '{{dias_atendimento}}', tokens: ['{{dias_atendimento}}'] },
  { k: 'horarioAtendimentoInicio', label: 'Atendimento (início)', token: '{{hr_inicio}}', tokens: ['{{hr_inicio}}'] },
  { k: 'horarioAtendimentoFim', label: 'Atendimento (fim)', token: '{{hr_fim}}', tokens: ['{{hr_fim}}'] },
]

const buildVariaveisFromArray = (vars) => {
  const arr = Array.isArray(vars) ? vars : []
  const obj = {}
  SYSTEM_VARIABLES.forEach(v => { obj[v.k] = arr.includes(v.k) })
  return obj
}

// Valores de exemplo usados SOMENTE na pré-visualização, para que todas as
// variáveis habilitadas apareçam resolvidas antes do disparo real.
// E-mail/WhatsApp de suporte são resolvidos em runtime com os dados salvos.
const PREVIEW_VARIABLE_SAMPLES = {
  nomeUsuario: 'João',
  nomeCompleto: 'João Silva',
  emailUsuario: 'joao.silva@empresa.com.br',
  cpfUsuario: '123.456.789-00',
  nomeEmpresa: 'Empresa Parceira',
  parceiroSas: 'Parceiro SAS',
  linkConvite: 'https://sofera.com.br/accept-invitation?invite_code=AB12CD&flow=sofera',
  linkResetSenha: 'https://sofera.com.br/reset-password?token=exemplo123',
  linkPlataforma: 'https://sofera.com.br',
  linkCertificado: 'https://sofera.com.br/certificado/123',
  curso: 'Curso de Segurança do Trabalho',
  progresso: '75',
  prazoConclusao: '31/12/2026',
  urlCurso: 'https://sofera.com.br/courses/123',
  urlAcessoCurso: 'https://sofera.com.br/courses/123?turma=456',
  numeroMatricula: '4821',
  codigoVerificacao: 'A1B2C3D4',
  cargaHoraria: '08:00',
  dataConclusao: '15/01/2026',
  numeroOS: 'OS-2026-0001',
  linkOS: 'https://sofera.com.br/admin/service_orders?os_id=789',
  dataAbertura: '02/01/2026 09:30',
  nomeAutorComentario: 'Maria Souza',
  textoComentario: 'Comentário de exemplo para a pré-visualização.',
  ano: String(new Date().getFullYear()),
}

// Lista de emojis com nome (PT-BR) e palavras-chave (PT + EN) para busca
const EMOJI_LIST = [
  // Faces - Sorrisos e positivos
  { e: '😀', n: 'Sorridente', k: 'feliz sorriso alegre grinning happy smile face' },
  { e: '😃', n: 'Sorriso Aberto', k: 'feliz sorriso alegre smiley happy face' },
  { e: '😄', n: 'Sorriso com Olhos', k: 'feliz sorriso alegre smiling eyes happy' },
  { e: '😁', n: 'Sorriso Olhos Sorrindo', k: 'feliz sorriso alegre beaming happy' },
  { e: '😆', n: 'Riso com Olhos Fechados', k: 'risada feliz sorriso laughing squint happy' },
  { e: '😅', n: 'Riso Nervoso', k: 'risada suor nervoso relieved sweat smile' },
  { e: '😂', n: 'Rindo Muito', k: 'risada lagrimas feliz laughing tears joy' },
  { e: '🤣', n: 'Rindo no Chão', k: 'risada muito feliz rolling floor laughing' },
  { e: '😊', n: 'Sorriso Suave', k: 'feliz sorriso suave blush smiling happy' },
  { e: '😇', n: 'Anjo Sorridente', k: 'anjo halo sorriso innocent angel smiling' },
  { e: '🙂', n: 'Leve Sorriso', k: 'sorriso leve feliz slightly smiling happy' },
  { e: '🙃', n: 'Sorriso Invertido', k: 'sorriso invertido upside down sarcasmo' },
  { e: '😉', n: 'Piscando', k: 'piscar wink sorriso feliz flirty' },
  { e: '😌', n: 'Aliviado', k: 'aliviado calmo feliz relieved calm' },
  { e: '😍', n: 'Apaixonado', k: 'amor coracao apaixonado heart eyes love' },
  { e: '🥰', n: 'Apaixonado com Corações', k: 'amor coracao apaixonado smiling hearts love' },
  { e: '😘', n: 'Beijo com Coração', k: 'beijo coracao amor blowing kiss heart love' },
  { e: '😗', n: 'Beijo', k: 'beijo amor kissing face love' },
  { e: '😙', n: 'Beijo com Olhos Sorrindo', k: 'beijo sorriso kissing smiling eyes' },
  { e: '😚', n: 'Beijo com Olhos Fechados', k: 'beijo olhos fechados kissing closed eyes' },
  { e: '😋', n: 'Lambendo os Lábios', k: 'comer gostoso sabor delicious tongue food' },
  { e: '😛', n: 'Mostrando Língua', k: 'lingua brincando tongue teasing face' },
  { e: '😝', n: 'Língua com Olhos Fechados', k: 'lingua brincando squinting tongue' },
  { e: '😜', n: 'Piscando com Língua', k: 'lingua piscar winking tongue playful' },
  { e: '🤪', n: 'Maluco', k: 'maluco doido crazy zany face' },
  { e: '🤨', n: 'Cético', k: 'cetico duvida raised eyebrow skeptical' },
  { e: '🧐', n: 'Com Lente de Aumento', k: 'monoculo analise nerd monocle studying' },
  { e: '🤓', n: 'Nerd', k: 'nerd estudioso glasses geek face' },
  { e: '😎', n: 'De Óculos Escuros', k: 'oculos escuros cool sunglasses face' },
  { e: '🤩', n: 'Estrelado', k: 'estrela impressionado star struck excited' },
  { e: '🥳', n: 'Festejando', k: 'festa aniversario celebration partying' },
  { e: '😏', n: 'Sorriso Malicioso', k: 'malicioso sarcasmo smirk smug' },
  { e: '😒', n: 'Sem Entusiasmo', k: 'sem entusiasmo desanimado unamused meh' },
  { e: '😞', n: 'Desapontado', k: 'desapontado triste disappointed sad' },
  { e: '😔', n: 'Pensativo', k: 'triste pensativo pensive sad' },
  { e: '😟', n: 'Preocupado', k: 'preocupado ansioso worried sad' },
  { e: '😕', n: 'Confuso', k: 'confuso incerto confused frown' },
  { e: '🙁', n: 'Triste Leve', k: 'triste leve slightly frowning sad' },
  { e: '☹️', n: 'Triste', k: 'triste desanimado frowning face sad' },
  { e: '😣', n: 'Perseverando', k: 'esforco dificil persevering struggle' },
  { e: '😖', n: 'Aflito', k: 'angustiado sofrimento confounded anguished' },
  { e: '😫', n: 'Exausto', k: 'cansado exausto tired weary' },
  { e: '😩', n: 'Exausto', k: 'cansado exausto weary tired' },
  { e: '🥺', n: 'Implorando', k: 'pedido olhos grandes pleading begging puppy' },
  { e: '😢', n: 'Chorando', k: 'choro lagrima triste crying tear sad' },
  { e: '😭', n: 'Chorando Muito', k: 'choro lagrimas soluçando loud crying tears' },
  { e: '😤', n: 'Narinha Inchada', k: 'irritado raiva triumph annoyed huff' },
  { e: '😠', n: 'Irritado', k: 'raiva irritado angry mad' },
  { e: '😡', n: 'Furioso', k: 'raiva furioso pouting angry furious' },
  { e: '🤬', n: 'Xingando', k: 'xingando raiva cursing angry swearing' },
  { e: '🤯', n: 'Explodindo a Cabeça', k: 'surpresa chocado exploding head mind blown' },
  { e: '😳', n: 'Envergonhado', k: 'vergonha envergonhado flushed embarrassed' },
  { e: '🥵', n: 'Com Calor', k: 'calor quente hot heated' },
  { e: '🥶', n: 'Com Frio', k: 'frio gelado cold freezing' },
  { e: '😱', n: 'Gritando de Medo', k: 'medo gritando terror screaming fear' },
  { e: '😨', n: 'Assustado', k: 'medo ansioso afraid scared fearful' },
  { e: '😰', n: 'Ansioso com Suor', k: 'ansiedade suor worried cold sweat' },
  { e: '😥', n: 'Disappointed but Relieved', k: 'aliviado triste sad but relieved' },
  { e: '😓', n: 'Suando Frio', k: 'suor esforco downcast sweat' },
  { e: '🤗', n: 'Abraço', k: 'abraço hug hugging care' },
  { e: '🤔', n: 'Pensativo', k: 'pensando duvida thinking chin' },
  { e: '🤭', n: 'Gargalhada', k: 'rindo alto bochechas hand over mouth giggle' },
  { e: '🤫', n: 'Silêncio', k: 'silencio segredo shushing quiet' },
  { e: '🤥', n: 'Mentiroso', k: 'mentira mentiroso lying pinocchio' },
  { e: '😶', n: 'Sem Boca', k: 'silencio sem falar speechless mouthless' },
  { e: '😐', n: 'Neutro', k: 'neutro sem expressao neutral face' },
  { e: '😑', n: 'Sem Expressão', k: 'neutro entediado expressionless' },
  { e: '😬', n: 'Grindo os Dentes', k: 'nervoso dentes grimacing grit' },
  { e: '🙄', n: 'Olhos para Cima', k: 'ironia chateado eye roll annoyed' },
  { e: '😯', n: 'Surpreso', k: 'surpresa chocado hushed surprised' },
  { e: '😦', n: 'Triste com Boca Aberta', k: 'triste surpresa frowning open mouth' },
  { e: '😧', n: 'Angustiado', k: 'preocupado angustiado anguished' },
  { e: '😮', n: 'Boca Aberta', k: 'surpresa boca aberta open mouth surprised' },
  { e: '😲', n: 'Atônito', k: 'surpresa chocado astonished amazed' },
  { e: '🥱', n: 'Bocejando', k: 'sono cansado yawning sleepy tired' },
  { e: '😴', n: 'Dormindo', k: 'sono dormindo sleeping sleepy' },
  { e: '🤤', n: 'Babando', k: 'baba comida drooling saliva' },
  { e: '😪', n: 'Sonolento', k: 'sono cansado sleepy drowsy' },
  { e: '😵', n: 'Atordoado', k: 'tonto atordoado dizzy knocked out' },
  { e: '🤐', n: 'Boca Fechada', k: 'silencio segredo zipper mouth secret' },
  { e: '🥴', n: 'Bêbado', k: 'bebado tonto woozy drunk' },
  { e: '🤢', n: 'Náusea', k: 'nojo enjoado nauseated sick' },
  { e: '🤮', n: 'Vomitando', k: 'vomito enjoado vomiting puke' },
  { e: '🤧', n: 'Espirrando', k: 'espirro alergia doente sneezing sick' },
  { e: '😷', n: 'Máscara', k: 'mascara doente gripe mask sick' },
  { e: '🤒', n: 'Febre', k: 'febre doente termometro thermometer sick' },
  { e: '🤕', n: 'Com Curativo', k: 'machucado ferimento head bandage hurt' },
  { e: '🤑', n: 'Dinheiro na Boca', k: 'dinheiro rico money mouth dollar' },
  { e: '🤠', n: 'Cowboy', k: 'cowboy cauboi hat western' },
  { e: '😈', n: 'Diabo Sorridente', k: 'diabo mau devil horns smiling' },
  { e: '👿', n: 'Diabo Bravo', k: 'diabo raiva angry devil horns' },
  { e: '👹', n: 'Ogro', k: 'monstro ogro devil demon' },
  { e: '👺', n: 'Tengu', k: 'monstro mascara goblin demon' },
  { e: '🤡', n: 'Palhaço', k: 'palhaco circo clown circus' },
  { e: '💩', n: 'Cocô', k: 'coco cocô poop' },
  { e: '👻', n: 'Fantasma', k: 'fantasma halloween ghost spirit' },
  { e: '💀', n: 'Caveira', k: 'caveira morte skull death' },
  { e: '☠️', n: 'Caveira e Ossos', k: 'caveira ossos morte skull bones danger' },
  { e: '👽', n: 'Alienígena', k: 'alienigena et alien extraterrestrial' },
  { e: '👾', n: 'Monstro Alienígena', k: 'alienigena jogo monster space invader' },
  { e: '🤖', n: 'Robô', k: 'robo robot machine android' },
  { e: '🎃', n: 'Abóbora', k: 'abobora halloween jack lantern' },
  { e: '😺', n: 'Gato Sorridente', k: 'gato sorriso happy cat face' },
  { e: '😸', n: 'Gato Sorrindo com Olhos', k: 'gato sorriso happy cat smiling eyes' },
  { e: '😹', n: 'Gato Rindo', k: 'gato risada lagrimas cat tears of joy' },
  { e: '😻', n: 'Gato Apaixonado', k: 'gato amor coracao cat heart eyes love' },
  { e: '😼', n: 'Gato Malicioso', k: 'gato malicioso cat smirk wry' },
  { e: '😽', n: 'Gato Beijando', k: 'gato beijo cat kiss blowing' },
  { e: '🙀', n: 'Gato Cansado', k: 'gato cansado weary cat' },
  { e: '😿', n: 'Gato Chorando', k: 'gato choro triste cat crying' },
  { e: '😾', n: 'Gato Irritado', k: 'gato raiva angry pouting cat' },
  // Mãos
  { e: '👋', n: 'Acenando', k: 'tchau ola aceno wave hello bye' },
  { e: '🤚', n: 'Mão para Cima', k: 'mao parada raised backhand hand' },
  { e: '🖐', n: 'Mão Aberta', k: 'mao aberta stop open hand' },
  { e: '✋', n: 'Mão Levantada', k: 'mao parada raised hand stop high five' },
  { e: '🖖', n: 'Saudação Vulcana', k: 'spock vulcano live long prosper' },
  { e: '👌', n: 'OK', k: 'ok perfeito bom ok hand sign' },
  { e: '🤏', n: 'Pouco', k: 'pouquinho pequeno pinching hand' },
  { e: '✌️', n: 'Paz', k: 'paz vitoria victory peace two fingers' },
  { e: '🤞', n: 'Dedos Cruzados', k: 'sorte cruzado crossed fingers good luck' },
  { e: '🤟', n: 'Rock', k: 'rock metal rock on love gesture' },
  { e: '🤘', n: 'Chifres', k: 'rock metal horns sign of the horns' },
  { e: '🤙', n: 'Ligue-me', k: 'telefone ligar call me hand shaka' },
  { e: '👈', n: 'Apontando Esquerda', k: 'esquerda apontando backhand left' },
  { e: '👉', n: 'Apontando Direita', k: 'direita apontando backhand right' },
  { e: '👆', n: 'Apontando Cima', k: 'cima apontando backhand index up' },
  { e: '🖕', n: 'Dedo do Meio', k: 'ofensa middle finger flip' },
  { e: '👇', n: 'Apontando Baixo', k: 'baixo apontando backhand down' },
  { e: '☝️', n: 'Dedo Indicador para Cima', k: 'cima atenção index pointing up one' },
  { e: '👍', n: 'Joinha', k: 'bom gostei like thumbs up' },
  { e: '👎', n: 'Joinha para Baixo', k: 'ruim nao gostei thumbs down dislike' },
  { e: '✊', n: 'Punho Levantado', k: 'punho forca luta raised fist' },
  { e: '👊', n: 'Punho em Frente', k: 'punho golpe oncoming fist' },
  { e: '🤛', n: 'Punho Esquerdo', k: 'punho esquerda left facing fist' },
  { e: '🤜', n: 'Punho Direito', k: 'punho direita right facing fist' },
  { e: '👏', n: 'Palmas', k: 'aplauso palmas clapping applause' },
  { e: '🙌', n: 'Mãos para Cima', k: 'celebrar vitoria raising hands celebration' },
  { e: '👐', n: 'Mãos Abertas', k: 'mao aberta hug open hands' },
  { e: '🤲', n: 'Mãos Juntas para Cima', k: 'pedido oracao palms up together' },
  { e: '🤝', n: 'Aperto de Mão', k: 'aperto acordo handshake deal' },
  { e: '🙏', n: 'Oração / Obrigado', k: 'oracao obrigado por favor pray please thank you' },
  { e: '✍️', n: 'Escrevendo', k: 'escrever caneta writing hand pen' },
  { e: '💅', n: 'Esmalte', k: 'unhas esmalte manicure nail polish' },
  { e: '🤳', n: 'Selfie', k: 'selfie foto camera phone' },
  { e: '💪', n: 'Músculo', k: 'forca musculoso bicep flex strength' },
  // Natureza
  { e: '☀️', n: 'Sol', k: 'sol dia calor sunny sun' },
  { e: '🌤', n: 'Sol com Poucas Nuvens', k: 'sol nuvem partly cloudy sun small cloud' },
  { e: '⛅️', n: 'Sol Atrás de Nuvem', k: 'sol nuvem cloudy sun behind cloud' },
  { e: '☁️', n: 'Nuvem', k: 'nuvem céu cloudy cloud' },
  { e: '🌦', n: 'Nuvem com Chuva e Sol', k: 'chuva sol nuvem sun rain cloud' },
  { e: '🌧', n: 'Nuvem com Chuva', k: 'chuva nuvem rain cloud' },
  { e: '⛈', n: 'Tempestade', k: 'tempestade trovão raio thunderstorm rain' },
  { e: '🌩', n: 'Nuvem com Relâmpago', k: 'raio trovão cloud lightning' },
  { e: '🌨', n: 'Nuvem com Neve', k: 'neve frio cloud snow' },
  { e: '❄️', n: 'Floco de Neve', k: 'neve frio snowflake cold' },
  { e: '☃️', n: 'Boneco de Neve', k: 'boneco neve snowman winter' },
  { e: '⛄️', n: 'Boneco de Neve sem Neve', k: 'boneco neve snowman without snow' },
  { e: '🌬', n: 'Vento', k: 'vento ventania wind face blowing' },
  { e: '💨', n: 'Rajada de Vento', k: 'vento rapido wind dash' },
  { e: '💧', n: 'Gota de Água', k: 'agua gota water droplet' },
  { e: '💦', n: 'Gotas de Suor', k: 'suor agua water sweat droplets' },
  { e: '☔️', n: 'Guarda-Chuva com Gotas', k: 'chuva guarda chuva umbrella rain' },
  { e: '☂️', n: 'Guarda-Chuva', k: 'chuva guarda chuva umbrella' },
  { e: '🌊', n: 'Onda', k: 'mar onda water wave ocean' },
  { e: '🌫', n: 'Névoa', k: 'neblina nevoa fog face' },
  { e: '🌈', n: 'Arco-Íris', k: 'arco iris rainbow' },
  { e: '⚡️', n: 'Raio', k: 'raio energia lightning zap' },
  { e: '✨', n: 'Brilho', k: 'brilho sparkle magic sparkles' },
  { e: '⭐️', n: 'Estrela', k: 'estrela star' },
  { e: '🌟', n: 'Estrela Brilhante', k: 'estrela brilho glowing star' },
  { e: '💫', n: 'Estrela Tonta', k: 'estrela tonta dizzy star' },
  { e: '☄️', n: 'Cometa', k: 'cometa espaço comet' },
  { e: '🌙', n: 'Lua Crescente', k: 'lua crescente crescent moon' },
  { e: '🌚', n: 'Lua com Rosto', k: 'lua rosto new moon face' },
  { e: '🌑', n: 'Lua Nova', k: 'lua nova new moon' },
  { e: '🌒', n: 'Lua Crescente Primeiro Quarto', k: 'lua crescente waxing moon' },
  { e: '🌓', n: 'Lua Primeiro Quarto', k: 'lua quarto first quarter moon' },
  { e: '🌔', n: 'Lua Gibosa Crescente', k: 'lua gibosa waxing gibbous moon' },
  { e: '🌕', n: 'Lua Cheia', k: 'lua cheia full moon' },
  { e: '🌖', n: 'Lua Gibosa Minguante', k: 'lua gibosa minguante waning gibbous' },
  { e: '🌗', n: 'Lua Último Quarto', k: 'lua quarto last quarter moon' },
  { e: '🌘', n: 'Lua Minguante', k: 'lua minguante waning crescent moon' },
  { e: '🪐', n: 'Saturno', k: 'planeta saturno aneis planet ringed' },
  // Objetos e Comunicação
  { e: '📱', n: 'Celular', k: 'celular telefone mobile phone smartphone' },
  { e: '📲', n: 'Celular com Seta', k: 'celular receber mobile incoming arrow' },
  { e: '☎️', n: 'Telefone', k: 'telefone fixo telephone phone' },
  { e: '📞', n: 'Telefone Recebendo', k: 'telefone chamada telephone receiver' },
  { e: '📟', n: 'Pager', k: 'pager beeper' },
  { e: '📠', n: 'Fax', k: 'fax máquina fax machine' },
  { e: '⏰', n: 'Relógio', k: 'relogio relógio hora horario tempo prazo clock alarm time' },
  { e: '🔋', n: 'Bateria', k: 'bateria energia battery' },
  { e: '🔌', n: 'Tomada', k: 'tomada energia plug power' },
  { e: '💻', n: 'Notebook', k: 'computador laptop laptop pc' },
  { e: '🖥', n: 'Computador de Mesa', k: 'computador desktop monitor pc' },
  { e: '🖨', n: 'Impressora', k: 'impressora printer' },
  { e: '⌨️', n: 'Teclado', k: 'teclado keyboard' },
  { e: '🖱', n: 'Mouse', k: 'mouse computador computer mouse' },
  { e: '🖲', n: 'Trackball', k: 'mouse trackball' },
  { e: '💽', n: 'Disco', k: 'disco floppy minidisc' },
  { e: '💾', n: 'Disquete', k: 'salvar disquete floppy disk save' },
  { e: '💿', n: 'CD', k: 'cd disco compact disc' },
  { e: '📀', n: 'DVD', k: 'dvd disco dvd disc' },
  { e: '🎥', n: 'Filmadora', k: 'camera video filme movie camera' },
  { e: '🎞', n: 'Rolo de Filme', k: 'filme cinema film frames' },
  { e: '📽', n: 'Projetor de Filme', k: 'projetor cinema film projector' },
  { e: '🎬', n: 'Claquete', k: 'cinema filme clapper board movie' },
  { e: '📺', n: 'Televisão', k: 'tv televisão television' },
  { e: '📷', n: 'Câmera', k: 'camera foto camera photographic' },
  { e: '📸', n: 'Câmera com Flash', k: 'camera foto flash camera with flash' },
  { e: '📹', n: 'Filmadora de Vídeo', k: 'camera video video camera' },
  { e: '📼', n: 'Fita Cassete de Vídeo', k: 'fita video vhs videotape' },
  { e: '🔍', n: 'Lupa Esquerda', k: 'busca procurar search magnifying glass left' },
  { e: '🔎', n: 'Lupa Direita', k: 'busca procurar search magnifying glass right' },
  { e: '🕯', n: 'Vela', k: 'vela luz candle' },
  { e: '💡', n: 'Lâmpada', k: 'lampada ideia luz light bulb idea' },
  { e: '🔦', n: 'Lanterna', k: 'lanterna luz flashlight' },
  { e: '🏮', n: 'Lanterna Vermelha', k: 'lanterna vermelha red paper lantern' },
  { e: '📔', n: 'Caderno com Capa', k: 'caderno notebook with cover' },
  { e: '📕', n: 'Livro Vermelho', k: 'livro vermelho closed book red' },
  { e: '📖', n: 'Livro Aberto', k: 'livro leitura open book reading' },
  { e: '📗', n: 'Livro Verde', k: 'livro verde closed book green' },
  { e: '📘', n: 'Livro Azul', k: 'livro azul closed book blue' },
  { e: '📙', n: 'Livro Laranja', k: 'livro laranja closed book orange' },
  { e: '📚', n: 'Livros', k: 'livros biblioteca books stack' },
  { e: '📓', n: 'Caderno', k: 'caderno notebook' },
  { e: '📒', n: 'Livro de Registros', k: 'caderno registro ledger notebook' },
  { e: '📃', n: 'Página com Margem', k: 'documento pagina page with curl' },
  { e: '📜', n: 'Pergaminho', k: 'pergaminho documento scroll' },
  { e: '📄', n: 'Documento', k: 'documento pagina page facing up' },
  { e: '📰', n: 'Jornal', k: 'jornal noticias newspaper news' },
  { e: '🗞', n: 'Jornal Enrolado', k: 'jornal rolled up newspaper' },
  { e: '📑', n: 'Abas de Marcador', k: 'marcador abas bookmark tabs' },
  { e: '🔖', n: 'Marcador', k: 'marcador bookmark' },
  { e: '🏷', n: 'Etiqueta', k: 'etiqueta preço label tag' },
  { e: '💰', n: 'Saco de Dinheiro', k: 'dinheiro dinheiro money bag' },
  { e: '💴', n: 'Iene', k: 'dinheiro iene yen banknote' },
  { e: '💵', n: 'Dólar', k: 'dinheiro dolar dollar banknote' },
  { e: '💶', n: 'Euro', k: 'dinheiro euro euro banknote' },
  { e: '💷', n: 'Libra', k: 'dinheiro libra pound banknote' },
  { e: '💸', n: 'Dinheiro com Asas', k: 'dinheiro voando money with wings' },
  { e: '💳', n: 'Cartão de Crédito', k: 'cartao credito credit card' },
  { e: '🧾', n: 'Recibo', k: 'recibo nota receipt' },
  { e: '✉️', n: 'Envelope', k: 'email carta envelope mail letter' },
  { e: '📧', n: 'E-mail', k: 'email envelope e-mail' },
  { e: '📨', n: 'Envelope com Seta Descendo', k: 'email receber incoming envelope' },
  { e: '📩', n: 'Envelope com Seta Subindo', k: 'email enviar outgoing envelope' },
  { e: '📤', n: 'Caixa de Envio', k: 'enviar outbox tray up' },
  { e: '📥', n: 'Caixa de Entrada', k: 'receber inbox tray down' },
  { e: '📦', n: 'Pacote', k: 'caixa pacote package box' },
  // Símbolos
  { e: '✅', n: 'Check Marcado', k: 'sim ok check mark button' },
  { e: '☑️', n: 'Check na Caixa', k: 'sim ok check box with check' },
  { e: '✔️', n: 'Check', k: 'sim ok check mark heavy' },
  { e: '❌', n: 'X Vermelho', k: 'nao errado cancelar cross mark x' },
  { e: '❎', n: 'X na Caixa', k: 'nao errado cross mark button' },
  { e: '➕', n: 'Mais', k: 'mais adicionar plus sign' },
  { e: '➖', n: 'Menos', k: 'menos subtrair minus sign' },
  { e: '➗', n: 'Divisão', k: 'dividir divide sign' },
  { e: '➰', n: 'Loop', k: 'loop curva curly loop' },
  { e: '➿', n: 'Loop Duplo', k: 'loop duplo double curly loop' },
  { e: '〽️', n: 'Parte Alternada', k: 'curva alternada part alternation' },
  { e: '✳️', n: 'Oito Pontos', k: 'estrela oito oito pontos eight spoked' },
  { e: '✴️', n: 'Oito Pontos Pontiagudos', k: 'estrela oito eight pointed black' },
  { e: '❇️', n: 'Brilho Oito Pontos', k: 'brilho sparkle eight pointed' },
  { e: '‼️', n: 'Ponto de Exclamação Duplo', k: 'exclamacao duplo double exclamation' },
  { e: '⁉️', n: 'Exclamação com Interrogação', k: 'duvida exclamacao interrobang' },
  { e: '❓', n: 'Ponto de Interrogação', k: 'pergunta duvida question mark' },
  { e: '❔', n: 'Interrogação Branco', k: 'pergunta duvida white question mark' },
  { e: '❕', n: 'Exclamação Branco', k: 'exclamacao white exclamation' },
  { e: '❗', n: 'Ponto de Exclamação', k: 'exclamacao atencao exclamation mark' },
  { e: '〰️', n: 'Ondulado', k: 'linha ondulada wavy dash' },
  { e: '©', n: 'Copyright', k: 'copyright direitos autorais' },
  { e: '®', n: 'Marcas Registradas', k: 'registrado marca registered' },
  { e: '™', n: 'Marca Registrada', k: 'marca trademark' },
  // Bandeiras
  { e: '🇧🇷', n: 'Brasil', k: 'brasil bandeira brazil flag' },
  { e: '🇺🇸', n: 'Estados Unidos', k: 'eua estados unidos usa flag' },
  { e: '🇪🇸', n: 'Espanha', k: 'espanha espanha spain flag' },
  { e: '🇫🇷', n: 'França', k: 'franca frança france flag' },
  { e: '🇩🇪', n: 'Alemanha', k: 'alemanha germany flag' },
  { e: '🇮🇹', n: 'Itália', k: 'italia itália italy flag' },
  { e: '🇵🇹', n: 'Portugal', k: 'portugal flag' },
  { e: '🏳️‍🌈', n: 'Arco-Íris LGBTQ+', k: 'arco iris lgbt pride rainbow flag' },
  { e: '🏳️‍⚧️', n: 'Transgênero', k: 'trans genero transgender pride flag' },
]

const RichEditor = ({ value, onChange }) => {
  const ref = useRef(null)
  useEffect(() => {
    if (ref.current && value !== ref.current.innerHTML) {
      ref.current.innerHTML = value || ''
    }
  }, [value])
  const cmd = (c) => { document.execCommand(c, false); onChange(ref.current?.innerHTML || '') }

  const insertTextAtCursor = (text) => {
    if (!ref.current) return
    ref.current.focus()
    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0) return
    let range = sel.getRangeAt(0)
    range.deleteContents()
    const node = document.createTextNode(text)
    range.insertNode(node)
    range = range.cloneRange()
    range.setStartAfter(node)
    range.collapse(true)
    sel.removeAllRanges()
    sel.addRange(range)
    onChange(ref.current.innerHTML || '')
  }
  return (
    <div>
      <div className="flex gap-2 mb-2">
        <button type="button" className="px-2 py-1 text-sm rounded bg-slate-800 text-slate-200" onClick={() => cmd('bold')}>B</button>
        <button type="button" className="px-2 py-1 text-sm rounded bg-slate-800 text-slate-200 italic" onClick={() => cmd('italic')}>I</button>
        <button type="button" className="px-2 py-1 text-sm rounded bg-slate-800 text-slate-200" onClick={() => cmd('insertUnorderedList')}>• Lista</button>
        <button type="button" className="px-2 py-1 text-sm rounded bg-slate-800 text-slate-200" onClick={() => cmd('insertOrderedList')}>1. Lista</button>
      </div>
      <div
        ref={ref}
        contentEditable
        className="min-h-[260px] bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition prose prose-invert max-w-none"
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault()
          const token = e.dataTransfer.getData('text/variable-token') || e.dataTransfer.getData('text/plain')
          if (token) insertTextAtCursor(token)
        }}
      ></div>
    </div>
  )
}

const ToggleSwitch = ({ checked, onChange, ariaLabel }) => (
  <button
    type="button"
    onClick={onChange}
    className={`${checked ? 'bg-orange-500' : 'bg-slate-700'} w-10 h-5 rounded-full relative transition-colors duration-300`}
    aria-pressed={checked}
    aria-label={ariaLabel}
  >
    <div className={`${checked ? 'left-6' : 'left-1'} absolute top-1 w-3 h-3 rounded-full bg-white transition-all duration-300 shadow-sm`}></div>
  </button>
)

// Badges das opções de uso do template (meta.usos). Mostra apenas o que está
// marcado no modal; quando nada está marcado, não renderiza nada. Fica em linha
// própria para não cortar o conteúdo ao lado do canal/tipo.
const TemplateUsageBadges = ({ meta }) => {
  const usos = meta?.usos || {}
  if (!usos.convite_padrao && !usos.convite_link) return null
  return (
    <div className="flex flex-wrap items-center gap-2 mt-2">
      {usos.convite_padrao && (
        <span className="text-[10px] leading-none bg-amber-500/10 text-amber-400 px-2 py-1 rounded uppercase font-bold tracking-wider border border-amber-500/20 inline-flex items-center gap-1 whitespace-nowrap" title="Disparado no Convite Padrão">
          <Send size={11} /> Convite Padrão
        </span>
      )}
      {usos.convite_link && (
        <span className="text-[10px] leading-none bg-orange-500/10 text-orange-400 px-2 py-1 rounded uppercase font-bold tracking-wider border border-orange-500/20 inline-flex items-center gap-1 whitespace-nowrap" title="Disparado no Convite Link+código">
          <Link2 size={11} /> Convite Link+código
        </span>
      )}
    </div>
  )
}

const normalizeWhatsappNumber = (value) => String(value || '').replace(/\D/g, '')

// Remove o DDI 55 (Brasil) do número, se presente.
// Só remove quando o total tem 12/13 dígitos (DDI + DDD + número), preservando
// o DDD 55 (número com 10/11 dígitos).
const stripWhatsappCountryCode = (value) => {
  const digits = normalizeWhatsappNumber(value)
  if ((digits.length === 12 || digits.length === 13) && digits.startsWith('55')) {
    return digits.slice(2)
  }
  return digits
}

// Formata o número para exibição: "DDD NNNN-NNNN" (fixo) ou "DDD 9NNNN-NNNN" (móvel)
const formatWhatsappDisplay = (value) => {
  const digits = stripWhatsappCountryCode(value)
  if (digits.length <= 2) return digits
  if (digits.length <= 6) return `${digits.slice(0, 2)} ${digits.slice(2)}`
  if (digits.length <= 10) return `${digits.slice(0, 2)} ${digits.slice(2, 6)}-${digits.slice(6)}`
  return `${digits.slice(0, 2)} ${digits.slice(2, 7)}-${digits.slice(7, 11)}`
}

// Retorna o número completo com DDI 55 para links wa.me
const getWhatsappFullNumber = (value) => {
  const digits = normalizeWhatsappNumber(value)
  if (!digits) return ''
  if (digits.startsWith('55') && (digits.length === 12 || digits.length === 13)) return digits
  return `55${digits}`
}

const normalizeSupportEmail = (value) => String(value || '').trim().toLowerCase()
const isValidSupportEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeSupportEmail(value))

// Dias de atendimento do WhatsApp. Valores seguem Date.getDay() (0=Domingo ... 6=Sábado).
// A ordem do array (Seg -> Dom) é a ordem canônica usada para agrupar dias consecutivos.
const WHATSAPP_SERVICE_WEEKDAYS = [
  { v: 1, short: 'Seg', full: 'Segunda-feira' },
  { v: 2, short: 'Ter', full: 'Terça-feira' },
  { v: 3, short: 'Qua', full: 'Quarta-feira' },
  { v: 4, short: 'Qui', full: 'Quinta-feira' },
  { v: 5, short: 'Sex', full: 'Sexta-feira' },
  { v: 6, short: 'Sáb', full: 'Sábado' },
  { v: 0, short: 'Dom', full: 'Domingo' },
]

// Agrupa dias consecutivos: [1,2,3,4,5] -> "Seg a Sex"; [1,2,3,5] -> "Seg a Qua e Sex".
const formatWeekdayRanges = (days) => {
  const order = WHATSAPP_SERVICE_WEEKDAYS.map(d => d.v)
  const selected = order.filter(v => Array.isArray(days) && days.map(Number).includes(v))
  if (selected.length === 0) return ''

  const labelOf = (v) => (WHATSAPP_SERVICE_WEEKDAYS.find(d => d.v === v) || {}).short || ''
  const groups = []
  let start = selected[0]
  let prev = selected[0]
  for (let i = 1; i < selected.length; i += 1) {
    const current = selected[i]
    if (order.indexOf(current) === order.indexOf(prev) + 1) {
      prev = current
      continue
    }
    groups.push([start, prev])
    start = current
    prev = current
  }
  groups.push([start, prev])

  const parts = groups.map(([a, b]) => (a === b ? labelOf(a) : `${labelOf(a)} a ${labelOf(b)}`))
  if (parts.length === 1) return parts[0]
  if (parts.length === 2) return `${parts[0]} e ${parts[1]}`
  return `${parts.slice(0, -1).join(', ')} e ${parts[parts.length - 1]}`
}

// Monta o texto de atendimento: "Seg a Sex - 08:00 às 18:00".
const formatSupportHours = (days, start, end) => {
  const daysPart = formatWeekdayRanges(days)
  const startTime = String(start || '').slice(0, 5)
  const endTime = String(end || '').slice(0, 5)
  const timePart = startTime && endTime ? `${startTime} às ${endTime}` : ''
  if (daysPart && timePart) return `${daysPart} - ${timePart}`
  return daysPart || timePart
}

const DEFAULT_REPORT_GENERATION_CONFIG = {
  active: true,
  days: [1, 2, 3, 4, 5],
  time: '08:00',
  id: null
}

const normalizeGenerationSettingsRow = (row) => ({
  active: row?.active ?? true,
  days: Array.isArray(row?.generation_days) ? row.generation_days : [1, 2, 3, 4, 5],
  time: row?.generation_time?.slice(0, 5) || '08:00',
  id: row?.id || null
})

export default function AdminCommunications({
  partner,
  partners = [],
  templates = [],
  setTemplates = () => {},
  defaultEmailHeader = '',
  setDefaultEmailHeader = () => {},
  defaultEmailFooter = '',
  setDefaultEmailFooter = () => {},
  role,
  userCompanyId,
  initialTab = null
}) {
  const partnerColor = partner?.color || '#f97316'
  const initialMessageFilters = {
    startDate: '',
    endDate: '',
    statuses: [],
    channels: [],
    types: [],
    partnerIds: [],
    companyIds: []
  }
  const [activeTab, setActiveTab] = usePersistentTab('admin_communications', 'general')
  const [whatsappSubTab, setWhatsappSubTab] = usePersistentTab('admin_communications_whatsapp_sub', 'inboun')
  const hasAppliedInitialTab = useRef(false)
  const [routinesSubTab, setRoutinesSubTab] = usePersistentTab('admin_communications_routines_sub', 'list')
  const [messagesSubTab, setMessagesSubTab] = usePersistentTab('admin_communications_messages_sub', 'history')
  const [isConfirmingReport, setIsConfirmingReport] = useState(false)
  const [isConfirmingSend, setIsConfirmingSend] = useState(false)
  const [reportResult, setReportResult] = useState(null)
  const [sendResult, setSendResult] = useState(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [refreshKeyDaily, setRefreshKeyDaily] = useState(0)
  const [adminProfiles, setAdminProfiles] = useState({})
  const [showMessageModal, setShowMessageModal] = useState(false)
  const [selectedMessage, setSelectedMessage] = useState(null)
  const [feedbackModal, setFeedbackModal] = useState({ isOpen: false, title: '', content: '' })
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null })
  const [forcedDetailsModal, setForcedDetailsModal] = useState({ isOpen: false, routineId: null, routineTitle: '', details: null })
  const [forcedAttemptsByEvent, setForcedAttemptsByEvent] = useState({})
  const [healthStatus, setHealthStatus] = useState(null)
  const [loadingHealth, setLoadingHealth] = useState(false)
  const [diagResult, setDiagResult] = useState(null)
  const [showDiagModal, setShowDiagModal] = useState(false)
  const [routineLogs, setRoutineLogs] = useState([])
  const [loadingRoutineLogs, setLoadingRoutineLogs] = useState(false)
  const [routineLogFilters, setRoutineLogFilters] = useState({
    startDate: '',
    endDate: '',
    routineId: 'all',
    status: 'all'
  })

  const showFeedback = (title, content) => {
    setFeedbackModal({ isOpen: true, title: title || '', content: content || '' })
  }

  const openConfirm = (title, message, onConfirm) => {
    setConfirmModal({ isOpen: true, title: title || '', message: message || '', onConfirm: typeof onConfirm === 'function' ? onConfirm : null })
  }

  const openForcedDetailsModal = (routineId, routineTitle, details) => {
    setForcedDetailsModal({ isOpen: true, routineId: routineId || null, routineTitle: routineTitle || '', details: details || null })
  }

  useEffect(() => {
    const loadAttempts = async () => {
      if (!forcedDetailsModal.isOpen || !forcedDetailsModal.routineId) {
        setForcedAttemptsByEvent({})
        return
      }
      try {
        const { data, error } = await supabase.rpc('get_routine_channel_attempts', {
          p_routine_id: forcedDetailsModal.routineId,
          p_limit_per_event: 12,
        })
        if (error) throw error
        const mapped = {}
        ;(Array.isArray(data) ? data : []).forEach((row) => {
          const key = String(row?.event_key || '')
          if (!key) return
          mapped[key] = Array.isArray(row?.attempts) ? row.attempts : []
        })
        setForcedAttemptsByEvent(mapped)
      } catch {
        setForcedAttemptsByEvent({})
      }
    }
    loadAttempts()
  }, [forcedDetailsModal.isOpen, forcedDetailsModal.routineId])

  const [dailyReportConfig, setDailyReportConfig] = useState({
    dispatchDays: [1, 2, 3, 4, 5],
    dispatchTime: '08:00',
    roles: ['Admin', 'Coordenador'],
    sendFinalOn100: true,
    pauseAfterCompletion: true,
    reactivateOnNewCourses: false,
    active: true,
    id: null // Track ID for updates
  })

  const [reportGenerationConfig, setReportGenerationConfig] = useState({
    active: true,
    days: [1, 2, 3, 4, 5],
    time: '08:00',
    id: null
  })

  // Load settings on mount
  useEffect(() => {
    if (activeTab === 'daily_report') {
      const loadSettings = async () => {
        const isPartnerPanel = isPartnerScopedRole(role) && !!partner?.id

        const loadGenerationSettings = async () => {
          const fetchScopedGenerationSettings = async () => {
            let query = supabase
              .from('admin_report_generation_settings')
              .select('*')
              .limit(1)

            if (isPartnerPanel) {
              query = query
                .eq('panel_scope', 'PARTNER')
                .eq('sas_partner_id', partner.id)
            } else {
              query = query
                .eq('panel_scope', 'GLOBAL')
                .is('sas_partner_id', null)
            }

            const { data, error } = await query.maybeSingle()
            if (error) throw error
            return data || null
          }

          const fetchGlobalGenerationDefaults = async () => {
            const { data, error } = await supabase
              .from('admin_report_generation_settings')
              .select('*')
              .eq('panel_scope', 'GLOBAL')
              .is('sas_partner_id', null)
              .limit(1)
              .maybeSingle()

            if (error) throw error
            return data || null
          }

          const fetchLegacyGenerationDefaults = async () => {
            const { data, error } = await supabase
              .from('admin_report_settings')
              .select('generation_active, generation_days, generation_time')
              .limit(1)
              .maybeSingle()

            if (error) throw error
            return data || null
          }

          const createScopedGenerationSettings = async () => {
            const source = (await fetchGlobalGenerationDefaults()) || (await fetchLegacyGenerationDefaults())
            const payload = {
              panel_scope: isPartnerPanel ? 'PARTNER' : 'GLOBAL',
              sas_partner_id: isPartnerPanel ? partner.id : null,
              active: source?.active ?? source?.generation_active ?? true,
              generation_days: Array.isArray(source?.generation_days) ? source.generation_days : [1, 2, 3, 4, 5],
              generation_time: source?.generation_time?.slice?.(0, 8) || '08:00',
              updated_at: new Date().toISOString()
            }

            const { data, error } = await supabase
              .from('admin_report_generation_settings')
              .insert(payload)
              .select('*')
              .single()

            if (error) {
              const duplicateInsert = error.code === '23505'
              if (duplicateInsert) {
                return fetchScopedGenerationSettings()
              }
              throw error
            }

            return data
          }

          const existing = await fetchScopedGenerationSettings()
          const row = existing || await createScopedGenerationSettings()
          setReportGenerationConfig(normalizeGenerationSettingsRow(row || DEFAULT_REPORT_GENERATION_CONFIG))
        }

        try {
          const { data } = await supabase
            .from('admin_report_settings')
            .select('*')
            .limit(1)
            .single()

          if (data) {
            setDailyReportConfig({
              dispatchDays: data.dispatch_days || [],
              dispatchTime: data.dispatch_time?.slice(0, 5) || '08:00',
              roles: data.roles || [],
              sendFinalOn100: data.conditions?.sendFinalOn100 ?? true,
              pauseAfterCompletion: data.conditions?.pauseAfterCompletion ?? true,
              reactivateOnNewCourses: data.conditions?.reactivateOnNewCourses ?? false,
              active: data.active ?? true,
              id: data.id
            })
          }
        } catch (e) {
          console.error('Error loading report settings:', e)
        }

        try {
          await loadGenerationSettings()
        } catch (e) {
          console.error('Error loading report generation settings:', e)
          setReportGenerationConfig(DEFAULT_REPORT_GENERATION_CONFIG)
        }
      }
      loadSettings()
    }
  }, [activeTab, partner?.id, role])

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingHealth(true)
        const { data } = await supabase.rpc('communication_health_summary')
        setHealthStatus(data || null)
      } catch (e) {
        setHealthStatus(null)
      } finally {
        setLoadingHealth(false)
      }
    }
    if (activeTab === 'routines') {
      load()
      const t = setInterval(load, 60000)
      return () => clearInterval(t)
    }
  }, [activeTab])

  const fetchRoutineLogs = async () => {
    setLoadingRoutineLogs(true)
    try {
      let q = supabase
        .from('communication_queue')
        .select('id, routine_id, status, created_at, processed_at, attempts, last_error, payload')
        .order('created_at', { ascending: false })
        .limit(300)

      if (routineLogFilters.startDate) q = q.gte('created_at', `${routineLogFilters.startDate}T00:00:00`)
      if (routineLogFilters.endDate) q = q.lte('created_at', `${routineLogFilters.endDate}T23:59:59`)
      if (routineLogFilters.status !== 'all') q = q.eq('status', routineLogFilters.status)
      if (routineLogFilters.routineId !== 'all' && routineLogFilters.routineId) q = q.eq('routine_id', routineLogFilters.routineId)

      const { data, error } = await q
      if (error) throw error
      setRoutineLogs(Array.isArray(data) ? data : [])
    } catch (e) {
      console.error('Erro ao carregar logs de rotinas:', e)
      setRoutineLogs([])
    } finally {
      setLoadingRoutineLogs(false)
    }
  }

  useEffect(() => {
    if (activeTab === 'routines' && routinesSubTab === 'reports') {
      fetchRoutineLogs()
    }
  }, [activeTab, routinesSubTab, routineLogFilters])

  // Save sending settings on change (debounced)
  useEffect(() => {
    if (!dailyReportConfig.id) return // Don't save before initial load

    const timer = setTimeout(async () => {
      try {
        const payload = {
          active: dailyReportConfig.active,
          dispatch_days: dailyReportConfig.dispatchDays,
          dispatch_time: dailyReportConfig.dispatchTime,
          roles: dailyReportConfig.roles,
          conditions: {
            sendFinalOn100: dailyReportConfig.sendFinalOn100,
            pauseAfterCompletion: dailyReportConfig.pauseAfterCompletion,
            reactivateOnNewCourses: dailyReportConfig.reactivateOnNewCourses
          },
          updated_at: new Date().toISOString()
        }
        
        await supabase
          .from('admin_report_settings')
          .update(payload)
          .eq('id', dailyReportConfig.id)

      } catch (e) {
        console.error('Error saving report settings:', e)
      }
    }, 1000)

    return () => clearTimeout(timer)
  }, [dailyReportConfig])

  // Save generation settings on change (debounced)
  useEffect(() => {
    if (!reportGenerationConfig.id) return // Don't save before initial load

    const timer = setTimeout(async () => {
      try {
        const payload = {
          active: reportGenerationConfig.active,
          generation_days: reportGenerationConfig.days,
          generation_time: reportGenerationConfig.time,
          updated_at: new Date().toISOString()
        }
        
        await supabase
          .from('admin_report_generation_settings')
          .update(payload)
          .eq('id', reportGenerationConfig.id)

      } catch (e) {
        console.error('Error saving generation settings:', e)
      }
    }, 1000)

    return () => clearTimeout(timer)
  }, [reportGenerationConfig])

  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('role', ['Admin', 'Coordenador', 'Instrutor', 'Parceiro SAS'])
        
        if (error) throw error
        const mapping = {}
        data.forEach(p => { mapping[p.id] = p.full_name })
        setAdminProfiles(mapping)
      } catch (e) { console.error('Error fetching profiles:', e) }
    }
    fetchProfiles()
  }, [])

  const [sentMessages, setSentMessages] = useState([])
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [selectedSentMessageIds, setSelectedSentMessageIds] = useState([])
  const [deletingSentMessages, setDeletingSentMessages] = useState(false)
  const [messageFilters, setMessageFilters] = useState(initialMessageFilters)
  const [whatsappSettingId, setWhatsappSettingId] = useState(null)
  const [whatsappNum, setWhatsappNum] = useState('')
  const [whatsappToken, setWhatsappToken] = useState('')
  const [whatsappEnabled, setWhatsappEnabled] = useState(false)
  const [whatsappStatus, setWhatsappStatus] = useState('Pendente')
  const [whatsappServiceDays, setWhatsappServiceDays] = useState([])
  const [whatsappServiceStart, setWhatsappServiceStart] = useState('')
  const [whatsappServiceEnd, setWhatsappServiceEnd] = useState('')
  const [loadingWhatsappConfig, setLoadingWhatsappConfig] = useState(false)
  const [emailSettingId, setEmailSettingId] = useState(null)
  const [supportEmail, setSupportEmail] = useState('')
  const [supportEmailDraft, setSupportEmailDraft] = useState('')
  const [emailStatus, setEmailStatus] = useState('Pendente')
  const [loadingEmailConfig, setLoadingEmailConfig] = useState(false)
  const [showEmailSupportModal, setShowEmailSupportModal] = useState(false)
  const [loadingWhatsappOutboundRead, setLoadingWhatsappOutboundRead] = useState(false)
  const [whatsappOutboundRead, setWhatsappOutboundRead] = useState({
    apiProvider: '',
    outboundToggleState: '',
    senderNumber: '',
    configUpdatedAt: '',
    configSourceLabel: '',
    notes: '',
    lastMessageAt: '',
    lastMessageStatus: '',
    lastMessageRecipient: '',
    queueTotal: 0,
    queuePending: 0,
    queueProcessing: 0,
    queueCompleted: 0,
    queueFailed: 0,
  })
  const [savingWhatsappOutboundToggle, setSavingWhatsappOutboundToggle] = useState(false)
  const [whatsappOutboundRpcReady, setWhatsappOutboundRpcReady] = useState(true)
  const [whatsappOutboundRpcMsg, setWhatsappOutboundRpcMsg] = useState('')

  const fetchSentMessages = async () => {
    setLoadingMessages(true)
    try {
      const enrichSentMessagesWithProfileContext = async (rows) => {
        const nextRows = Array.isArray(rows) ? rows : []
        if (nextRows.length === 0) return []

        const profileSelect = 'id, email, whatsapp, company_id, sas_partner_id, companies:companies!profiles_company_id_fkey(name), sas_partners(name)'
        const emailRecipients = Array.from(new Set(
          nextRows
            .map(row => normalizeSupportEmail(row?.recipient || ''))
            .filter(value => value && value.includes('@'))
        ))
        const rawPhoneRecipients = Array.from(new Set(
          nextRows
            .map(row => String(row?.recipient || '').trim())
            .filter(value => value && !value.includes('@'))
        ))
        const normalizedPhoneRecipients = Array.from(new Set(
          rawPhoneRecipients
            .map(value => normalizeWhatsappNumber(value))
            .filter(Boolean)
        ))
        const phoneCandidates = Array.from(new Set([...rawPhoneRecipients, ...normalizedPhoneRecipients])).filter(Boolean)
        const profileQueries = []

        if (emailRecipients.length > 0) {
          profileQueries.push(
            supabase
              .from('profiles')
              .select(profileSelect)
              .in('email', emailRecipients)
          )
        }

        if (phoneCandidates.length > 0) {
          profileQueries.push(
            supabase
              .from('profiles')
              .select(profileSelect)
              .in('whatsapp', phoneCandidates)
          )
        }

        const profileResults = await Promise.all(profileQueries)
        const profileRowsById = new Map()
        profileResults.forEach((result) => {
          if (result?.error) throw result.error
          ;(Array.isArray(result?.data) ? result.data : []).forEach((profile) => {
            if (profile?.id) profileRowsById.set(profile.id, profile)
          })
        })

        const emailMap = new Map()
        const phoneMap = new Map()
        profileRowsById.forEach((profile) => {
          const normalizedEmail = normalizeSupportEmail(profile?.email || '')
          if (normalizedEmail) {
            emailMap.set(normalizedEmail, profile)
          }

          const normalizedWhatsapp = normalizeWhatsappNumber(profile?.whatsapp || '')
          if (normalizedWhatsapp) {
            phoneMap.set(normalizedWhatsapp, profile)
          }
        })

        return nextRows.map((row) => {
          const recipient = String(row?.recipient || '').trim()
          const matchedProfile = recipient.includes('@')
            ? emailMap.get(normalizeSupportEmail(recipient))
            : phoneMap.get(normalizeWhatsappNumber(recipient))

          return {
            ...row,
            company_id: matchedProfile?.company_id || null,
            company_name: matchedProfile?.companies?.name || '',
            partner_id: matchedProfile?.sas_partner_id || null,
            partner_name: matchedProfile?.sas_partners?.name || '',
          }
        })
      }

      let query = supabase
        .from('message_logs')
        .select('id, recipient, status, title, channel, type, created_at, provider, provider_message_id, provider_event, provider_payload, error')
        .order('created_at', { ascending: false })

      if (messageFilters.startDate) query = query.gte('created_at', `${messageFilters.startDate}T00:00:00`)
      if (messageFilters.endDate) query = query.lte('created_at', `${messageFilters.endDate}T23:59:59`)

      const { data, error } = await query
      if (error) throw error
      const nextMessages = await enrichSentMessagesWithProfileContext(Array.isArray(data) ? data : [])
      setSentMessages(nextMessages)
      setSelectedSentMessageIds(prev => prev.filter(id => nextMessages.some(message => message.id === id)))
    } catch (e) {
      console.error('Erro ao carregar mensagens enviadas:', e)
      setSentMessages([])
      setSelectedSentMessageIds([])
    } finally {
      setLoadingMessages(false)
    }
  }

  useEffect(() => { 
    if (activeTab === 'messages' && messagesSubTab === 'history') fetchSentMessages() 
  }, [activeTab, messagesSubTab, messageFilters.startDate, messageFilters.endDate])

  const messageStatusOptions = useMemo(() => (
    Array.from(new Set(sentMessages.map(message => String(message?.status || '').trim()).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b, 'pt-BR'))
      .map(value => ({ id: value, label: value }))
  ), [sentMessages])

  const messageChannelOptions = useMemo(() => (
    Array.from(new Set(sentMessages.map(message => String(message?.channel || '').trim()).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b, 'pt-BR'))
      .map(value => ({ id: value, label: value }))
  ), [sentMessages])

  const messageTypeOptions = useMemo(() => (
    Array.from(new Set(sentMessages.map(message => String(message?.type || '').trim()).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b, 'pt-BR'))
      .map(value => ({ id: value, label: value }))
  ), [sentMessages])

  const messagePartnerOptions = useMemo(() => (
    Array.from(new Map(
      sentMessages
        .filter(message => message?.partner_id && message?.partner_name)
        .map(message => [message.partner_id, { id: message.partner_id, label: message.partner_name }])
    ).values()).sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'))
  ), [sentMessages])

  const messageCompanyOptions = useMemo(() => (
    Array.from(new Map(
      sentMessages
        .filter(message => message?.company_id && message?.company_name)
        .map(message => [message.company_id, { id: message.company_id, label: message.company_name }])
    ).values()).sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'))
  ), [sentMessages])

  const filteredSentMessages = useMemo(() => (
    sentMessages.filter((message) => {
      if (messageFilters.statuses.length > 0 && !messageFilters.statuses.includes(message?.status || '')) return false
      if (messageFilters.channels.length > 0 && !messageFilters.channels.includes(message?.channel || '')) return false
      if (messageFilters.types.length > 0 && !messageFilters.types.includes(message?.type || '')) return false
      if (messageFilters.partnerIds.length > 0 && !messageFilters.partnerIds.includes(message?.partner_id || null)) return false
      if (messageFilters.companyIds.length > 0 && !messageFilters.companyIds.includes(message?.company_id || null)) return false
      return true
    })
  ), [sentMessages, messageFilters])

  const hasActiveMessageFilters = useMemo(() => (
    Boolean(
      messageFilters.startDate ||
      messageFilters.endDate ||
      messageFilters.statuses.length ||
      messageFilters.channels.length ||
      messageFilters.types.length ||
      messageFilters.partnerIds.length ||
      messageFilters.companyIds.length
    )
  ), [messageFilters])

  const handleClearMessageFilters = () => {
    setMessageFilters(initialMessageFilters)
  }

  const canDeleteMessageLogs = role === 'Admin'
  const allVisibleSentMessagesSelected = filteredSentMessages.length > 0 && filteredSentMessages.every(message => selectedSentMessageIds.includes(message.id))

  const handleToggleSentMessageSelection = (messageId) => {
    if (!canDeleteMessageLogs) return
    setSelectedSentMessageIds(prev => (
      prev.includes(messageId)
        ? prev.filter(id => id !== messageId)
        : [...prev, messageId]
    ))
  }

  const handleToggleSelectAllSentMessages = () => {
    if (!canDeleteMessageLogs || filteredSentMessages.length === 0) return
    setSelectedSentMessageIds(prev => {
      if (filteredSentMessages.every(message => prev.includes(message.id))) {
        return prev.filter(id => !filteredSentMessages.some(message => message.id === id))
      }
      const merged = new Set(prev)
      filteredSentMessages.forEach(message => merged.add(message.id))
      return Array.from(merged)
    })
  }

  const handleDeleteSelectedSentMessages = () => {
    if (!canDeleteMessageLogs) return
    if (selectedSentMessageIds.length === 0) {
      showFeedback('Atenção', 'Selecione ao menos uma mensagem para excluir.')
      return
    }

    const count = selectedSentMessageIds.length
    openConfirm(
      'Excluir mensagens',
      `Tem certeza que deseja excluir definitivamente ${count} mensagem(ns)? Esta ação não pode ser desfeita.`,
      async () => {
        const idsToDelete = [...selectedSentMessageIds]
        setDeletingSentMessages(true)
        try {
          const { error } = await supabase
            .from('message_logs')
            .delete()
            .in('id', idsToDelete)

          if (error) throw error

          setSentMessages(prev => prev.filter(message => !idsToDelete.includes(message.id)))
          setSelectedSentMessageIds([])
          if (selectedMessage && idsToDelete.includes(selectedMessage.id)) {
            setSelectedMessage(null)
            setShowMessageModal(false)
          }
          showFeedback('Sucesso', `${count} mensagem(ns) excluída(s) definitivamente.`)
        } catch (error) {
          console.error('Erro ao excluir mensagens:', error)
          showFeedback('Erro', `Não foi possível excluir as mensagens: ${error?.message || 'erro desconhecido'}`)
        } finally {
          setDeletingSentMessages(false)
        }
      }
    )
  }

  useEffect(() => {
    hasAppliedInitialTab.current = false
  }, [initialTab])

  useEffect(() => {
    if (!initialTab || hasAppliedInitialTab.current) return
    hasAppliedInitialTab.current = true
    setActiveTab(initialTab)
  }, [initialTab, setActiveTab])

  useEffect(() => {
    let mounted = true

    const loadEmailConfig = async () => {
      if (!partner?.id) {
        if (!mounted) return
        setEmailSettingId(null)
        setSupportEmail('')
        setSupportEmailDraft('')
        setEmailStatus('Pendente')
        return
      }

      setLoadingEmailConfig(true)
      try {
        const { data, error } = await supabase
          .from('email_settings')
          .select('id, support_email, updated_at')
          .eq('partner_id', partner.id)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle()

        if (error) throw error
        if (!mounted) return

        const normalizedEmail = normalizeSupportEmail(data?.support_email || '')
        setEmailSettingId(data?.id || null)
        setSupportEmail(normalizedEmail)
        setSupportEmailDraft(normalizedEmail)
        setEmailStatus(normalizedEmail ? 'Salvo' : 'Pendente')
      } catch (error) {
        console.error('Erro ao carregar configuração de e-mail de suporte:', error)
        if (!mounted) return
        setEmailStatus('Pendente')
      } finally {
        if (mounted) setLoadingEmailConfig(false)
      }
    }

    loadEmailConfig()
    return () => { mounted = false }
  }, [partner?.id])

  const handleOpenEmailSupportModal = () => {
    setSupportEmailDraft(supportEmail)
    setShowEmailSupportModal(true)
  }

  const handleSaveEmailConfig = async () => {
    const cleanEmail = normalizeSupportEmail(supportEmailDraft)

    if (!partner?.id) {
      setEmailStatus('Pendente')
      return
    }

    if (!cleanEmail || !isValidSupportEmail(cleanEmail)) {
      showFeedback('E-mail inválido', 'Informe um e-mail de suporte válido antes de salvar.')
      return
    }

    setLoadingEmailConfig(true)
    try {
      if (emailSettingId) {
        const { error } = await supabase
          .from('email_settings')
          .update({
            support_email: cleanEmail
          })
          .eq('id', emailSettingId)

        if (error) throw error
      } else {
        const { data: latestDefaults } = await supabase
          .from('email_settings')
          .select('default_header_url, default_footer_url')
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle()

        const { data: inserted, error } = await supabase
          .from('email_settings')
          .insert({
            partner_id: partner.id,
            default_header_url: latestDefaults?.default_header_url || null,
            default_footer_url: latestDefaults?.default_footer_url || null,
            support_email: cleanEmail
          })
          .select('id')
          .single()

        if (error) throw error
        setEmailSettingId(inserted?.id || null)
      }

      setSupportEmail(cleanEmail)
      setSupportEmailDraft(cleanEmail)
      setEmailStatus('Salvo')
      setShowEmailSupportModal(false)
    } catch (error) {
      console.error('Erro ao salvar configuração de e-mail de suporte:', error)
      setEmailStatus('Pendente')
      showFeedback('Erro', `Não foi possível salvar o e-mail de suporte: ${error?.message || 'erro desconhecido'}`)
    } finally {
      setLoadingEmailConfig(false)
    }
  }

  useEffect(() => {
    let mounted = true

    const loadWhatsappConfig = async () => {
      if (!partner?.id) {
        if (!mounted) return
        setWhatsappSettingId(null)
        setWhatsappNum('')
        setWhatsappEnabled(false)
        setWhatsappServiceDays([])
        setWhatsappServiceStart('')
        setWhatsappServiceEnd('')
        setWhatsappStatus('Pendente')
        return
      }

      setLoadingWhatsappConfig(true)
      try {
        const { data, error } = await supabase
          .from('email_settings')
          .select('id, whatsapp_number, whatsapp_enabled, whatsapp_service_days, whatsapp_service_start, whatsapp_service_end, updated_at')
          .eq('partner_id', partner.id)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle()

        if (error) throw error
        if (!mounted) return

        const rawNumber = stripWhatsappCountryCode(data?.whatsapp_number || '')
        setWhatsappSettingId(data?.id || null)
        setWhatsappNum(rawNumber || '')
        setWhatsappEnabled(Boolean(data?.whatsapp_enabled))
        setWhatsappServiceDays(Array.isArray(data?.whatsapp_service_days) ? data.whatsapp_service_days.map(Number) : [])
        setWhatsappServiceStart(data?.whatsapp_service_start ? String(data.whatsapp_service_start).slice(0, 5) : '')
        setWhatsappServiceEnd(data?.whatsapp_service_end ? String(data.whatsapp_service_end).slice(0, 5) : '')
        setWhatsappStatus(data ? 'Salvo' : 'Pendente')
      } catch (error) {
        console.error('Erro ao carregar configuração do WhatsApp:', error)
        if (!mounted) return
        setWhatsappStatus('Pendente')
      } finally {
        if (mounted) setLoadingWhatsappConfig(false)
      }
    }

    loadWhatsappConfig()
    return () => { mounted = false }
  }, [partner?.id])

  const handleSaveWhatsappConfig = async () => {
    const cleanNumber = stripWhatsappCountryCode(whatsappNum)
    const shouldEnable = Boolean(whatsappEnabled && cleanNumber)

    if (!partner?.id) {
      setWhatsappStatus('Pendente')
      return
    }

    setLoadingWhatsappConfig(true)
    try {
      if (whatsappSettingId) {
        const { error } = await supabase
          .from('email_settings')
          .update({
            whatsapp_number: cleanNumber || null,
            whatsapp_enabled: shouldEnable,
            whatsapp_service_days: whatsappServiceDays,
            whatsapp_service_start: whatsappServiceStart || null,
            whatsapp_service_end: whatsappServiceEnd || null
          })
          .eq('id', whatsappSettingId)

        if (error) throw error
      } else {
        const { data: latestDefaults } = await supabase
          .from('email_settings')
          .select('default_header_url, default_footer_url')
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle()

        const { data: inserted, error } = await supabase
          .from('email_settings')
          .insert({
            partner_id: partner.id,
            default_header_url: latestDefaults?.default_header_url || null,
            default_footer_url: latestDefaults?.default_footer_url || null,
            whatsapp_number: cleanNumber || null,
            whatsapp_enabled: shouldEnable,
            whatsapp_service_days: whatsappServiceDays,
            whatsapp_service_start: whatsappServiceStart || null,
            whatsapp_service_end: whatsappServiceEnd || null
          })
          .select('id')
          .single()

        if (error) throw error
        setWhatsappSettingId(inserted?.id || null)
      }

      setWhatsappStatus('Salvo')
      showFeedback('Sucesso', 'Configuração do WhatsApp salva com sucesso.')
    } catch (error) {
      console.error('Erro ao salvar configuração do WhatsApp:', error)
      setWhatsappStatus('Pendente')
      showFeedback('Erro', `Não foi possível salvar a configuração do WhatsApp: ${error?.message || 'erro desconhecido'}`)
    } finally {
      setLoadingWhatsappConfig(false)
    }
  }

  useEffect(() => {
    if (activeTab !== 'whatsapp' || whatsappSubTab !== 'outbound') return
    let mounted = true

    const loadWhatsappOutboundRead = async () => {
      setLoadingWhatsappOutboundRead(true)
      try {
        const [{ data: queueRows, error: queueError }, { data: outboundCfg, error: outboundCfgError }] = await Promise.all([
          supabase
          .from('communication_queue')
          .select('id, recipient_id, template_id, status, created_at, processed_at, payload, last_error')
          .order('created_at', { ascending: false })
            .limit(400),
          supabase.rpc('get_whatsapp_outbound_config')
        ])

        if (queueError) throw queueError

        const allRows = Array.isArray(queueRows) ? queueRows : []
        const templateIds = [...new Set(allRows.map((row) => row?.template_id).filter(Boolean))]
        const recipientIds = [...new Set(allRows.map((row) => row?.recipient_id).filter(Boolean))]

        const [{ data: templatesRows }, { data: profileRows }] = await Promise.all([
          templateIds.length > 0
            ? supabase.from('templates').select('id, channel').in('id', templateIds)
            : Promise.resolve({ data: [] }),
          recipientIds.length > 0
            ? supabase.from('profiles').select('id, whatsapp').in('id', recipientIds)
            : Promise.resolve({ data: [] })
        ])

        const templateChannelMap = new Map((Array.isArray(templatesRows) ? templatesRows : []).map((row) => [row.id, String(row.channel || '')]))
        const profileWhatsappMap = new Map((Array.isArray(profileRows) ? profileRows : []).map((row) => [row.id, normalizeWhatsappNumber(row.whatsapp || '')]))

        const isWhatsappRow = (row) => {
          const payloadChannel = String(row?.payload?.channel || '').toLowerCase()
          if (payloadChannel.includes('whatsapp')) return true
          const tplChannel = String(templateChannelMap.get(row?.template_id) || '').toLowerCase()
          if (tplChannel === 'whatsapp') return true
          return false
        }

        const whatsappRows = allRows.filter(isWhatsappRow)
        const latestWhatsappQueue = whatsappRows[0] || null

        const queuePending = whatsappRows.filter((row) => row?.status === 'pending').length
        const queueProcessing = whatsappRows.filter((row) => row?.status === 'processing').length
        const queueCompleted = whatsappRows.filter((row) => row?.status === 'completed').length
        const queueFailed = whatsappRows.filter((row) => row?.status === 'failed').length

        const cfgRow = (!outboundCfgError && outboundCfg) ? (Array.isArray(outboundCfg) ? (outboundCfg[0] || null) : outboundCfg) : null
        const outboundOn = cfgRow?.enabled == null ? true : Boolean(cfgRow.enabled)
        const apiLabel = outboundOn ? 'WhatsApp Cloud API (Meta) — fila ativa' : 'Sem atividade WhatsApp na fila'

        const fallbackRecipient = latestWhatsappQueue?.recipient_id ? profileWhatsappMap.get(latestWhatsappQueue.recipient_id) || '' : ''

        const lastStatusMap = {
          pending: 'Pendente',
          processing: 'Processando',
          completed: 'Concluído',
          failed: 'Falhou'
        }

        if (!mounted) return
        if (outboundCfgError) {
          setWhatsappOutboundRpcReady(false)
          setWhatsappOutboundRpcMsg('Migração de ON/OFF pendente no banco (RPC não disponível).')
        } else {
          setWhatsappOutboundRpcReady(true)
          setWhatsappOutboundRpcMsg('')
        }
        setWhatsappOutboundRead({
          apiProvider: apiLabel,
          outboundToggleState: outboundOn ? 'ON' : 'OFF',
          senderNumber: '',
          configUpdatedAt: cfgRow?.updated_at || '',
          configSourceLabel: cfgRow ? 'Fonte: app_flags (RPC de outbound)' : 'Fonte de ON/OFF indisponível',
          notes: 'ON/OFF controla o uso do envio de WhatsApp pelo sistema. O envio é feito direto pela WhatsApp Cloud API (Meta), com credenciais em secrets no servidor.',
          lastMessageAt: latestWhatsappQueue?.created_at || '',
          lastMessageStatus: lastStatusMap[latestWhatsappQueue?.status] || '-',
          lastMessageRecipient: fallbackRecipient || '-',
          queueTotal: whatsappRows.length,
          queuePending,
          queueProcessing,
          queueCompleted,
          queueFailed
        })
      } catch (error) {
        console.error('Erro ao ler communication_queue (Outbound WhatsApp):', error)
        if (!mounted) return
        setWhatsappOutboundRpcReady(false)
        setWhatsappOutboundRpcMsg('Falha ao carregar configurações de Outbound.')
        setWhatsappOutboundRead({
          apiProvider: '',
          outboundToggleState: '',
          senderNumber: '',
          configUpdatedAt: '',
          configSourceLabel: '',
          notes: 'Falha ao consultar a tabela communication_queue.',
          lastMessageAt: '',
          lastMessageStatus: '',
          lastMessageRecipient: '',
          queueTotal: 0,
          queuePending: 0,
          queueProcessing: 0,
          queueCompleted: 0,
          queueFailed: 0
        })
      } finally {
        if (mounted) setLoadingWhatsappOutboundRead(false)
      }
    }

    loadWhatsappOutboundRead()
    return () => { mounted = false }
  }, [activeTab, whatsappSubTab, partner?.id])

  const handleToggleWhatsappOutbound = async () => {
    if (!whatsappOutboundRpcReady) {
      showFeedback('ON/OFF indisponível', 'A migração de Outbound ainda não está aplicada no banco ativo. Rode as migrations e tente novamente.')
      return
    }
    try {
      setSavingWhatsappOutboundToggle(true)
      const currentOn = String(whatsappOutboundRead.outboundToggleState || '').startsWith('ON')
      const nextOn = !currentOn
      const { error } = await supabase.rpc('set_whatsapp_outbound_enabled', { enable: nextOn })
      if (error) throw error
      setWhatsappOutboundRead((prev) => ({
        ...prev,
        outboundToggleState: nextOn ? 'ON' : 'OFF',
        configUpdatedAt: new Date().toISOString(),
      }))
    } catch (error) {
      console.error('Erro ao atualizar ON/OFF Outbound do WhatsApp:', error)
      showFeedback('Erro', `Não foi possível atualizar o ON/OFF do Outbound: ${error?.message || 'erro desconhecido'}`)
    } finally {
      setSavingWhatsappOutboundToggle(false)
    }
  }

  const [routines, setRoutines] = useState([])
  const [loadingRoutines, setLoadingRoutines] = useState(false)
  const [showRoutineModal, setShowRoutineModal] = useState(false)
  const [editingRoutine, setEditingRoutine] = useState(null)
  const [isRoutineReadOnly, setIsRoutineReadOnly] = useState(false)
  const [showRoutineRolesDropdown, setShowRoutineRolesDropdown] = useState(false)
  const [showRoutinePartnersDropdown, setShowRoutinePartnersDropdown] = useState(false)
  const [showDispatchModal, setShowDispatchModal] = useState(false)
  const [dispatchingRoutine, setDispatchingRoutine] = useState(null)
  const [showDeleteRoutineModal, setShowDeleteRoutineModal] = useState(false)
  const [routineToDelete, setRoutineToDelete] = useState(null)
  const builtinRoles = ['Admin', 'Coordenador', 'Instrutor', 'Parceiro SAS', 'Ponto Focal', 'Aluno']
  const [customRoles, setCustomRoles] = useState([])
  const roleOptions = [...builtinRoles, ...customRoles]

  const [rolesRows, setRolesRows] = useState([]) // {id,name}
  useEffect(() => {
    const loadCustomRoles = async () => {
      try {
        const { data, error } = await supabase
          .from('communication_roles')
          .select('id, name')
          .order('name', { ascending: true })
        if (error) throw error
        setRolesRows(Array.isArray(data) ? data : [])
        setCustomRoles((Array.isArray(data) ? data : []).map(r => r.name))
      } catch (e) { console.error('Erro ao carregar perfis personalizados:', e) }
    }
    loadCustomRoles()
  }, [])

  // --- Manual Send Logic ---
  const [showManualSendModal, setShowManualSendModal] = useState(false)
  const [loadingManualOptions, setLoadingManualOptions] = useState(false)
  const [showManualRolesDropdown, setShowManualRolesDropdown] = useState(false)
  
  const [companies, setCompanies] = useState([])
  const [classes, setClasses] = useState([])
  const [courses, setCourses] = useState([])
  
  const [manualSendForm, setManualSendForm] = useState({
    roles: [],
    companyIds: [],
    partnerIds: [],
    classIds: [],
    courseIds: [],
    courseStatuses: [],
    emailSubject: '',
    messageText: '',
    whatsappTemplateId: '',
    emailTemplateId: ''
  })

  // Helper for MultiSelectDropdown logic
  const [multiSelectState, setMultiSelectState] = useState({
    roles: false,
    companies: false,
    partners: false,
    classes: false,
    courses: false,
    statuses: false,
    messageStatuses: false,
    messageChannels: false,
    messageTypes: false,
    messagePartners: false,
    messageCompanies: false
  })

  const toggleMultiSelectDropdown = (key) => {
    setMultiSelectState(prev => {
        const next = { ...prev }
        Object.keys(next).forEach(k => next[k] = (k === key ? !prev[key] : false))
        return next
    })
  }

  const MultiSelectDropdown = ({ label, options, selectedIds, onChange, dropdownKey, placeholder = 'Selecionar...' }) => {
    const isOpen = multiSelectState[dropdownKey]
    
    return (
        <div className="relative">
            <label className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1 block">{label}</label>
            <button 
                onClick={() => toggleMultiSelectDropdown(dropdownKey)} 
                type="button" 
                className="w-full px-3 py-2 rounded-lg border border-slate-700 bg-slate-900 text-slate-200 hover:border-slate-600 transition text-left text-sm flex justify-between items-center"
            >
                <span className="truncate">
                    {selectedIds.length > 0 ? `${selectedIds.length} selecionado(s)` : placeholder}
                </span>
                <ChevronDown size={14} className="text-slate-500" />
            </button>
            {isOpen && (
                <div className="absolute z-50 mt-1 w-full max-h-60 overflow-auto bg-slate-900 border border-slate-700 rounded-lg shadow-xl p-1" onMouseLeave={() => toggleMultiSelectDropdown(null)}>
                    {options.map(opt => {
                        const isSelected = selectedIds.includes(opt.id)
                        return (
                            <div 
                                key={opt.id} 
                                className={`flex items-center justify-between p-2 rounded cursor-pointer ${isSelected ? 'bg-teal-500/10' : 'hover:bg-slate-800'}`} 
                                onClick={() => {
                                    const next = isSelected 
                                        ? selectedIds.filter(id => id !== opt.id)
                                        : [...selectedIds, opt.id]
                                    onChange(next)
                                }}
                            >
                                <span className="text-sm text-slate-200">{opt.label}</span>
                                {isSelected && <div className="w-2 h-2 rounded-full bg-teal-500"></div>}
                            </div>
                        )
                    })}
                    {options.length === 0 && <div className="p-2 text-xs text-slate-500 text-center">Nenhuma opção</div>}
                </div>
            )}
        </div>
    )
  }

  const fetchManualSendOptions = async () => {
    setLoadingManualOptions(true)
    try {
      let companiesQuery = supabase.from('companies').select('id, name').eq('status', 'Ativo').order('name');
      let classesQuery = supabase.from('classes').select('id, name, company_id, partner_id').in('status', ['Ativo', 'Programada']).order('name');
      
      if (isPartnerScopedRole(role) && partner?.id) {
        const [{ data: pData }, { data: cData }] = await Promise.all([
          supabase.from('profiles').select('company_id').eq('sas_partner_id', partner.id),
          supabase.from('classes').select('company_id').eq('partner_id', partner.id)
        ]);
        const compIdsFromProfiles = pData ? pData.map(p => p.company_id).filter(Boolean) : [];
        const compIdsFromClasses = cData ? cData.map(c => c.company_id).filter(Boolean) : [];
        let compIds = [...new Set([...compIdsFromProfiles, ...compIdsFromClasses])];
        
        if (userCompanyId) {
          compIds = compIds.filter(id => id !== userCompanyId);
        }

        if (compIds.length > 0) {
          companiesQuery = companiesQuery.in('id', compIds);
        } else {
          companiesQuery = companiesQuery.in('id', ['00000000-0000-0000-0000-000000000000']);
        }
        classesQuery = classesQuery.eq('partner_id', partner.id);
      }

      const [compRes, classRes, courseRes] = await Promise.all([
        companiesQuery,
        classesQuery,
        supabase.from('courses').select('id, name').eq('status', 'Ativo').order('name')
      ])
      
      if (compRes.error) throw compRes.error
      if (classRes.error) throw classRes.error
      if (courseRes.error) throw courseRes.error
      
      setCompanies(compRes.data || [])
      setClasses(classRes.data || [])
      setCourses(courseRes.data || [])
    } catch (e) {
      console.error('Error fetching manual send options:', e)
    } finally {
      setLoadingManualOptions(false)
    }
  }
  
  useEffect(() => {
    if (showManualSendModal) {
       fetchManualSendOptions()
    }
  }, [showManualSendModal])

  const toggleManualRole = (role) => {
    setManualSendForm(prev => {
      const current = prev.roles || []
      if (current.includes(role)) return { ...prev, roles: current.filter(r => r !== role) }
      return { ...prev, roles: [...current, role] }
    })
  }

  const handleManualSend = async () => {
      // Validate
      if (!manualSendForm.whatsappTemplateId && !manualSendForm.emailTemplateId) {
          setFeedbackModal({
            isOpen: true,
            title: 'Atenção',
            content: 'Selecione pelo menos um template (WhatsApp ou E-mail) para enviar.'
          })
          return
      }

      setLoadingManualOptions(true) // Reuse loader or add a new one for sending
      
      try {
          const { data, error } = await supabase.rpc('queue_manual_messages', {
              p_filters: {
                  roles: manualSendForm.roles,
                  companyIds: manualSendForm.companyIds,
                  partnerIds: manualSendForm.partnerIds,
                  classIds: manualSendForm.classIds,
                  courseIds: manualSendForm.courseIds,
                  courseStatuses: manualSendForm.courseStatuses
              },
              p_message: {
                  text: manualSendForm.messageText,
                  emailSubject: manualSendForm.emailSubject,
                  whatsappTemplateId: manualSendForm.whatsappTemplateId,
                  emailTemplateId: manualSendForm.emailTemplateId
              }
          })

          if (error) throw error

          if (data.success) {
              setFeedbackModal({
                isOpen: true,
                title: 'Envio Iniciado',
                content: `Envio iniciado para ${data.count} destinatários.\nAs mensagens serão processadas em breve.`
              })
              setShowManualSendModal(false)
              // Reset form?
              setManualSendForm({
                roles: [],
                companyIds: [],
                partnerIds: [],
                classIds: [],
                courseIds: [],
                courseStatuses: [],
                emailSubject: '',
                messageText: '',
                whatsappTemplateId: '',
                emailTemplateId: ''
              })
          } else {
              setFeedbackModal({
                isOpen: true,
                title: 'Erro',
                content: 'Ocorreu um erro ao iniciar o envio.'
              })
          }

      } catch (err) {
          console.error('Error sending manual messages:', err)
          setFeedbackModal({
            isOpen: true,
            title: 'Erro',
            content: 'Erro ao enviar mensagens: ' + err.message
          })
      } finally {
          setLoadingManualOptions(false)
      }
  }

  const initialRoutineFormState = {
    type: '',
    title: '',
    dispatchTime: '08:00',
    dispatchDays: [1, 2, 3, 4, 5],
    templateId: '',
    templateAttachmentId: '',
    isDefault: false,
    partnerIds: [],
    roleNames: [],
    conditions: {
      sendIfProgress: false,
      sendIfNewAccess: false,
      sendIfPendingStudents: false,
      skipIfNoActivity7d: false,
      sendFinalOn100: false,
      pauseAfterCompletion: false,
      reactivateOnNewCourses: false,
      inviteFirstSendHours: 24,
      inviteRepeatEveryHours: 72,
      inviteMaxSends: 3,
      alertsMatrix: buildDefaultAlertsMatrix(),
    },
    active: true,
  }
  const [routineForm, setRoutineForm] = useState(initialRoutineFormState)

  const fetchRoutines = async () => {
    setLoadingRoutines(true)
    try {
      let q = supabase
        .from('communication_routines')
        .select('id, title, type, dispatch_time, dispatch_days, template_id, template_attachment_id, is_default, sas_partner_ids, role_names, conditions, is_active, created_at')
        .order('created_at', { ascending: false })

      if (partner?.id) {
        q = q.or(`sas_partner_id.eq.${partner.id},sas_partner_ids.cs.{${partner.id}},sas_partner_id.is.null`)
      }

      const { data, error } = await q
      if (error) throw error
      setRoutines(Array.isArray(data) ? data : [])
    } catch (e) {
      console.error('Erro ao carregar rotinas:', e)
      setRoutines([])
    } finally {
      setLoadingRoutines(false)
    }
  }

  useEffect(() => {
    if (activeTab === 'routines') fetchRoutines()
  }, [activeTab])

  const formatRoutineDays = (days) => {
    const map = {
      0: 'Dom',
      1: 'Seg',
      2: 'Ter',
      3: 'Qua',
      4: 'Qui',
      5: 'Sex',
      6: 'Sáb',
    }
    const list = Array.isArray(days) ? days : []
    if (list.length === 0) return '-'
    const order = [1, 2, 3, 4, 5, 6, 0]
    const sorted = [...list].sort((a, b) => order.indexOf(a) - order.indexOf(b))
    return sorted.map(d => map[d]).filter(Boolean).join(', ')
  }

  const getConditionTags = (c = {}) => {
    const tags = []
    if (c.sendIfProgress) tags.push('Progresso')
    if (c.sendIfNewAccess) tags.push('Novos acessos')
    if (c.sendIfPendingStudents) tags.push('Pendências')
    if (c.skipIfNoActivity7d) tags.push('Sem atividade 7d')
    if (c.sendFinalOn100) tags.push('Final 100%')
    if (c.pauseAfterCompletion) tags.push('Pausar após conclusão')
    if (c.reactivateOnNewCourses) tags.push('Reativar novos cursos')
    return tags
  }

  const openCreateRoutine = () => {
    const emailTemplates = (templates || []).filter(t => t.channel === 'E-mail' && t.is_active !== false)
    const inviteTemplates = emailTemplates.filter(t => String(t.meta?.tipo || '').trim() === 'Convite')
    const attachmentTemplates = (templates || []).filter(t => t.channel === 'Relatórios & Anexos' && t.is_active !== false)
    const typeOptions = Array.from(new Set([
      ...transactionalTypes,
      ...reportTypes,
      ...emailTemplates.map(t => t.meta?.tipo).filter(Boolean),
      ...attachmentTemplates.map(t => t.meta?.tipo).filter(Boolean),
    ])).filter(Boolean)
    setEditingRoutine(null)
    setIsRoutineReadOnly(false)
    setShowRoutineRolesDropdown(false)
    setRoutineForm({
      ...initialRoutineFormState,
      type: typeOptions[0] || 'Relatório',
      templateId: (typeOptions[0] === 'Convite' ? inviteTemplates[0] : emailTemplates[0])?.id || '',
      templateAttachmentId: attachmentTemplates[0]?.id || '',
      isDefault: false,
      partnerIds: [],
      roleNames: [],
      conditions: { ...initialRoutineFormState.conditions },
    })
    setShowRoutineModal(true)
  }

  const openEditRoutine = (r, readOnly = false) => {
    if (!r) return
    setEditingRoutine(r)
    setIsRoutineReadOnly(readOnly)
    setShowRoutineRolesDropdown(false)
    setRoutineForm({
      type: r.type || '',
      title: r.title || '',
      dispatchTime: (r.dispatch_time || '').slice(0, 5) || '08:00',
      dispatchDays: Array.isArray(r.dispatch_days) ? r.dispatch_days : [],
      templateId: r.template_id || '',
      templateAttachmentId: r.template_attachment_id || '',
      isDefault: !!r.is_default,
      partnerIds: Array.isArray(r.sas_partner_ids) ? r.sas_partner_ids : [],
      roleNames: Array.isArray(r.role_names) ? r.role_names : [],
      conditions: {
        sendIfProgress: !!r?.conditions?.sendIfProgress,
        sendIfNewAccess: !!r?.conditions?.sendIfNewAccess,
        sendIfPendingStudents: !!r?.conditions?.sendIfPendingStudents,
        skipIfNoActivity7d: !!r?.conditions?.skipIfNoActivity7d,
        sendFinalOn100: !!r?.conditions?.sendFinalOn100,
        pauseAfterCompletion: !!r?.conditions?.pauseAfterCompletion,
        reactivateOnNewCourses: !!r?.conditions?.reactivateOnNewCourses,
        inviteFirstSendHours: Math.max(0, Number(r?.conditions?.inviteFirstSendHours ?? 24)),
        inviteRepeatEveryHours: Math.max(1, Number(r?.conditions?.inviteRepeatEveryHours ?? 72)),
        inviteMaxSends: Math.max(1, Number(r?.conditions?.inviteMaxSends ?? 3)),
        alertsMatrix: {
          ...initialRoutineFormState.conditions.alertsMatrix,
          ...(r?.conditions?.alertsMatrix || {}),
        },
      },
      active: r.is_active !== false,
    })
    setShowRoutineModal(true)
  }

  const toggleRoutineDay = (day) => {
    const current = Array.isArray(routineForm.dispatchDays) ? routineForm.dispatchDays : []
    const next = current.includes(day) ? current.filter(d => d !== day) : [...current, day]
    setRoutineForm({ ...routineForm, dispatchDays: next })
  }

  const toggleRoutineRoleName = (roleName) => {
    const current = Array.isArray(routineForm.roleNames) ? routineForm.roleNames : []
    const next = current.includes(roleName) ? current.filter(r => r !== roleName) : [...current, roleName]
    setRoutineForm({ ...routineForm, roleNames: next })
  }

  const toggleCondition = (key) => {
    setRoutineForm({ ...routineForm, conditions: { ...routineForm.conditions, [key]: !routineForm.conditions[key] } })
  }

  const handleSaveRoutine = async () => {
    const title = (routineForm.title || '').trim()
    const type = (routineForm.type || '').trim()
    const dispatchTime = (routineForm.dispatchTime || '').trim()
    const dispatchDays = Array.isArray(routineForm.dispatchDays) ? routineForm.dispatchDays : []
    const templateId = routineForm.templateId || null
    const templateAttachmentId = routineForm.templateAttachmentId || null
    const isDefault = !!routineForm.isDefault
    const partnerIds = Array.isArray(routineForm.partnerIds) ? routineForm.partnerIds : []
    const roleNames = Array.isArray(routineForm.roleNames) ? routineForm.roleNames : []
    const conditions = routineForm.conditions || {}

    if (type === 'Convite') {
      const firstSendHours = Number(conditions.inviteFirstSendHours)
      const repeatEveryHours = Number(conditions.inviteRepeatEveryHours)
      const maxSends = Number(conditions.inviteMaxSends)
      if (!Number.isFinite(firstSendHours) || firstSendHours < 0) return showFeedback('Atenção', 'Informe um prazo válido para o primeiro envio do convite.')
      if (!Number.isFinite(repeatEveryHours) || repeatEveryHours < 1) return showFeedback('Atenção', 'O intervalo de repetição do convite deve ser de pelo menos 1 hora.')
      if (!Number.isInteger(maxSends) || maxSends < 1) return showFeedback('Atenção', 'Informe pelo menos um envio máximo para o convite.')
    }

    // Check if it's an immediate event based routine (we don't need time validation for triggers)
    // Actually, routines are for scheduled jobs. The immediate triggers are handled in the DB directly.
    // However, if the user configures "Alertas", we keep the current logic as they are polled.

    if (!title) return showFeedback('Atenção', 'Informe o Título.')
    if (!type) return showFeedback('Atenção', 'Informe o Tipo.')
    if (!dispatchTime) return showFeedback('Atenção', 'Informe o Horário de disparo.')
    if (dispatchDays.length === 0) return showFeedback('Atenção', 'Selecione ao menos um dia de disparo.')
    if (type !== 'Alertas' && !templateId) return showFeedback('Atenção', 'Selecione um TEMPLATE-E-MAIL.')

    let currentUser = null
    try {
      const { data: { user } } = await supabase.auth.getUser()
      currentUser = user
    } catch (e) { void e }

    const payload = {
      sas_partner_id: partner?.id || null,
      title,
      type,
      dispatch_time: dispatchTime,
      dispatch_days: dispatchDays,
      template_id: templateId,
      template_attachment_id: templateAttachmentId,
      is_default: isDefault,
      sas_partner_ids: partnerIds,
      role_names: roleNames,
      conditions,
      is_active: !!routineForm.active,
    }
    if (currentUser?.id) payload.created_by = currentUser.id

    try {
      if (editingRoutine?.id) {
        const { data: updated, error } = await supabase
          .from('communication_routines')
          .update(payload)
          .eq('id', editingRoutine.id)
          .select()
          .single()
        if (error) throw error
        setRoutines(routines.map(r => r.id === updated.id ? updated : r))
      } else {
        const { data: inserted, error } = await supabase
          .from('communication_routines')
          .insert(payload)
          .select()
          .single()
        if (error) throw error
        setRoutines([inserted, ...routines])
      }
      setShowRoutineModal(false)
      setEditingRoutine(null)
    } catch (e) {
      console.error('Erro ao salvar rotina:', e)
      showFeedback('Erro', 'Erro ao salvar rotina.')
    }
  }

  const handleOpenDispatchModal = (r) => {
    setDispatchingRoutine(r)
    setShowDispatchModal(true)
  }

  const handleDiagnoseRoutine = async (r) => {
    if (!r?.id) return
    try {
      const { data, error } = await supabase.rpc('dry_run_communication_routine', { p_routine_id: r.id })
      if (error) throw error
      setDiagResult(data || null)
      setShowDiagModal(true)
    } catch (e) {
      setDiagResult({ ok: false, error: e.message || 'Erro' })
      setShowDiagModal(true)
    }
  }

  const handleConfirmDispatch = async () => {
    if (!dispatchingRoutine) return
    try {
      const { data, error } = await supabase.rpc('force_process_communication_routine', { 
        p_routine_id: dispatchingRoutine.id 
      })

      if (error) throw error

      const result = data && data.length > 0 ? data[0] : null
      const count = result?.recipients_count || 0

      // alert(`Disparo forçado iniciado! ${count} usuários na fila.`)
      // Substituindo alert por toast ou notificação customizada se houver, mas por enquanto vamos manter o fluxo e fechar o modal.
      // A UI de confirmação deve ser o próprio feedback.
      
      if (count > 0) {
        let workerSummary = ''
        try {
          const { data: workerData, error: workerError } = await supabase.functions.invoke('communication-worker', { body: {} })
          if (workerError) throw workerError

          const processed = Array.isArray(workerData?.processed) ? workerData.processed : []
          const successCount = processed.filter(p => p?.status === 'success').length
          const errorCount = processed.filter(p => p?.status === 'error').length
          const ignoredCount = processed.filter(p => String(p?.status || '').startsWith('ignored_')).length
          const processingCount = Math.max(count - successCount - errorCount - ignoredCount, 0)

          if (processed.length === 0 && workerData?.message === 'No jobs pending') {
            workerSummary = `Status atual: 0 enviados, ${count} em processamento, 0 com erro. A fila será atualizada automaticamente.`
          } else {
            workerSummary = `Status atual: ${successCount} enviados, ${processingCount} em processamento, ${errorCount} com erro.`
          }
        } catch (e) {
          workerSummary = `Status atual: 0 enviados, ${count} em processamento, 0 com erro. A fila será atualizada automaticamente.`
        }

        showFeedback('Sucesso', `${count} usuários foram adicionados à fila de envio.\n\n${workerSummary}`)
      } else {
        openForcedDetailsModal(dispatchingRoutine?.id || null, dispatchingRoutine?.title || 'Rotina', result?.details)
      }

      setShowDispatchModal(false)
      setDispatchingRoutine(null)
    } catch (error) {
      console.error('Erro ao disparar rotina:', error)
      showFeedback('Erro', 'Erro ao processar disparo: ' + (error.message || 'Erro desconhecido'))
    }
  }

  const handleToggleRoutineActive = async (r) => {
    if (!r?.id) return
    const next = !(r.is_active !== false)
    try {
      const { data: updated, error } = await supabase
        .from('communication_routines')
        .update({ is_active: next })
        .eq('id', r.id)
        .select()
        .single()
      if (error) throw error
      setRoutines(routines.map(x => x.id === updated.id ? updated : x))
    } catch (e) {
      console.error('Erro ao atualizar rotina:', e)
      showFeedback('Erro', 'Erro ao atualizar rotina.')
    }
  }

  const handleDeleteRoutine = (r) => {
    if (!r?.id) return
    setRoutineToDelete(r)
    setShowDeleteRoutineModal(true)
  }

  const confirmDeleteRoutine = async () => {
    if (!routineToDelete?.id) return
    try {
      const { error } = await supabase.from('communication_routines').delete().eq('id', routineToDelete.id)
      if (error) throw error
      setRoutines(routines.filter(x => x.id !== routineToDelete.id))
      setShowDeleteRoutineModal(false)
      setRoutineToDelete(null)
    } catch (e) {
      console.error('Erro ao excluir rotina:', e)
      showFeedback('Erro', 'Erro ao excluir rotina.')
    }
  }

  const handleCloneRoutine = async (r) => {
    if (!r) return
    const title = `copia ${r.title || ''}`.trim()
    try {
      let currentUser = null
      try {
        const { data: { user } } = await supabase.auth.getUser()
        currentUser = user
      } catch (e) { void e }

      const payload = {
        sas_partner_id: partner?.id || null,
        title,
        type: r.type,
        dispatch_time: r.dispatch_time,
        dispatch_days: r.dispatch_days || [],
        template_id: r.template_id || null,
        template_attachment_id: r.template_attachment_id || null,
        role_names: r.role_names || [],
        is_default: r.is_default || false,
        sas_partner_ids: r.sas_partner_ids || [],
        conditions: r.conditions || {},
        is_active: r.is_active !== false,
      }
      if (currentUser?.id) payload.created_by = currentUser.id

      const { data: inserted, error } = await supabase
        .from('communication_routines')
        .insert(payload)
        .select()
        .single()
      if (error) throw error
      setRoutines([inserted, ...routines])
    } catch (e) {
      console.error('Erro ao clonar rotina:', e)
      showFeedback('Erro', 'Erro ao clonar rotina.')
    }
  }
  
  const [templateFilters, setTemplateFilters] = useState({
    name: '',
    creator: 'all',
    status: 'all',
    startDate: '',
    endDate: '',
  })
  const [templateSortOrder, setTemplateSortOrder] = useState('desc') // desc = mais novos no topo

  const [editingTemplate, setEditingTemplate] = useState(null)
  
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [previewTemplate, setPreviewTemplate] = useState(null)

  const [showTransactionalModal, setShowTransactionalModal] = useState(false)
  const [transactionalChannel, setTransactionalChannel] = useState('E-mail')
  const transactionalTypes = ['Convite', 'Boas Vindas', 'Reset Senha', 'Certificado', 'Prorrogação', 'confirmação', 'aviso', 'notificação', 'Comunicado', 'Engajamento', 'Alertas']
  const reportTypes = ['Relatório', 'Anexo', 'Update', 'Arquivo']



  const ensureAlertsMatrix = () => {
    const current = routineForm?.conditions?.alertsMatrix || {}
    const base = initialRoutineFormState.conditions.alertsMatrix
    const merged = { ...base, ...current }
    return merged
  }
  
  const initialTransactionalFormState = {
    tipo: 'Convite',
    header: null,
    headerUrl: '',
    tornarHeaderPadrao: false,
    titulo: '',
    textoHtml: '',
    botaoHabilitado: false,
    botaoTitulo: '',
    botaoLink: '',
    observacaoHabilitada: false,
    observacao: '',
    usarHtml: true,
    rodape: null,
    rodapeUrl: '',
    tornarRodapePadrao: false,
    variaveisHabilitadas: false,
    variaveis: buildVariaveisFromArray([]),
    atribuirParceiros: false,
    parceirosSelecionados: [],
    anexos: [],
    anexosAberto: true,
    active: true,
    code: '',
    isDefault: false,
    // Opções de uso (por onde o template de convite deve ser disparado).
    // Podem estar marcadas simultaneamente: o mesmo template atende aos dois fluxos.
    usoConvitePadrao: false,
    usoConviteLink: false,
    metaTemplateName: '',
    metaTemplateLanguage: 'pt_BR',
    metaTemplateParams: [],
    // Botão de ação (URL) do template aprovado na Meta: prefixo estático da URL
    // + variável (SYSTEM_VARIABLES) que preenche a parte dinâmica.
    metaTemplateButtonEnabled: false,
    metaTemplateButtonPrefix: '',
    metaTemplateButtonVar: '',
  }

  const [transactionalForm, setTransactionalForm] = useState(initialTransactionalFormState)
  const [showPartnerDropdown, setShowPartnerDropdown] = useState(false)
  const allowedAttachments = {
    'application/pdf': 5 * 1024 * 1024,
    'image/jpeg': 2 * 1024 * 1024,
    'image/png': 2 * 1024 * 1024,
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 5 * 1024 * 1024,
  }
  const acceptAttr = Object.keys(allowedAttachments).map(t => {
    if (t.includes('pdf')) return '.pdf'
    if (t.includes('jpeg')) return '.jpg,.jpeg'
    if (t.includes('png')) return '.png'
    if (t.includes('wordprocessingml')) return '.docx'
    return ''
  }).filter(Boolean).join(',')

  const validateAndSetAttachments = (filesList) => {
    const current = transactionalForm.anexos || []
    const incoming = Array.from(filesList || [])
    const combined = [...current]
    for (const f of incoming) {
      if (combined.length >= 2) break
      const max = allowedAttachments[f.type]
      if (!max || f.size > max) continue
      combined.push({ name: f.name, size: f.size, type: f.type, file: f, url: URL.createObjectURL(f) })
    }
    setTransactionalForm({ ...transactionalForm, anexos: combined })
  }

  // Chaves das variáveis habilitadas no template (ordem canônica de SYSTEM_VARIABLES).
  const getToggledVariableKeys = () =>
    SYSTEM_VARIABLES
      .filter(v => transactionalForm.variaveisHabilitadas && transactionalForm.variaveis?.[v.k])
      .map(v => v.k)

  // Variáveis cujos tokens realmente aparecem no corpo da mensagem, na ordem de aparição.
  // É o que define as posições {{1}}, {{2}}... do corpo aprovado na Meta.
  const getBodyVariableKeys = () => {
    const text = String(transactionalForm.textoHtml || '')
    if (!text) return []
    const found = []
    SYSTEM_VARIABLES.forEach(v => {
      let first = -1
      v.tokens.forEach(tok => {
        const idx = text.indexOf(tok)
        if (idx >= 0 && (first === -1 || idx < first)) first = idx
      })
      if (first >= 0) found.push({ k: v.k, idx: first })
    })
    return found.sort((a, b) => a.idx - b.idx).map(x => x.k)
  }

  // Ordem dos parâmetros do template da Meta: placeholders do corpo (na ordem em que
  // aparecem) + variáveis habilitadas que não estejam no corpo. A posição no array
  // corresponde a {{1}}, {{2}}, {{3}}... no corpo aprovado na Meta.
  // Variáveis recém-inseridas no corpo entram na posição correspondente do corpo,
  // preservando a ordem manual já existente.
  const getMetaTemplateParamKeys = () => {
    const bodyOrder = getBodyVariableKeys()
    const all = [...bodyOrder]
    getToggledVariableKeys().forEach(k => { if (!all.includes(k)) all.push(k) })

    const stored = (transactionalForm.metaTemplateParams || []).filter(k => all.includes(k))
    if (stored.length === 0) return all

    const missing = all.filter(k => !stored.includes(k))
    if (missing.length === 0) return stored

    const result = [...stored]
    missing.forEach(k => {
      const bodyIdx = bodyOrder.indexOf(k)
      if (bodyIdx === -1) { result.push(k); return }
      let insertAt = result.length
      for (let i = 0; i < result.length; i += 1) {
        const otherBodyIdx = bodyOrder.indexOf(result[i])
        if (otherBodyIdx !== -1 && otherBodyIdx > bodyIdx) { insertAt = i; break }
      }
      result.splice(insertAt, 0, k)
    })
    return result
  }

  const moveMetaTemplateParam = (idx, dir) => {
    const current = getMetaTemplateParamKeys()
    const target = idx + dir
    if (target < 0 || target >= current.length) return
    const next = [...current]
    ;[next[idx], next[target]] = [next[target], next[idx]]
    setTransactionalForm({ ...transactionalForm, metaTemplateParams: next })
  }

  const TitleMax = 80
  const ObsMax = 180

  const getFilteredTemplates = (list) => {
    let filtered = [...list]
    
    if (templateFilters.name) {
      const q = templateFilters.name.toLowerCase()
      filtered = filtered.filter(t => 
        (t.name || '').toLowerCase().includes(q) || 
        (t.code || '').toLowerCase().includes(q) ||
        (t.subject || '').toLowerCase().includes(q)
      )
    }

    if (templateFilters.creator !== 'all') {
      filtered = filtered.filter(t => t.created_by === templateFilters.creator)
    }

    if (templateFilters.status !== 'all') {
      const active = templateFilters.status === 'active'
      filtered = filtered.filter(t => (t.is_active !== false) === active)
    }

    if (templateFilters.startDate) {
      const start = new Date(templateFilters.startDate)
      filtered = filtered.filter(t => new Date(t.created_at) >= start)
    }

    if (templateFilters.endDate) {
      const end = new Date(templateFilters.endDate)
      end.setHours(23, 59, 59, 999)
      filtered = filtered.filter(t => new Date(t.created_at) <= end)
    }

    // Sort by created_at
    filtered.sort((a, b) => {
      const da = new Date(a.created_at || 0)
      const db = new Date(b.created_at || 0)
      return templateSortOrder === 'desc' ? db - da : da - db
    })

    return filtered
  }

  const ColumnFilters = ({ channel }) => (
    <div className="bg-slate-900/80 border border-slate-800 p-3 rounded-xl mb-4 flex flex-col gap-3 shadow-sm">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input 
            type="text" 
            value={templateFilters.name} 
            onChange={e => setTemplateFilters({ ...templateFilters, name: e.target.value })}
            placeholder="Nome ou Código..."
            className="w-full bg-slate-950 border border-slate-800 pl-9 pr-3 py-1.5 rounded-lg text-xs text-white outline-none focus:border-slate-600 transition"
          />
        </div>
        <button 
          onClick={() => setTemplateSortOrder(v => v === 'desc' ? 'asc' : 'desc')}
          className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg border border-slate-700 transition"
          title={templateSortOrder === 'desc' ? 'Mais novos primeiro' : 'Mais antigos primeiro'}
        >
          <ArrowUpDown size={14} className={templateSortOrder === 'asc' ? 'rotate-180 transition' : 'transition'} />
        </button>
      </div>
      
      <div className="grid grid-cols-2 gap-2">
        <select 
          value={templateFilters.creator} 
          onChange={e => setTemplateFilters({ ...templateFilters, creator: e.target.value })}
          className="bg-slate-950 border border-slate-800 px-2 py-1.5 rounded-lg text-[10px] text-white outline-none focus:border-slate-600 transition uppercase font-bold"
        >
          <option value="all">CRIADOR: TODOS</option>
          {Object.entries(adminProfiles).map(([id, name]) => (
            <option key={id} value={id}>{name.toUpperCase()}</option>
          ))}
        </select>
        <select 
          value={templateFilters.status} 
          onChange={e => setTemplateFilters({ ...templateFilters, status: e.target.value })}
          className="bg-slate-950 border border-slate-800 px-2 py-1.5 rounded-lg text-[10px] text-white outline-none focus:border-slate-600 transition uppercase font-bold"
        >
          <option value="all">STATUS: TODOS</option>
          <option value="active">ATIVOS</option>
          <option value="inactive">INATIVOS</option>
        </select>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex-1 flex items-center gap-1 bg-slate-950 border border-slate-800 rounded-lg px-2 py-1">
          <Calendar size={10} className="text-slate-500" />
          <input 
            type="date" 
            value={templateFilters.startDate} 
            onChange={e => setTemplateFilters({ ...templateFilters, startDate: e.target.value })}
            className="bg-transparent border-none text-[10px] text-white outline-none w-full"
          />
        </div>
        <span className="text-slate-600 text-[10px] font-bold">ATÉ</span>
        <div className="flex-1 flex items-center gap-1 bg-slate-950 border border-slate-800 rounded-lg px-2 py-1">
          <Calendar size={10} className="text-slate-500" />
          <input 
            type="date" 
            value={templateFilters.endDate} 
            onChange={e => setTemplateFilters({ ...templateFilters, endDate: e.target.value })}
            className="bg-transparent border-none text-[10px] text-white outline-none w-full"
          />
        </div>
        {(templateFilters.name || templateFilters.creator !== 'all' || templateFilters.status !== 'all' || templateFilters.startDate || templateFilters.endDate) && (
          <button 
            onClick={() => setTemplateFilters({ name: '', creator: 'all', status: 'all', startDate: '', endDate: '' })}
            className="p-1 text-rose-500 hover:bg-rose-500/10 rounded transition"
            title="Limpar Filtros"
          >
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  )

  const handleTransactionalSave = async () => {
    let currentUser = null
    try {
        const { data: { user } } = await supabase.auth.getUser()
        currentUser = user
    } catch (e) { console.error('Error getting user', e) }

    const plainText = (transactionalForm.textoHtml || '').replace(/<[^>]+>/g, '').trim()
    const name = `${transactionalForm.tipo} - ${transactionalForm.titulo || 'Template'}`
    const finalSubject = transactionalForm.titulo || transactionalForm.tipo || 'Template'
    
    let headerUrl = ''
    let rodapeUrl = ''
    if (transactionalChannel === 'E-mail') {
      headerUrl = transactionalForm.headerUrl || defaultEmailHeader || ''
      rodapeUrl = transactionalForm.rodapeUrl || defaultEmailFooter || ''
      if (transactionalForm.header) {
        const ext = transactionalForm.header.name.split('.').pop() || 'img'
        const path = `headers/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`
        try { headerUrl = await uploadPublic(path, transactionalForm.header) } catch (e) { void e }
      }
      if (transactionalForm.rodape) {
        const ext = transactionalForm.rodape.name.split('.').pop() || 'img'
        const path = `footers/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`
        try { rodapeUrl = await uploadPublic(path, transactionalForm.rodape) } catch (e) { void e }
      }
    }

    const isHtml = transactionalForm.usarHtml
    const finalContent = isHtml ? (transactionalForm.textoHtml || '') : plainText

    const newTemplate = {
      name,
      channel: transactionalChannel,
      subject: finalSubject,
      content: finalContent,
      transactional: true,
      is_html: isHtml,
      partner_ids: transactionalForm.atribuirParceiros ? transactionalForm.parceirosSelecionados : [],
      attachments: transactionalForm.anexos || [],
      is_active: transactionalForm.active,
      code: transactionalForm.code,
      meta: {
        tipo: transactionalForm.tipo,
        usarHtml: isHtml,
        headerUrl,
        rodapeUrl,
        textoHtml: transactionalForm.textoHtml,
        botao: transactionalForm.botaoHabilitado ? { titulo: transactionalForm.botaoTitulo, link: transactionalForm.botaoLink } : null,
        observacao: transactionalForm.observacaoHabilitada ? transactionalForm.observacao : '',
        // Inclui variáveis ativas por toggle e as que aparecem como placeholder no corpo,
        // para que o toggle reflita exatamente o que a mensagem utiliza.
        variaveis: (() => {
          const toggled = Object.keys(transactionalForm.variaveis || {}).filter(k => transactionalForm.variaveis[k])
          const merged = [...toggled]
          getBodyVariableKeys().forEach(k => { if (!merged.includes(k)) merged.push(k) })
          return merged
        })(),
        parceirosSAS: transactionalForm.atribuirParceiros ? transactionalForm.parceirosSelecionados : [],
        anexos: transactionalForm.anexos || [],
        is_default: transactionalForm.isDefault,
        // Opções de uso: onde o template deve ser disparado. Só é permitido em
        // templates do tipo 'Convite' nos canais E-mail, WhatsApp e SMS (o banco
        // tem CHECK constraint equivalente). Ambos podem estar marcados (o mesmo
        // template atende aos dois fluxos). Fora disso o valor é sempre false.
        usos: {
          convite_padrao: ['E-mail', 'WhatsApp', 'SMS'].includes(transactionalChannel) && transactionalForm.tipo === 'Convite' ? !!transactionalForm.usoConvitePadrao : false,
          convite_link: ['E-mail', 'WhatsApp', 'SMS'].includes(transactionalChannel) && transactionalForm.tipo === 'Convite' ? !!transactionalForm.usoConviteLink : false,
        },
        // Template aprovado na Meta (obrigatório para envio de WhatsApp).
        // Persiste quando houver nome do template OU quando o botão de URL estiver
        // habilitado — assim a configuração do botão nunca é descartada em silêncio.
        metaTemplate: transactionalChannel === 'WhatsApp'
          && (String(transactionalForm.metaTemplateName || '').trim() || transactionalForm.metaTemplateButtonEnabled)
          ? {
              name: String(transactionalForm.metaTemplateName || '').trim(),
              language: String(transactionalForm.metaTemplateLanguage || 'pt_BR').trim() || 'pt_BR',
              params: getMetaTemplateParamKeys(),
              // Botão de URL do template: prefixo estático + variável da parte dinâmica.
              // Necessário quando o template aprovado possui botão com variável (erro #131008).
              button: transactionalForm.metaTemplateButtonEnabled
                ? {
                    prefix: String(transactionalForm.metaTemplateButtonPrefix || '').trim(),
                    paramKey: String(transactionalForm.metaTemplateButtonVar || '').trim(),
                  }
                : null,
            }
          : null,
      },
    }

    if (transactionalForm.isDefault && transactionalChannel !== 'Relatórios & Anexos') {
      const others = templates.filter(other => 
        other.channel === transactionalChannel && 
        (other.meta?.tipo === transactionalForm.tipo) &&
        other.meta?.is_default &&
        (!editingTemplate || other.id !== editingTemplate.id)
      )
      
      for (const other of others) {
        const newMeta = { ...other.meta, is_default: false }
        await supabase.from('templates').update({ meta: newMeta }).eq('id', other.id)
      }
    }

    if (!editingTemplate) {
        if (currentUser) newTemplate.created_by = currentUser.id
    } else {
        // preserve existing created_by if editing, or set it if missing
        newTemplate.created_by = editingTemplate.created_by || currentUser?.id
    }

    if (transactionalChannel === 'E-mail' && (transactionalForm.tornarHeaderPadrao || transactionalForm.tornarRodapePadrao)) {
      const payload = {
        default_header_url: transactionalForm.tornarHeaderPadrao ? headerUrl : defaultEmailHeader || '',
        default_footer_url: transactionalForm.tornarRodapePadrao ? rodapeUrl : defaultEmailFooter || '',
      }
      try {
        await supabase.from('email_settings').insert(payload)
        if (transactionalForm.tornarHeaderPadrao && typeof setDefaultEmailHeader === 'function') setDefaultEmailHeader(headerUrl)
        if (transactionalForm.tornarRodapePadrao && typeof setDefaultEmailFooter === 'function') setDefaultEmailFooter(rodapeUrl)
      } catch (e) { void e }
    }

    try {
      if (editingTemplate) {
        const { data: updated, error } = await supabase.from('templates').update(newTemplate).eq('id', editingTemplate.id).select().single()
        if (error) throw error
        const updatedList = templates.map(t => t.id === editingTemplate.id ? updated : t)
        setTemplates(updatedList)
      } else {
        const { data: inserted, error } = await supabase.from('templates').insert(newTemplate).select().single()
        if (error) throw error
        setTemplates([inserted, ...templates])
      }
      setShowTransactionalModal(false)
      setEditingTemplate(null)
    } catch (error) {
      console.error('Error saving template:', error)
      const msg = error?.message || error?.details || 'Erro desconhecido'
      if (msg.includes('templates_channel_check')) {
          showFeedback('Erro', 'O canal selecionado não é suportado pelo banco de dados.')
      } else {
          showFeedback('Erro', `Erro ao salvar template: ${msg}`)
      }
    }
  }

  const handleDeleteTemplate = async (id) => {
    openConfirm('Excluir Template', 'Deseja excluir este template?', async () => {
      try {
        const { error } = await supabase.from('templates').delete().eq('id', id)
        if (error) throw error
        setTemplates(templates.filter(t => t.id !== id))
      } catch (error) {
        console.error('Error deleting template:', error)
        showFeedback('Erro', 'Erro ao excluir template.')
      }
    })
  }

  const handleSetDefault = async (template) => {
    if (!template) return
    const isCurrentlyDefault = template.meta?.is_default
    const newStatus = !isCurrentlyDefault
    
    // Se estiver ativando, desativa os outros do mesmo tipo/canal (EXCETO PARA Relatórios & Anexos)
    if (newStatus && template.channel !== 'Relatórios & Anexos') {
      const others = templates.filter(other => 
        other.id !== template.id && 
        other.channel === template.channel && 
        (other.meta?.tipo === (template.meta?.tipo || '')) &&
        other.meta?.is_default
      )
      
      // Atualiza outros para false
      for (const other of others) {
        const newMeta = { ...other.meta, is_default: false }
        await supabase.from('templates').update({ meta: newMeta }).eq('id', other.id)
      }
    }

    // Atualiza o atual
    const newMeta = { ...template.meta, is_default: newStatus }
    const { error } = await supabase.from('templates').update({ meta: newMeta }).eq('id', template.id)

    if (error) {
      console.error('Error updating default status:', error)
      showFeedback('Erro', 'Erro ao atualizar status padrão.')
      return
    }

    // Atualiza estado local
    const updatedList = templates.map(item => {
      if (item.id === template.id) return { ...item, meta: newMeta }
      if (newStatus && item.channel === template.channel && item.channel !== 'Relatórios & Anexos' && item.meta?.tipo === (template.meta?.tipo || '')) {
        return { ...item, meta: { ...item.meta, is_default: false } }
      }
      return item
    })
    setTemplates(updatedList)
  }

  // Aplica na pré-visualização os valores de exemplo de TODAS as variáveis de
  // sistema presentes no conteúdo. Espelha o envio real: o worker substitui
  // todo token encontrado, independentemente de a variável estar "habilitada".
  const applyPreviewVariables = (template, value) => {
    const previewSupportEmail = supportEmail || 'suporte@empresa.com.br'
    const normalizedPreviewWhatsapp = normalizeWhatsappNumber(whatsappNum)
    const hasActivePreviewWhatsapp = Boolean(whatsappEnabled && normalizedPreviewWhatsapp)
    const previewWhatsappFull = hasActivePreviewWhatsapp ? getWhatsappFullNumber(normalizedPreviewWhatsapp) : '5511999999999'

    // Horário de atendimento: usa a configuração salva; se nada estiver
    // configurado, cai no exemplo padrão para o placeholder não ficar vazio.
    const hoursConfigured = (Array.isArray(whatsappServiceDays) && whatsappServiceDays.length > 0)
      || Boolean(String(whatsappServiceStart || '').trim())
      || Boolean(String(whatsappServiceEnd || '').trim())
    const previewHoursDays = hoursConfigured ? whatsappServiceDays : [1, 2, 3, 4, 5]
    const previewHoursStart = hoursConfigured ? whatsappServiceStart : '08:00'
    const previewHoursEnd = hoursConfigured ? whatsappServiceEnd : '18:00'

    const samples = {
      ...PREVIEW_VARIABLE_SAMPLES,
      emailSuporte: previewSupportEmail,
      whatsappSuporte: formatWhatsappDisplay(previewWhatsappFull),
      whatsappSuporteLink: hasActivePreviewWhatsapp ? `https://wa.me/${previewWhatsappFull}` : '#',
      horarioAtendimento: formatSupportHours(previewHoursDays, previewHoursStart, previewHoursEnd),
      diasAtendimento: formatWeekdayRanges(previewHoursDays),
      horarioAtendimentoInicio: String(previewHoursStart || '').slice(0, 5),
      horarioAtendimentoFim: String(previewHoursEnd || '').slice(0, 5),
    }

    let out = String(value || '')
    SYSTEM_VARIABLES.forEach(v => {
      const sample = samples[v.k]
      if (sample == null) return
      v.tokens.forEach(tok => { out = out.split(tok).join(sample) })
    })
    return out
  }

  const buildTransactionalHtml = (t) => {
    const meta = t.meta || {}
    const isHtml = t.is_html !== undefined ? t.is_html : meta.usarHtml
    const previewSupportEmail = supportEmail || 'suporte@empresa.com.br'
    const normalizedPreviewWhatsapp = normalizeWhatsappNumber(whatsappNum)
    const hasActivePreviewWhatsapp = Boolean(whatsappEnabled && normalizedPreviewWhatsapp)
    const previewWhatsappFull = hasActivePreviewWhatsapp ? getWhatsappFullNumber(normalizedPreviewWhatsapp) : '5511999999999'
    const previewWhatsapp = formatWhatsappDisplay(previewWhatsappFull)
    const previewWhatsappLink = hasActivePreviewWhatsapp ? `https://wa.me/${previewWhatsappFull}` : '#'
    const supportPreviewBlock = `
      <div style="margin-top:18px;padding:10px 14px;border:1px solid #2D3748;border-radius:8px;background:#1A2230;color:#EDEFF5;font-size:13px;line-height:1.4;">
        <p style="margin:0 0 6px 0;font-size:11px;color:#8A98A6;text-transform:uppercase;font-weight:700;letter-spacing:1px;">Canais de Suporte</p>
        <div style="display:flex;flex-wrap:wrap;align-items:center;gap:18px 24px;">
          <div>E-mail: <a href="mailto:${previewSupportEmail}" style="color:#29B3A8;text-decoration:none;font-weight:600;">${previewSupportEmail}</a></div>
          <div>WhatsApp: <a href="${previewWhatsappLink}" target="_blank" style="color:#29B3A8;text-decoration:none;font-weight:600;">${previewWhatsapp}</a></div>
        </div>
      </div>
    `
    const replacePreviewSupportPlaceholders = (value) => applyPreviewVariables(t, value)
    const injectPreviewSupportBlock = (value) => {
      const html = replacePreviewSupportPlaceholders(value)
      if (t.channel !== 'Relatórios & Anexos') return html
      if (/Canais de Suporte|mailto:|wa\.me\//i.test(html)) return html
      if (html.includes('</footer>')) return html.replace('</footer>', `${supportPreviewBlock}</footer>`)
      if (html.includes('</body>')) return html.replace('</body>', `${supportPreviewBlock}</body>`)
      return `${html}${supportPreviewBlock}`
    }

    if (isHtml) return injectPreviewSupportBlock(meta.textoHtml || t.content || '') + (meta.anexos && meta.anexos.length > 0 
      ? `<div style="margin-top:20px;padding-top:20px;border-top:1px solid #1e293b;background:#020617;padding:12px;border-radius:8px;">
           <p style="font-size:12px;color:#94a3b8;margin-bottom:8px;font-weight:bold;text-transform:uppercase;">Anexos:</p>
           ${meta.anexos.map(a => `
             <div style="display:flex;align-items:center;gap:8px;padding:8px;background:#0f172a;border-radius:6px;margin-bottom:4px;font-size:12px;color:#e2e8f0;">
               <span style="font-weight:500;">📎 ${a.name}</span>
               <span style="color:#64748b;font-size:10px;">(${(a.size / 1024 / 1024).toFixed(2)} MB)</span>
             </div>
           `).join('')}
         </div>` 
      : '')
    const headerUrl = meta.headerUrl || defaultEmailHeader || ''
    const footerUrl = meta.rodapeUrl || defaultEmailFooter || ''
    const body = injectPreviewSupportBlock(meta.textoHtml || t.content || '')
    const btn = meta.botao && (meta.botao.titulo || meta.botao.link) ? meta.botao : null
    const obs = meta.observacao || ''

    const buttonHtml = btn ? `<a href="${applyPreviewVariables(t, btn.link) || '#'}" style="display:inline-block;padding:12px 18px;background:#f97316;color:white;text-decoration:none;border-radius:8px;font-weight:700;">${applyPreviewVariables(t, btn.titulo) || 'Acessar'}</a>` : ''
    const headerImg = headerUrl ? `<img src="${headerUrl}" style="width:100%;max-height:140px;object-fit:cover;border-radius:12px 12px 0 0;" />` : ''
    const footerImg = footerUrl ? `<img src="${footerUrl}" style="width:100%;max-height:120px;object-fit:cover;border-radius:0 0 12px 12px;" />` : ''
    const obsHtml = obs ? `<p style="margin-top:16px;color:#64748b;font-size:12px;">${applyPreviewVariables(t, obs)}</p>` : ''
    const attachmentsHtml = meta.anexos && meta.anexos.length > 0 
      ? `<div style="margin-top:20px;padding-top:20px;border-top:1px solid #1e293b;background:#020617;padding:12px;border-radius:8px;">
           <p style="font-size:12px;color:#94a3b8;margin-bottom:8px;font-weight:bold;text-transform:uppercase;">Anexos:</p>
           ${meta.anexos.map(a => `
             <div style="display:flex;align-items:center;gap:8px;padding:8px;background:#0f172a;border-radius:6px;margin-bottom:4px;font-size:12px;color:#e2e8f0;">
               <span style="font-weight:500;">📎 ${a.name}</span>
               <span style="color:#64748b;font-size:10px;">(${(a.size / 1024 / 1024).toFixed(2)} MB)</span>
             </div>
           `).join('')}
         </div>`
      : ''
    
    return `<!doctype html><html><head><meta charset="utf-8"/></head><body style="background:#0b1220;padding:24px;font-family:system-ui, -apple-system, Segoe UI, Roboto;">
      <div style="max-width:720px;margin:0 auto;background:white;border-radius:12px;overflow:hidden;">
        ${headerImg}
        <div style="padding:20px;">
          ${body}
          ${buttonHtml ? `<div style="margin-top:16px;">${buttonHtml}</div>` : ''}
          ${obsHtml}
          ${attachmentsHtml}
        </div>
        ${footerImg}
      </div>
    </body></html>`
  }

  const getPreviewModalTitle = (template) => {
    const channel = template?.channel || ''
    if (channel === 'WhatsApp' || channel === 'SMS') return `Pré-visualização do ${channel}`
    if (channel === 'Relatórios & Anexos') return 'Pré-visualização de Relatórios & Anexos'
    return 'Pré-visualização do E-mail'
  }

  const formatWhatsAppText = (text, template) => {
    if (!text) return ''
    let formatted = applyPreviewVariables(template, String(text || ''))
      .replace(/&nbsp;/g, ' ')
      .replace(/\*(.*?)\*/g, '<strong>$1</strong>')
      .replace(/_(.*?)_/g, '<em>$1</em>')
      .replace(/~(.*?)~/g, '<strike>$1</strike>')
      .replace(/```(.*?)```/gs, '<code class="bg-slate-100 px-1 rounded font-mono text-xs">$1</code>')
      .replace(/\n/g, '<br />')
    return formatted
  }

  const filteredEmailTemplates = activeTab === 'templates' ? getFilteredTemplates(templates.filter(t => t.channel === 'E-mail')) : []
  const filteredWhatsappTemplates = activeTab === 'templates' ? getFilteredTemplates(templates.filter(t => t.channel === 'WhatsApp')) : []
  const filteredSmsTemplates = activeTab === 'templates' ? getFilteredTemplates(templates.filter(t => t.channel === 'SMS')) : []
  const filteredReportsAndAttachmentsTemplates = activeTab === 'templates' ? getFilteredTemplates(templates.filter(t => t.channel === 'Relatórios & Anexos')) : []

  const [showLinkTemplateModal, setShowLinkTemplateModal] = useState(false)
  const [linkTemplateCategory, setLinkTemplateCategory] = useState('') // 'Transacionais' or 'Engajamento'
  const [linkTemplateChannel, setLinkTemplateChannel] = useState('') // 'E-mail' or 'WhatsApp'
  const [replacingTemplate, setReplacingTemplate] = useState(null)
  const [selectedTemplateIds, setSelectedTemplateIds] = useState([])
  const [linkTemplateForm, setLinkTemplateForm] = useState({
    type: '',
    code: '',
    title: ''
  })
  
  const [showTypeSelector, setShowTypeSelector] = useState(false)
  const [editingConfigurationType, setEditingConfigurationType] = useState(null)
  
  const whatsappTextareaRef = useRef(null)
  const emailHtmlTextareaRef = useRef(null)
  const [emojiSearch, setEmojiSearch] = useState('')

  const insertAtCursor = (token) => {
    if ((transactionalChannel === 'WhatsApp' || transactionalChannel === 'SMS') && whatsappTextareaRef.current) {
      const input = whatsappTextareaRef.current
      const start = input.selectionStart
      const end = input.selectionEnd
      const text = input.value
      const newText = text.substring(0, start) + token + text.substring(end)
      setTransactionalForm({ ...transactionalForm, textoHtml: newText })
      setTimeout(() => {
        input.focus()
        input.setSelectionRange(start + token.length, start + token.length)
      }, 0)
    } else if (transactionalChannel === 'E-mail' && transactionalForm.usarHtml && emailHtmlTextareaRef.current) {
      const input = emailHtmlTextareaRef.current
      const start = input.selectionStart
      const end = input.selectionEnd
      const text = input.value
      const newText = text.substring(0, start) + token + text.substring(end)
      setTransactionalForm({ ...transactionalForm, textoHtml: newText })
      setTimeout(() => {
        input.focus()
        input.setSelectionRange(start + token.length, start + token.length)
      }, 0)
    } else {
      const text = String(transactionalForm.textoHtml || '')
      const sep = text.length > 0 && !/\s$/.test(text) ? ' ' : ''
      setTransactionalForm({ ...transactionalForm, textoHtml: text + sep + token })
    }
  }

  // New state to store linked messages
  // Structure: { 'E-mail': { 'Transacionais': [], 'Engajamento': [] }, 'WhatsApp': { 'Transacionais': [], 'Engajamento': [] } }
  const [linkedMessages, setLinkedMessages] = useState({
    'E-mail': {
      'Transacionais': [],
      'Engajamento': []
    },
    'WhatsApp': {
      'Transacionais': [],
      'Engajamento': []
    },
    'SMS': {
      'Transacionais': [],
      'Engajamento': []
    },
    'Relatórios & Anexos': {
      'Relatórios': [],
      'Anexos': []
    }
  })

  useEffect(() => {
    if (templates) {
      const newLinkedMessages = {
        'E-mail': { 'Transacionais': [], 'Engajamento': [] },
        'WhatsApp': { 'Transacionais': [], 'Engajamento': [] },
        'SMS': { 'Transacionais': [], 'Engajamento': [] },
        'Relatórios & Anexos': { 'Relatórios': [], 'Anexos': [] }
      }
      
      templates.forEach(t => {
        if (t.is_active !== false && t.message_category) {
           const msg = {
             id: t.id,
             templateId: t.id,
             title: t.subject,
             type: t.meta?.tipo || 'Geral',
             code: t.code,
             channel: t.channel,
             date: t.updated_at ? new Date(t.updated_at).toISOString().slice(0, 10) : new Date(t.created_at).toISOString().slice(0, 10),
             status: 'Ativo',
             partnerIds: t.meta?.parceirosSAS || [],
             isDefault: !!t.meta?.is_default,
             enabled: t.meta?.enabled !== false && t.meta?.enabled !== 'false',
             meta: t.meta
           }
           if (newLinkedMessages[t.channel] && newLinkedMessages[t.channel][t.message_category]) {
             newLinkedMessages[t.channel][t.message_category].push(msg)
           }
        }
      })
      
      setLinkedMessages(newLinkedMessages)
    }
  }, [templates])

  const getFilteredActiveTemplates = () => {
    let filtered = templates.filter(t => t.is_active !== false)
    
    // Filter by channel if set
    if (linkTemplateChannel) {
      filtered = filtered.filter(t => t.channel === linkTemplateChannel)
    }

    if (linkTemplateForm.type) {
      filtered = filtered.filter(t => (t.meta?.tipo || '').toLowerCase() === linkTemplateForm.type.toLowerCase())
    }
    
    if (linkTemplateForm.code) {
      filtered = filtered.filter(t => (t.code || '').toLowerCase().includes(linkTemplateForm.code.toLowerCase()))
    }
    
    if (linkTemplateForm.title) {
      filtered = filtered.filter(t => (t.subject || '').toLowerCase().includes(linkTemplateForm.title.toLowerCase()))
    }
    
    return filtered
  }

  const handleSaveLinks = async () => {
    try {
        const typeToProcess = editingConfigurationType || linkTemplateForm.type
        
        if (!typeToProcess) {
            showFeedback('Erro', 'Tipo não definido.')
            return
        }

        // Encontrar templates que estavam vinculados a este tipo e categoria, mas não estão mais selecionados
        // Para desvincular
        const previouslyLinked = linkedMessages[linkTemplateChannel]?.[linkTemplateCategory]?.filter(m => m.type === typeToProcess) || []
        const previousIds = previouslyLinked.map(m => m.id)
        
        const toUnlink = previousIds.filter(id => !selectedTemplateIds.includes(id))
        const toLink = selectedTemplateIds // Todos os selecionados devem garantir o vínculo

        if (toUnlink.length > 0) {
            const { error: unlinkError } = await supabase
                .from('templates')
                .update({ message_category: null })
                .in('id', toUnlink)
            if (unlinkError) throw unlinkError
        }

        if (toLink.length > 0) {
            // Atualizar meta.tipo para garantir que o backend encontre pelo tipo correto
            await Promise.all(toLink.map(async (id) => {
                const t = templates.find(temp => temp.id === id)
                const currentMeta = t?.meta || {}
                const updates = { message_category: linkTemplateCategory }
                
                if (currentMeta.tipo !== typeToProcess) {
                    updates.meta = { ...currentMeta, tipo: typeToProcess }
                }
                
                const { error } = await supabase
                    .from('templates')
                    .update(updates)
                    .eq('id', id)
                    
                if (error) throw error
            }))
        }
        
        // Atualiza estado local
        let updatedTemplates = [...templates]
        
        // Desvincular
        updatedTemplates = updatedTemplates.map(t => toUnlink.includes(t.id) ? { ...t, message_category: null } : t)
        
        // Vincular e atualizar tipo
        updatedTemplates = updatedTemplates.map(t => {
            if (toLink.includes(t.id)) {
                return { 
                    ...t, 
                    message_category: linkTemplateCategory,
                    meta: { ...(t.meta || {}), tipo: typeToProcess }
                }
            }
            return t
        })
        
        setTemplates(updatedTemplates)
        
        setShowLinkTemplateModal(false)
        setLinkTemplateForm({ type: '', code: '', title: '' })
        setEditingConfigurationType(null)
        setReplacingTemplate(null)
        setSelectedTemplateIds([])
    } catch (e) {
        console.error('Erro ao salvar vínculos:', e)
        showFeedback('Erro', 'Erro ao vincular mensagens.')
    }
  }

  const handleLinkTemplate = async (template) => {
    // Deprecated in favor of handleSaveLinks
  }

  const closeLinkTemplateModal = () => {
    setShowLinkTemplateModal(false)
    setLinkTemplateForm({ type: '', code: '', title: '' })
    setEditingConfigurationType(null)
    setReplacingTemplate(null)
    setSelectedTemplateIds([])
  }

  const handleUnlinkTemplate = async (id) => {
    openConfirm('Remover Mensagem', 'Remover esta mensagem da lista?', async () => {
      try {
          const { error } = await supabase
              .from('templates')
              .update({ message_category: null })
              .eq('id', id)
              
          if (error) throw error
          
          const updatedTemplates = templates.map(t => t.id === id ? { ...t, message_category: null } : t)
          setTemplates(updatedTemplates)
      } catch (e) {
          console.error('Erro ao desvincular template:', e)
          showFeedback('Erro', 'Erro ao remover mensagem.')
      }
    })
  }

  const handleUnlinkTypeGroup = async (type) => {
    openConfirm('Remover Configurações', `Deseja remover todas as configurações do tipo "${type}"?`, async () => {
      try {
          const msgs = linkedMessages[linkTemplateChannel]?.[linkTemplateCategory]?.filter(m => m.type === type) || []
          const ids = msgs.map(m => m.id)
          
          if (ids.length === 0) return

          const { error } = await supabase
              .from('templates')
              .update({ message_category: null })
              .in('id', ids)
              
          if (error) throw error
          
          const updatedTemplates = templates.map(t => ids.includes(t.id) ? { ...t, message_category: null } : t)
          setTemplates(updatedTemplates)
      } catch (e) {
          console.error('Erro ao remover grupo:', e)
          showFeedback('Erro', 'Erro ao remover configurações.')
      }
    })
  }

  const handleToggleEnabled = async (msg) => {
    try {
      const newEnabled = !msg.enabled
      console.log('Toggling enabled for:', msg.id, 'New value:', newEnabled)
      
      const { error } = await supabase
        .from('templates')
        .update({
          meta: { ...msg.meta, enabled: newEnabled }
        })
        .eq('id', msg.id)

      if (error) throw error
      
      // Update local state
      setTemplates(prev => prev.map(t => {
        if (t.id === msg.id) {
          const nextMeta = { ...t.meta, enabled: newEnabled }
          return { ...t, meta: nextMeta }
        }
        return t
      }))
    } catch (e) {
      console.error('Erro ao atualizar status:', e)
      showFeedback('Erro', 'Erro ao atualizar status: ' + (e.message || 'Erro desconhecido'))
    }
  }

  const renderMessageList = (channel, category) => {
    const msgs = linkedMessages[channel]?.[category] || []

    // Agrupar por Tipo
    const grouped = msgs.reduce((acc, msg) => {
        const type = msg.type || 'Outros'
        if (!acc[type]) acc[type] = []
        acc[type].push(msg)
        return acc
    }, {})

    return (
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 mb-6 last:mb-0">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-bold text-white uppercase tracking-wider">{category}</h2>
          <button 
            onClick={() => {
              setLinkTemplateCategory(category)
              setLinkTemplateChannel(channel)
              setLinkTemplateForm({ type: '', code: '', title: '' })
              setSelectedTemplateIds([])
              setEditingConfigurationType(null)
              setShowTypeSelector(true)
            }}
            style={{ backgroundColor: partnerColor }}
            className="text-white px-3 py-1.5 rounded-lg font-bold flex items-center gap-1.5 hover:opacity-90 transition shadow-lg text-xs"
          >
            + Criar
          </button>
        </div>
        
        {msgs.length > 0 ? (
          <div className="grid grid-cols-1 gap-3">
            {msgs.map(msg => (
              <div key={msg.id} className="bg-slate-950/50 border border-slate-800 rounded-lg p-4 hover:border-slate-700 transition relative group">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex flex-col gap-1">
                    <span className="font-bold text-white text-sm">{msg.title}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-mono text-slate-500 bg-slate-900 px-1.5 py-0.5 rounded border border-slate-800">{msg.code}</span>
                      <span className="text-[10px] text-teal-500 uppercase font-bold tracking-wider">{msg.type}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-2 mr-2" title={msg.enabled ? "Desabilitar envio automático" : "Habilitar envio automático"}>
                        <span className={`text-[10px] uppercase font-bold tracking-wider ${msg.enabled ? 'text-teal-400' : 'text-slate-600'}`}>{msg.enabled ? 'ON' : 'OFF'}</span>
                        <ToggleSwitch 
                            checked={msg.enabled} 
                            onChange={(e) => { e.stopPropagation(); handleToggleEnabled(msg); }}
                            ariaLabel="Habilitar envio automático"
                        />
                    </div>
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleSetDefault(msg); }}
                      className={`text-xs font-bold px-2 py-1 rounded-lg border transition flex items-center gap-1.5 ${msg.isDefault ? 'bg-amber-500/10 text-amber-400 border-amber-500/50 hover:bg-amber-500/20' : 'bg-slate-900 text-slate-500 border-slate-700 hover:border-slate-500 hover:text-slate-300'}`}
                      title={msg.isDefault ? "Este é o padrão do sistema" : "Definir como padrão do sistema"}
                    >
                      {msg.isDefault ? <span className="fill-current">★</span> : <span className="opacity-50">☆</span>}
                      {msg.isDefault ? 'Padrão' : 'Definir Padrão'}
                    </button>
                    <button 
                       onClick={() => {
                         const t = templates.find(temp => temp.id === msg.id)
                         if (!t) return
                         const currentType = msg.type || t?.meta?.tipo || ''
                         setReplacingTemplate(msg)
                         setLinkTemplateCategory(category)
                          setLinkTemplateChannel(channel)
                          setLinkTemplateForm({
                            type: currentType,
                            code: '',
                            title: ''
                          })
                          setEditingConfigurationType(currentType || null)
                          setSelectedTemplateIds([msg.id])
                          setShowLinkTemplateModal(true)
                        }} 
                       className="p-1.5 text-slate-600 hover:text-blue-500 hover:bg-blue-500/10 rounded-lg transition" 
                       title="Editar"
                     >
                       <Edit2 size={14} />
                     </button>
                    <button onClick={() => handleUnlinkTemplate(msg.id)} className="p-1.5 text-slate-600 hover:text-rose-500 hover:bg-rose-500/10 rounded-lg transition" title="Remover"><Trash2 size={14} /></button>
                  </div>
                </div>
                
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-800/50">
                  <div className="flex flex-wrap gap-1">
                    {msg.partnerIds && msg.partnerIds.length > 0 ? (
                      msg.partnerIds.map(pid => {
                        const p = partners.find(pt => pt.id === pid)
                        if (!p) return null
                        return (
                          <span key={pid} className="px-2 py-0.5 rounded text-[10px] font-bold text-white uppercase tracking-wider shadow-sm" style={{ backgroundColor: p.color || '#64748b' }}>
                            {p.name}
                          </span>
                        )
                      })
                    ) : (
                      <span className="text-[10px] text-slate-600 italic">Sem parceiros vinculados</span>
                    )}
                  </div>
                  <span className="text-[10px] text-slate-600">{new Date(msg.date).toLocaleDateString('pt-BR')}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-slate-500 text-xs italic py-4 text-center bg-slate-950/30 rounded-lg border border-slate-800/50">
            Nenhuma mensagem configurada.
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="p-4 md:p-8 animate-fade-in">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Comunicações</h1>
          <p className="text-slate-400">Gerencie mensagens e templates transacionais.</p>
        </div>
      </div>

      <div className="flex gap-4 border-b border-slate-800 mb-6 overflow-x-auto whitespace-nowrap">
        <button onClick={() => setActiveTab('sms')} className={`pb-3 font-bold text-sm uppercase ${activeTab === 'sms' ? 'text-white border-b-2 border-orange-500' : 'text-slate-500'}`}>SMS</button>
        <button onClick={() => setActiveTab('whatsapp')} className={`pb-3 font-bold text-sm uppercase ${activeTab === 'whatsapp' ? 'text-white border-b-2 border-orange-500' : 'text-slate-500'}`}>WhatsApp</button>
        <button onClick={() => setActiveTab('email')} className={`pb-3 font-bold text-sm uppercase ${activeTab === 'email' ? 'text-white border-b-2 border-orange-500' : 'text-slate-500'}`}>E-mail</button>
        <button onClick={() => setActiveTab('general')} className={`pb-3 font-bold text-sm uppercase ${activeTab === 'general' ? 'text-white border-b-2 border-orange-500' : 'text-slate-500'}`}>Configurações</button>
        <button onClick={() => setActiveTab('templates')} className={`pb-3 font-bold text-sm uppercase ${activeTab === 'templates' ? 'text-white border-b-2 border-orange-500' : 'text-slate-500'}`}>Templates</button>
        <button onClick={() => setActiveTab('messages')} className={`pb-3 font-bold text-sm uppercase ${activeTab === 'messages' ? 'text-white border-b-2 border-orange-500' : 'text-slate-500'}`}>Mensagens</button>
        <button onClick={() => setActiveTab('routines')} className={`pb-3 font-bold text-sm uppercase ${activeTab === 'routines' ? 'text-white border-b-2 border-orange-500' : 'text-slate-500'}`}>Rotinas</button>
        <button onClick={() => setActiveTab('daily_report')} className={`pb-3 font-bold text-sm uppercase ${activeTab === 'daily_report' ? 'text-white border-b-2 border-orange-500' : 'text-slate-500'}`}>Relatório Diário</button>
        <button onClick={() => setActiveTab('evaluations')} className={`pb-3 font-bold text-sm uppercase ${activeTab === 'evaluations' ? 'text-white border-b-2 border-orange-500' : 'text-slate-500'}`}>Avaliações - Moderação</button>
        <button onClick={() => setActiveTab('notices')} className={`pb-3 font-bold text-sm uppercase ${activeTab === 'notices' ? 'text-white border-b-2 border-orange-500' : 'text-slate-500'}`}>Avisos</button>
      </div>

      {activeTab === 'general' && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 animate-fade-in">
          {/* Coluna E-mail */}
          <div>
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4">Canal: E-mail</h4>
            {renderMessageList('E-mail', 'Transacionais')}
            {renderMessageList('E-mail', 'Engajamento')}
          </div>

          {/* Coluna WhatsApp */}
          <div>
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4">Canal: WhatsApp</h4>
            {renderMessageList('WhatsApp', 'Transacionais')}
            {renderMessageList('WhatsApp', 'Engajamento')}
          </div>

          {/* Coluna SMS */}
          <div>
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4">Canal: SMS</h4>
            {renderMessageList('SMS', 'Transacionais')}
            {renderMessageList('SMS', 'Engajamento')}
          </div>

          {/* Coluna Relatórios & Anexos */}
          <div>
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4">Canal: Relatórios & Anexos</h4>
            {renderMessageList('Relatórios & Anexos', 'Relatórios')}
            {renderMessageList('Relatórios & Anexos', 'Anexos')}
          </div>
        </div>
      )}

      {activeTab === 'whatsapp' && (
        <div className="animate-fade-in">
          <div className="max-w-xl">
            <div className="flex gap-4 border-b border-slate-800 mb-5 overflow-x-auto">
              <button onClick={() => setWhatsappSubTab('inboun')} className={`pb-2 font-bold text-xs uppercase ${whatsappSubTab === 'inboun' ? 'text-white border-b-2 border-orange-500' : 'text-slate-500'}`}>Inboun</button>
              <button onClick={() => setWhatsappSubTab('outbound')} className={`pb-2 font-bold text-xs uppercase ${whatsappSubTab === 'outbound' ? 'text-white border-b-2 border-orange-500' : 'text-slate-500'}`}>Outbound</button>
            </div>

            {whatsappSubTab === 'inboun' && (
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-8 shadow-xl">
                <div className="flex items-center gap-4 mb-8 bg-slate-950 p-4 rounded-lg border border-slate-800">
                  <div className={`w-3 h-3 rounded-full transition-all ${whatsappStatus === 'Salvo' ? 'bg-teal-400 shadow-[0_0_12px_rgba(45,212,191,0.6)]' : 'bg-amber-400'}`}></div>
                  <p className="font-bold text-slate-300">Status da Configuração: {whatsappStatus}</p>
                </div>
                <div className="flex flex-col gap-6">
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Número (DDD + Número)</label>
                    <div className="flex border border-slate-800 rounded-lg overflow-hidden bg-slate-950 focus-within:border-white transition">
                      <div className="bg-slate-800 px-4 flex items-center text-slate-400 font-bold text-sm">+55</div>
                      <input
                        type="text"
                        value={formatWhatsappDisplay(whatsappNum)}
                        onChange={(event) => {
                          const digits = stripWhatsappCountryCode(event.target.value)
                          setWhatsappNum(digits)
                          setWhatsappStatus('Pendente')
                        }}
                        placeholder="11 5141-0749"
                        className="bg-transparent p-3 text-white flex-1 outline-none font-mono"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Horário Atendimento</label>
                    <div className="flex flex-col gap-4 bg-slate-950 border border-slate-800 rounded-lg p-4 focus-within:border-white transition">
                      <div className="flex flex-wrap gap-1.5">
                        {WHATSAPP_SERVICE_WEEKDAYS.map((day) => {
                          const active = whatsappServiceDays.includes(day.v)
                          return (
                            <button
                              key={day.v}
                              type="button"
                              title={day.full}
                              aria-label={day.full}
                              aria-pressed={active}
                              onClick={() => {
                                const next = new Set(whatsappServiceDays)
                                if (next.has(day.v)) next.delete(day.v)
                                else next.add(day.v)
                                setWhatsappServiceDays(WHATSAPP_SERVICE_WEEKDAYS.filter((option) => next.has(option.v)).map((option) => option.v))
                                setWhatsappStatus('Pendente')
                              }}
                              className={`w-12 h-9 rounded-lg text-xs font-bold uppercase tracking-wide transition-colors ${active ? 'bg-orange-500 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}
                            >
                              {day.short}
                            </button>
                          )
                        })}
                      </div>
                      <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Início</span>
                          <input
                            type="time"
                            value={whatsappServiceStart}
                            onChange={(event) => {
                              setWhatsappServiceStart(event.target.value)
                              setWhatsappStatus('Pendente')
                            }}
                            className="bg-slate-900 border border-slate-700 text-white text-sm rounded px-2 py-1 outline-none focus:border-orange-500 [color-scheme:dark]"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Fim</span>
                          <input
                            type="time"
                            value={whatsappServiceEnd}
                            onChange={(event) => {
                              setWhatsappServiceEnd(event.target.value)
                              setWhatsappStatus('Pendente')
                            }}
                            className="bg-slate-900 border border-slate-700 text-white text-sm rounded px-2 py-1 outline-none focus:border-orange-500 [color-scheme:dark]"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Token de Acesso API (Opcional)</label>
                    <input
                      type="password"
                      placeholder="Insira o Bearer Token"
                      value={whatsappToken}
                      onChange={(event) => {
                        setWhatsappToken(event.target.value)
                        setWhatsappStatus('Pendente')
                      }}
                      className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-white transition outline-none font-mono text-sm"
                    />
                  </div>
                  <div className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-950 p-3">
                    <div>
                      <div className="text-xs font-bold text-slate-300 uppercase tracking-widest">Exibir botão para o usuário</div>
                      <div className="text-[11px] text-slate-500 mt-1">{whatsappEnabled ? 'Ativado' : 'Desativado'}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setWhatsappEnabled((prev) => !prev)
                        setWhatsappStatus('Pendente')
                      }}
                      className={`${whatsappEnabled ? 'bg-teal-500' : 'bg-slate-700'} w-10 h-5 rounded-full relative transition-colors duration-300`}
                      aria-pressed={whatsappEnabled}
                      aria-label="Ativar botão do WhatsApp para usuários"
                    >
                      <div className={`${whatsappEnabled ? 'left-6' : 'left-1'} absolute top-1 w-3 h-3 rounded-full bg-white transition-all duration-300 shadow-sm`}></div>
                    </button>
                  </div>
                  <button
                    disabled={loadingWhatsappConfig || whatsappStatus === 'Salvo'}
                    onClick={handleSaveWhatsappConfig}
                    style={{ backgroundColor: partnerColor }}
                    className={`mt-4 text-white font-bold py-4 rounded-lg flex items-center justify-center gap-2 shadow-lg transition ${loadingWhatsappConfig || whatsappStatus === 'Salvo' ? 'opacity-70 cursor-not-allowed' : 'hover:opacity-90'}`}
                  >
                    <Check size={20} /> Salvar Configuração
                  </button>
                </div>
              </div>
            )}

            {whatsappSubTab === 'outbound' && (
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-8 shadow-xl">
                <h3 className="text-lg font-bold text-white mb-2">Outbound</h3>
                <p className="text-slate-400 text-sm mb-5">Controle e status do envio de WhatsApp do sistema.</p>

                {loadingWhatsappOutboundRead && (
                  <div className="text-slate-500 text-sm">Carregando configurações...</div>
                )}

                {!loadingWhatsappOutboundRead && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="rounded-lg border border-slate-800 bg-slate-950 p-4">
                      <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">API de Envio</div>
                      <div className="text-white font-semibold">{whatsappOutboundRead.apiProvider || 'Não identificada no banco'}</div>
                      <div className="text-[11px] text-slate-500 mt-2">Identificada pela atividade de envio.</div>
                    </div>

                    <div className="rounded-lg border border-slate-800 bg-slate-950 p-4">
                      <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Envio WhatsApp no Sistema</div>
                      <div className="flex items-center justify-between gap-4 mt-1">
                        <div className={`font-semibold ${String(whatsappOutboundRead.outboundToggleState || '').startsWith('ON') ? 'text-teal-400' : 'text-rose-400'}`}>
                          {whatsappOutboundRead.outboundToggleState || 'Sem dados'}
                        </div>
                        <ToggleSwitch
                          checked={String(whatsappOutboundRead.outboundToggleState || '').startsWith('ON')}
                          onChange={() => {
                            if (!savingWhatsappOutboundToggle && whatsappOutboundRpcReady) handleToggleWhatsappOutbound()
                          }}
                          ariaLabel="Ativar ou desativar envio de WhatsApp do sistema"
                        />
                      </div>
                      <div className="text-[11px] text-slate-500 mt-2">{savingWhatsappOutboundToggle ? 'Salvando alteração...' : ''}</div>
                      {!whatsappOutboundRpcReady && (
                        <div className="text-[11px] text-amber-400 mt-2">{whatsappOutboundRpcMsg || 'ON/OFF indisponível no banco.'}</div>
                      )}
                    </div>

                    <div className="rounded-lg border border-slate-800 bg-slate-950 p-4">
                      <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Número de Envio (Outbound)</div>
                      <div className="text-white font-semibold">Gerenciado com segurança no servidor (Meta Cloud API)</div>
                      <div className="text-[11px] text-slate-500 mt-2">Número de envio definido por secret no servidor; não fica salvo no banco.</div>
                    </div>

                    <div className="rounded-lg border border-slate-800 bg-slate-950 p-4">
                      <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Atualização de Config Outbound</div>
                      <div className="text-white font-semibold">
                        {whatsappOutboundRead.configUpdatedAt ? new Date(whatsappOutboundRead.configUpdatedAt).toLocaleString('pt-BR') : 'Sem registro no banco'}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-2">{whatsappOutboundRead.configSourceLabel || 'Não há tabela dedicada de config Outbound identificada.'}</div>
                    </div>

                    <div className="rounded-lg border border-slate-800 bg-slate-950 p-4 md:col-span-2">
                      <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Último Envio WhatsApp</div>
                      <div className="text-slate-300">
                        {whatsappOutboundRead.lastMessageAt
                          ? `${new Date(whatsappOutboundRead.lastMessageAt).toLocaleString('pt-BR')} · Status: ${whatsappOutboundRead.lastMessageStatus || '-'} · Destino: ${whatsappOutboundRead.lastMessageRecipient || '-'}`
                          : 'Nenhum envio WhatsApp encontrado em message_logs.'}
                      </div>
                    </div>

                    <div className="rounded-lg border border-slate-800 bg-slate-950 p-4 md:col-span-2">
                      <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Resumo da Fila WhatsApp</div>
                      <div className="text-slate-300">
                        Total: {whatsappOutboundRead.queueTotal} · Pendentes: {whatsappOutboundRead.queuePending} · Processando: {whatsappOutboundRead.queueProcessing} · Concluídos: {whatsappOutboundRead.queueCompleted} · Falhas: {whatsappOutboundRead.queueFailed}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'email' && (
        <div className="animate-fade-in">
          <div className="max-w-xl">
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-8 shadow-xl">
              <div className="flex items-center gap-4 mb-8 bg-slate-950 p-4 rounded-lg border border-slate-800">
                <div className={`w-3 h-3 rounded-full transition-all ${emailStatus === 'Salvo' ? 'bg-teal-400 shadow-[0_0_12px_rgba(45,212,191,0.6)]' : 'bg-amber-400'}`}></div>
                <p className="font-bold text-slate-300">Status da Configuração: {emailStatus}</p>
              </div>

              <div className="space-y-4">
                <div className="rounded-lg border border-slate-800 bg-slate-950 p-4">
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">E-mail de Suporte</div>
                  <div className="text-white font-semibold break-all">{supportEmail || 'Nenhum e-mail configurado'}</div>
                  <div className="text-[11px] text-slate-500 mt-2">
                    Este endereço pode ser usado pelos templates e comunicações automáticas com a variável <code className="font-mono text-slate-300">{'{{email_suporte}}'}</code>.
                  </div>
                </div>

                <div className="rounded-lg border border-slate-800 bg-slate-950 p-4">
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Escopo</div>
                  <div className="text-slate-300">
                    {partner?.name ? `Configuração vinculada ao parceiro ${partner.name}.` : 'Selecione um parceiro para configurar o e-mail de suporte.'}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleOpenEmailSupportModal}
                  disabled={loadingEmailConfig || !partner?.id}
                  style={{ backgroundColor: partnerColor }}
                  className={`mt-4 w-full text-white font-bold py-4 rounded-lg flex items-center justify-center gap-2 shadow-lg hover:opacity-90 transition ${loadingEmailConfig || !partner?.id ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  <Edit2 size={18} /> Editar Configuração
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'sms' && <AdminSmsPanel />}

      {activeTab === 'routines' && (
        <div className="animate-fade-in">
          {!loadingHealth && healthStatus && healthStatus.ok === false && !(
            (healthStatus.stuck_pending || 0) > 0 &&
            healthStatus.queue_pending_due === 0 &&
            (healthStatus.recent_failures || 0) === 0 &&
            healthStatus.cron_active !== false
          ) && (
            <div className="mb-4 p-3 rounded-lg border border-rose-700/50 bg-rose-600/10 text-rose-300 text-xs font-bold flex items-center justify-between">
              <span>
                Processamento automático inativo. Último término: {healthStatus.last_run_end ? new Date(healthStatus.last_run_end).toLocaleString('pt-BR') : '-'} · Pendências vencidas (&gt;10 min): {healthStatus.stuck_pending || 0} · Falhas recentes: {healthStatus.recent_failures || 0}
              </span>
              <button
                onClick={async () => {
                  try {
                    setLoadingHealth(true)
                    const { data } = await supabase.rpc('communication_health_summary')
                    setHealthStatus(data || null)
                  } finally {
                    setLoadingHealth(false)
                  }
                }}
                className="px-2 py-1 rounded border border-rose-700/60 hover:bg-rose-700/20"
              >
                Atualizar
              </button>
            </div>
          )}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <div className="flex gap-4 border-b border-slate-800 mb-5 overflow-x-auto">
              <button onClick={() => setRoutinesSubTab('list')} className={`pb-2 font-bold text-xs uppercase ${routinesSubTab === 'list' ? 'text-white border-b-2 border-orange-500' : 'text-slate-500'}`}>Rotinas</button>
              <button onClick={() => setRoutinesSubTab('reports')} className={`pb-2 font-bold text-xs uppercase ${routinesSubTab === 'reports' ? 'text-white border-b-2 border-orange-500' : 'text-slate-500'}`}>Relatórios de Rotinas</button>
            </div>
            {routinesSubTab === 'list' && (
              <>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Rotinas</h4>
                  <button
                    onClick={openCreateRoutine}
                    className="px-3 py-1.5 rounded-lg font-bold text-white text-xs md:text-sm"
                    style={{ backgroundColor: partnerColor }}
                  >
                    +Criar Rotina
                  </button>
                </div>
                {loadingRoutines && (
                  <div className="bg-slate-950/30 rounded-lg border border-slate-800/50 p-6 text-center text-slate-400 text-sm">
                    Carregando...
                  </div>
                )}
            {!loadingRoutines && routines.length === 0 && (
              <div className="bg-slate-950/30 rounded-lg border border-slate-800/50 p-6 text-center">
                <div className="text-white font-bold mb-1">Nenhuma rotina criada</div>
                <div className="text-slate-400 text-sm">Clique em +Criar Rotina para começar.</div>
              </div>
            )}
            {!loadingRoutines && routines.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-slate-950/50 text-slate-400 text-[10px] uppercase font-semibold">
                    <tr>
                      <th className="p-3">Título</th>
                      <th className="p-3">Tipo</th>
                      <th className="p-3">Horário</th>
                      <th className="p-3">Dias</th>
                      <th className="p-3">Status</th>
                      <th className="p-3 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/50">
                    {routines.map(r => {
                      const tpl = (templates || []).find(t => t.id === r.template_id)
                      const tplAttachment = (templates || []).find(t => t.id === r.template_attachment_id)
                      return (
                        <tr key={r.id} className="text-slate-300 hover:bg-slate-800/30 transition text-xs">
                          <td className="p-3">
                            <div className="flex flex-col gap-1">
                              <span className="font-medium text-white">{r.title}</span>
                              <button
                                onClick={() => handleCloneRoutine(r)}
                                className="self-start px-2 py-1 rounded border border-slate-700 text-[10px] font-bold text-slate-300 hover:border-white/40 bg-slate-900"
                                title="Clonar Rotina"
                              >
                                Clonar Rotina
                              </button>
                            </div>
                          </td>
                          <td className="p-3">{r.type}</td>
                          <td className="p-3">{(r.dispatch_time || '').slice(0, 5) || '-'}</td>
                          <td className="p-3">{formatRoutineDays(r.dispatch_days)}</td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${r.is_active !== false ? 'bg-teal-500/10 text-teal-400 border-teal-600/40' : 'bg-rose-600/20 text-rose-400 border-rose-700/50'}`}>
                                {r.is_active !== false ? 'ATIVA' : 'INATIVA'}
                              </span>
                              {r.is_default && (
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold border bg-amber-500/10 text-amber-400 border-amber-600/40">
                                  PADRÃO
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="p-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <ToggleSwitch checked={r.is_active !== false} onChange={() => handleToggleRoutineActive(r)} ariaLabel="Ativar/Inativar" />
                              <button
                                onClick={() => handleDiagnoseRoutine(r)}
                                className="p-2 bg-slate-900 border border-slate-800 rounded-lg hover:bg-slate-800 text-sky-400 transition"
                                title="Diagnóstico"
                              >
                                <Search size={16} />
                              </button>
                              <button
                                onClick={() => handleOpenDispatchModal(r)}
                                className="p-2 bg-slate-900 border border-slate-800 rounded-lg hover:bg-slate-800 text-teal-400 transition"
                                title="Forçar Disparo"
                              >
                                <Send size={16} />
                              </button>
                              <button
                                onClick={() => openEditRoutine(r, true)}
                                className="p-2 bg-slate-900 border border-slate-800 rounded-lg hover:bg-slate-800 text-slate-300 transition"
                                title="Visualizar"
                              >
                                <Eye size={16} />
                              </button>
                              <button
                                onClick={() => openEditRoutine(r)}
                                className="p-2 bg-slate-900 border border-slate-800 rounded-lg hover:bg-slate-800 text-slate-300 transition"
                                title="Editar"
                              >
                                <Edit2 size={16} />
                              </button>
                              <button
                                onClick={() => handleDeleteRoutine(r)}
                                className="p-2 bg-slate-900 border border-slate-800 rounded-lg hover:bg-slate-800 text-rose-400 transition"
                                title="Excluir"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
            </>
            )}
            {routinesSubTab === 'reports' && (
              <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                   <div className="bg-slate-950 border border-slate-800 rounded-lg p-4">
                     <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Próximo Disparo CRON</div>
                     <div className="text-teal-400 font-semibold">{healthStatus?.next_run ? new Date(healthStatus.next_run).toLocaleString('pt-BR') : '-'}</div>
                     <div className="mt-1 text-[10px] text-slate-500">
                       {healthStatus?.cron_job_name ? `Job: ${healthStatus.cron_job_name}` : 'Job: -'}
                       {healthStatus?.cron_schedule ? ` · Agenda: ${healthStatus.cron_schedule}` : ''}
                       {healthStatus?.cron_active === false ? ' · Inativo' : ''}
                     </div>
                   </div>
                  <div className="bg-slate-950 border border-slate-800 rounded-lg p-4">
                    <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Último término</div>
                    <div className="text-white font-semibold">{healthStatus?.last_run_end ? new Date(healthStatus.last_run_end).toLocaleString('pt-BR') : '-'}</div>
                  </div>
                  <div className="bg-slate-950 border border-slate-800 rounded-lg p-4">
                    <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Falhas na última hora</div>
                    <div className="text-white font-semibold">{healthStatus?.recent_failures ?? 0}</div>
                  </div>
                  <div className="bg-slate-950 border border-slate-800 rounded-lg p-4">
                    <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Pendentes {">"} 10min</div>
                    <div className={`font-semibold ${healthStatus?.stuck_pending ? 'text-amber-400' : 'text-white'}`}>{healthStatus?.stuck_pending ?? 0}</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-slate-950 border border-slate-800 rounded-lg p-4">
                    <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Pendentes na fila</div>
                    <div className="text-white font-semibold">{healthStatus?.queue_pending_total ?? 0}</div>
                  </div>
                  <div className="bg-slate-950 border border-slate-800 rounded-lg p-4">
                    <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Pendentes prontos</div>
                    <div className="text-white font-semibold">{healthStatus?.queue_pending_due ?? 0}</div>
                  </div>
                  <div className="bg-slate-950 border border-slate-800 rounded-lg p-4">
                    <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Processando</div>
                    <div className="text-white font-semibold">{healthStatus?.queue_processing_total ?? 0}</div>
                  </div>
                  <div className="bg-slate-950 border border-slate-800 rounded-lg p-4">
                    <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Enfileirados no último CRON</div>
                    <div className="text-white font-semibold">{healthStatus?.queued_last_run ?? 0}</div>
                  </div>
                </div>

                <div className="bg-slate-950 border border-slate-800 rounded-lg p-4">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Histórico de Disparos CRON (pg_cron)</h4>
                  <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
                    <table className="w-full text-left">
                      <thead className="bg-slate-900 text-slate-400 text-[10px] uppercase font-semibold sticky top-0 z-10">
                        <tr>
                          <th className="p-3">Início</th>
                          <th className="p-3">Fim</th>
                          <th className="p-3">Status</th>
                          <th className="p-3">Mensagem do DB</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800/50">
                        {loadingHealth && <tr><td colSpan="4" className="p-6 text-center text-slate-500">Carregando...</td></tr>}
                        {!loadingHealth && (!healthStatus?.cron_history || healthStatus.cron_history.length === 0) && (
                          <tr><td colSpan="4" className="p-6 text-center text-slate-500">Nenhum registro de CRON encontrado.</td></tr>
                        )}
                        {!loadingHealth && healthStatus?.cron_history?.map(run => (
                          <tr key={run.runid} className="text-xs text-slate-300 hover:bg-slate-800/30 transition">
                            <td className="p-3">{run.start_time ? new Date(run.start_time).toLocaleString('pt-BR') : '-'}</td>
                            <td className="p-3">{run.end_time ? new Date(run.end_time).toLocaleString('pt-BR') : '-'}</td>
                            <td className="p-3"><span className={`font-bold ${run.status === 'succeeded' ? 'text-teal-400' : 'text-rose-400'}`}>{run.status}</span></td>
                            <td
                              className="p-3 text-[10px] max-w-xs truncate"
                              title={run.return_message === '0 rows' ? '0 rows (nenhuma rotina agendada para este minuto)' : (run.return_message || '')}
                            >
                              {run.return_message === '0 rows' ? '0 rows (nenhuma rotina neste minuto)' : (run.return_message || '-')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-slate-950 border border-slate-800 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Fila de Processamento (Rotinas e Disparos Manuais)</h4>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1 bg-slate-900 border border-slate-700 rounded px-2 py-1">
                        <Calendar size={12} className="text-slate-500" />
                        <input type="date" value={routineLogFilters.startDate} onChange={e => setRoutineLogFilters({ ...routineLogFilters, startDate: e.target.value })} className="bg-transparent border-none text-[10px] text-white outline-none" />
                      </div>
                      <span className="text-slate-600 text-[10px] font-bold">ATÉ</span>
                      <div className="flex items-center gap-1 bg-slate-900 border border-slate-700 rounded px-2 py-1">
                        <Calendar size={12} className="text-slate-500" />
                        <input type="date" value={routineLogFilters.endDate} onChange={e => setRoutineLogFilters({ ...routineLogFilters, endDate: e.target.value })} className="bg-transparent border-none text-[10px] text-white outline-none" />
                      </div>
                      <select value={routineLogFilters.status} onChange={e => setRoutineLogFilters({ ...routineLogFilters, status: e.target.value })} className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-[10px] text-white">
                        <option value="all">Status: Todos</option>
                        <option value="completed">Concluído</option>
                        <option value="failed">Falhou</option>
                        <option value="processing">Processando</option>
                        <option value="pending">Pendente</option>
                      </select>
                      <select value={routineLogFilters.routineId} onChange={e => setRoutineLogFilters({ ...routineLogFilters, routineId: e.target.value })} className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-[10px] text-white">
                        <option value="all">Rotina: Todas</option>
                        {routines.map(r => <option key={r.id} value={r.id}>{r.title}</option>)}
                      </select>
                      <button onClick={fetchRoutineLogs} className="px-2 py-1 rounded border border-slate-700 hover:bg-slate-800 text-slate-300"><RefreshCcw size={14} /></button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead className="bg-slate-900 text-slate-400 text-[10px] uppercase font-semibold">
                        <tr>
                          <th className="p-3">Data</th>
                          <th className="p-3">Rotina</th>
                          <th className="p-3">Status</th>
                          <th className="p-3">Tentativas</th>
                          <th className="p-3">Fim</th>
                          <th className="p-3">Detalhes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800/50">
                        {loadingRoutineLogs && (
                          <tr><td colSpan="6" className="p-6 text-center text-slate-500">Carregando...</td></tr>
                        )}
                        {!loadingRoutineLogs && routineLogs.map(item => {
                          const r = routines.find(x => x.id === item.routine_id)
                          const statusCls = item.status === 'completed' ? 'text-teal-400' : item.status === 'failed' ? 'text-rose-400' : 'text-slate-300'
                          return (
                            <tr key={item.id} className="text-xs text-slate-300 hover:bg-slate-800/30 transition">
                              <td className="p-3">{new Date(item.created_at).toLocaleString('pt-BR')}</td>
                              <td className="p-3">
                                <div className="text-white font-medium">{r?.title || '-'}</div>
                                <div className="text-[10px] text-slate-500">{item.routine_id || '-'}</div>
                              </td>
                              <td className="p-3"><span className={`font-bold ${statusCls}`}>{item.status}</span></td>
                              <td className="p-3">{item.attempts}</td>
                              <td className="p-3">{item.processed_at ? new Date(item.processed_at).toLocaleString('pt-BR') : '-'}</td>
                              <td className="p-3">
                                {item.last_error ? (
                                  <span className="text-rose-400">{String(item.last_error).slice(0, 72)}{String(item.last_error).length > 72 ? '…' : ''}</span>
                                ) : (
                                  <span className="text-slate-500">—</span>
                                )}
                              </td>
                            </tr>
                          )
                        })}
                        {!loadingRoutineLogs && routineLogs.length === 0 && (
                          <tr><td colSpan="6" className="p-6 text-center text-slate-500">Sem registros no período.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showDispatchModal && dispatchingRoutine && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md shadow-2xl p-6">
            <h3 className="text-xl font-bold text-white mb-4">Confirmar Disparo Forçado</h3>
            <p className="text-slate-300 mb-6">
              Tem certeza que deseja forçar o disparo da rotina <strong>{dispatchingRoutine.title}</strong> agora?
              <br /><br />
              <span className="text-rose-400 font-bold">Atenção:</span> Todos os usuários configurados nesta rotina receberão a comunicação instantaneamente.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowDispatchModal(false); setDispatchingRoutine(null) }}
                className="px-4 py-2 text-slate-400 font-bold hover:text-white transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmDispatch}
                className="px-6 py-2 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-lg shadow-lg transition"
              >
                Confirmar Disparo
              </button>
            </div>
          </div>
        </div>
      )}

      {showDiagModal && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg shadow-2xl p-6">
            <h3 className="text-xl font-bold text-white mb-4">Diagnóstico</h3>
            <div className="text-slate-200 text-sm space-y-2">
              {diagResult ? (
                <>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Status</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${diagResult.ok ? 'bg-teal-500/10 text-teal-400 border-teal-600/40' : 'bg-rose-600/20 text-rose-400 border-rose-700/50'}`}>
                      {diagResult.ok ? 'OK' : 'ERRO'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-slate-950 border border-slate-800 rounded p-2 text-xs">
                      <div className="text-slate-400">Dia confere</div>
                      <div className="font-bold">{String(diagResult.day_match)}</div>
                    </div>
                    <div className="bg-slate-950 border border-slate-800 rounded p-2 text-xs">
                      <div className="text-slate-400">Horário confere</div>
                      <div className="font-bold">{String(diagResult.time_match)} {diagResult.brt_now_hhmm ? `(${diagResult.brt_now_hhmm} BRT)` : ''}</div>
                    </div>
                  </div>
                  <div className="bg-slate-950 border border-slate-800 rounded p-2 text-xs">
                    <div className="text-slate-400 mb-1">Eventos</div>
                    <div className="space-y-1 max-h-52 overflow-auto">
                      {(diagResult.events || []).map((e, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-slate-900 border border-slate-800 rounded px-2 py-1">
                          <span className="font-mono text-[11px] text-rose-300">{e.event}</span>
                          <div className="flex items-center gap-2">
                            <span className={`text-[10px] font-bold ${e.active ? 'text-teal-400' : 'text-slate-500'}`}>{e.active ? 'ATIVO' : 'INATIVO'}</span>
                            <span className={`text-[10px] font-bold ${e.email_template_ok === true ? 'text-teal-400' : e.email_template_ok === false ? 'text-rose-400' : 'text-slate-500'}`}>E-mail</span>
                            <span className={`text-[10px] font-bold ${e.whatsapp_template_ok === true ? 'text-teal-400' : e.whatsapp_template_ok === false ? 'text-rose-400' : 'text-slate-500'}`}>WhatsApp</span>
                          </div>
                        </div>
                      ))}
                      {(diagResult.events || []).length === 0 && <div className="text-slate-500">Sem eventos configurados</div>}
                    </div>
                  </div>
                  {Array.isArray(diagResult.hints) && diagResult.hints.length > 0 && (
                    <div className="bg-slate-950 border border-slate-800 rounded p-2 text-xs">
                      <div className="text-slate-400 mb-1">Observações</div>
                      <div className="flex flex-wrap gap-1">
                        {diagResult.hints.map((h, i) => (
                          <span key={i} className="px-2 py-0.5 rounded-full text-[10px] font-bold border bg-yellow-500/10 text-yellow-400 border-yellow-600/40">{h}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-slate-500">Sem dados</div>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowDiagModal(false)} className="px-4 py-2 text-slate-400 font-bold hover:text-white transition">Fechar</button>
            </div>
          </div>
        </div>
      )}

      {showRoutineModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-[85%] shadow-2xl flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center p-6 border-b border-slate-800">
              <h3 className="text-xl font-bold text-white">
                {isRoutineReadOnly ? 'Visualizar Rotina' : (editingRoutine ? 'Editar Rotina' : 'Nova Rotina')}
              </h3>
              <button onClick={() => { setShowRoutineModal(false); setEditingRoutine(null); setShowRoutineRolesDropdown(false) }} className="text-slate-500 hover:text-white"><X size={20} /></button>
            </div>

            <div className="p-6 space-y-6 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                <div className="flex flex-col gap-1.5 md:col-span-3">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Tipo</label>
                  <select
                    disabled={isRoutineReadOnly}
                    value={routineForm.type}
                    onChange={e => {
                      const nextType = e.target.value
                      const nextInviteTemplate = (templates || []).find(t => t.channel === 'E-mail' && t.is_active !== false && String(t.meta?.tipo || '').trim() === 'Convite')
                      setRoutineForm({
                        ...routineForm,
                        type: nextType,
                        templateId: nextType === 'Convite' ? (nextInviteTemplate?.id || '') : routineForm.templateId,
                        templateAttachmentId: nextType === 'Convite' ? '' : routineForm.templateAttachmentId,
                      })
                    }}
                    className={`bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition ${isRoutineReadOnly ? 'opacity-70 cursor-not-allowed' : ''}`}
                  >
                    {Array.from(new Set([
                      ...transactionalTypes,
                      ...reportTypes,
                      ...((templates || []).filter(t => t.channel === 'E-mail' && t.is_active !== false).map(t => t.meta?.tipo).filter(Boolean)),
                      ...((templates || []).filter(t => t.channel === 'Relatórios & Anexos' && t.is_active !== false).map(t => t.meta?.tipo).filter(Boolean))
                    ])).filter(Boolean).map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col gap-1.5 md:col-span-9">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Título</label>
                  <input
                    disabled={isRoutineReadOnly}
                    value={routineForm.title}
                    onChange={e => setRoutineForm({ ...routineForm, title: e.target.value })}
                    className={`bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition ${isRoutineReadOnly ? 'opacity-70 cursor-not-allowed' : ''}`}
                    placeholder="Ex: Relatório diário"
                  />
                </div>

                <div className="flex flex-col gap-1.5 md:col-span-3">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Horário de disparo</label>
                  <input
                    disabled={isRoutineReadOnly}
                    type="time"
                    step={60}
                    value={routineForm.dispatchTime}
                    onChange={e => setRoutineForm({ ...routineForm, dispatchTime: e.target.value })}
                    className={`bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition ${isRoutineReadOnly ? 'opacity-70 cursor-not-allowed' : ''}`}
                  />
                </div>

                <div className="flex flex-col gap-1.5 md:col-span-6">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Dias de disparo</label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { id: 1, label: 'Seg' },
                      { id: 2, label: 'Ter' },
                      { id: 3, label: 'Qua' },
                      { id: 4, label: 'Qui' },
                      { id: 5, label: 'Sex' },
                      { id: 6, label: 'Sáb' },
                      { id: 0, label: 'Dom' },
                    ].map(d => (
                      <button
                        disabled={isRoutineReadOnly}
                        key={d.id}
                        type="button"
                        onClick={() => toggleRoutineDay(d.id)}
                        className={`px-3 py-1.5 rounded-lg border text-xs font-bold transition ${
                          (routineForm.dispatchDays || []).includes(d.id)
                            ? 'bg-teal-500/10 text-teal-300 border-teal-600/40'
                            : 'bg-slate-950 text-slate-400 border-slate-800 hover:bg-slate-800'
                        } ${isRoutineReadOnly ? 'opacity-70 cursor-not-allowed' : ''}`}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col gap-3 md:col-span-3">
                  <div className={`flex items-center justify-between gap-3 bg-slate-950 border border-slate-800 p-3 rounded-lg h-[46px] ${isRoutineReadOnly ? 'opacity-70' : ''}`}>
                    <div className="flex items-center gap-3">
                      <ToggleSwitch 
                        disabled={isRoutineReadOnly}
                        checked={!!routineForm.active} 
                        onChange={() => !isRoutineReadOnly && setRoutineForm({ ...routineForm, active: !routineForm.active })} 
                        ariaLabel="Ativa" 
                      />
                      <span className="text-xs text-slate-300 font-bold uppercase tracking-widest">Status: Ativa</span>
                    </div>
                  </div>
                  <div className={`flex items-center justify-between gap-3 bg-slate-950 border border-slate-800 p-3 rounded-lg h-[46px] ${isRoutineReadOnly ? 'opacity-70' : ''}`}>
                    <div className="flex items-center gap-3">
                      <ToggleSwitch 
                        disabled={isRoutineReadOnly}
                        checked={!!routineForm.isDefault} 
                        onChange={() => !isRoutineReadOnly && setRoutineForm({ ...routineForm, isDefault: !routineForm.isDefault })} 
                        ariaLabel="Tornar Padrão" 
                      />
                      <span className={`${routineForm.isDefault ? 'text-amber-400' : 'text-slate-300'} text-xs font-bold uppercase tracking-widest`}>Tornar Padrão</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-1.5 md:col-span-6">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Perfis</label>
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="relative">
                      <button 
                        disabled={isRoutineReadOnly}
                        onClick={() => !isRoutineReadOnly && setShowRoutineRolesDropdown(v => !v)} 
                        type="button" 
                        className={`px-4 py-2 rounded-lg border border-slate-700 bg-slate-950 text-slate-200 hover:border-white/40 transition min-w-[260px] text-left ${isRoutineReadOnly ? 'opacity-70 cursor-not-allowed' : ''}`}
                      >
                        {Array.isArray(routineForm.roleNames) && routineForm.roleNames.length > 0 ? 'Gerenciar seleção...' : 'Selecionar perfis...'}
                      </button>
                      {showRoutineRolesDropdown && !isRoutineReadOnly && (
                        <div className="absolute z-50 mt-2 w-80 max-h-60 overflow-auto bg-slate-900 border border-slate-700 rounded-lg shadow-xl p-2" onMouseLeave={() => setShowRoutineRolesDropdown(false)}>
                          {roleOptions.map(r => {
                            const selected = (routineForm.roleNames || []).includes(r)
                            return (
                              <div key={r} className={`flex items-center justify-between p-2 rounded cursor-pointer ${selected ? 'bg-teal-500/10' : 'hover:bg-slate-800'}`} onClick={() => toggleRoutineRoleName(r)}>
                                <span className="text-sm text-slate-200">{r}</span>
                                <ToggleSwitch checked={selected} onChange={(e) => { e.stopPropagation(); toggleRoutineRoleName(r) }} ariaLabel={`Selecionar ${r}`} />
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>



                <div className="flex flex-col gap-1.5 md:col-span-6">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Parceiros SAS</label>
                    <button
                      type="button"
                      onClick={() => setRoutineForm({ ...routineForm, isDefault: !routineForm.isDefault, partnerIds: (!routineForm.isDefault) ? [] : (routineForm.partnerIds || []) })}
                      className={`px-3 py-1.5 text-[11px] font-bold rounded-lg border transition ${routineForm.isDefault ? 'border-teal-600/60 bg-teal-500/10 text-teal-300' : 'border-slate-700 bg-slate-950 text-slate-300 hover:border-white/40'}`}
                    >
                      Tornar Padrão
                    </button>
                  </div>
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="relative">
                      <button 
                        disabled={isRoutineReadOnly || routineForm.isDefault}
                        onClick={() => !isRoutineReadOnly && !routineForm.isDefault && setShowRoutinePartnersDropdown(v => !v)} 
                        type="button" 
                        className={`px-4 py-2 rounded-lg border border-slate-700 bg-slate-950 text-slate-200 hover:border-white/40 transition min-w-[260px] text-left ${(isRoutineReadOnly || routineForm.isDefault) ? 'opacity-70 cursor-not-allowed' : ''}`}
                      >
                        {Array.isArray(routineForm.partnerIds) && routineForm.partnerIds.length > 0 ? 'Gerenciar parceiros...' : 'Selecionar parceiros...'}
                      </button>
                      {showRoutinePartnersDropdown && !isRoutineReadOnly && !routineForm.isDefault && (
                        <div className="absolute z-50 mt-2 w-96 max-h-60 overflow-auto bg-slate-900 border border-slate-700 rounded-lg shadow-xl p-2" onMouseLeave={() => setShowRoutinePartnersDropdown(false)}>
                          {(partners || []).map(p => {
                            const selected = (routineForm.partnerIds || []).includes(p.id)
                            return (
                              <div key={p.id} className={`flex items-center justify-between p-2 rounded cursor-pointer ${selected ? 'bg-teal-500/10' : 'hover:bg-slate-800'}`} onClick={() => {
                                const current = Array.isArray(routineForm.partnerIds) ? routineForm.partnerIds : []
                                const next = current.includes(p.id) ? current.filter(id => id !== p.id) : [...current, p.id]
                                setRoutineForm({ ...routineForm, partnerIds: next })
                              }}>
                                <span className="text-sm text-slate-200">{p.name}</span>
                                <ToggleSwitch checked={selected} onChange={(e) => { e.stopPropagation(); const current = Array.isArray(routineForm.partnerIds) ? routineForm.partnerIds : []; const next = current.includes(p.id) ? current.filter(id => id !== p.id) : [...current, p.id]; setRoutineForm({ ...routineForm, partnerIds: next }) }} ariaLabel={`Selecionar ${p.name}`} />
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                    {Array.isArray(routineForm.partnerIds) && routineForm.partnerIds.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {routineForm.partnerIds.map(id => {
                          const p = (partners || []).find(x => x.id === id)
                          return (
                            <span key={id} className="px-3 py-1 bg-slate-700/30 text-slate-300 border border-slate-600/50 rounded-full text-[10px] font-bold uppercase tracking-wider">
                              {p?.name || id}
                            </span>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-3 md:col-span-12">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">CONDIÇÕES</label>

                  { (routineForm.type || '') === 'Alertas' ? (
                    <div className="bg-slate-950 border border-slate-800 rounded-lg p-4 overflow-auto">
                      <table className="min-w-[760px] text-xs text-slate-300">
                        <thead>
                          <tr className="text-[10px] uppercase text-slate-400 tracking-widest border-b border-slate-800">
                            <th className="py-2 pr-4 text-left sticky left-0 z-20 bg-slate-950">Evento</th>
                            <th className="py-2 pr-4 text-left">Ativo</th>
                            <th className="py-2 pr-4 text-left">Momento</th>
                            <th className="py-2 pr-4 text-left">Primeiro lembrete (h)</th>
                            <th className="py-2 pr-4 text-left">Repetir a cada (h)</th>
                            <th className="py-2 pr-4 text-left">Máximo de envios</th>
                            <th className="py-2 pr-2 text-left">Template E-mail</th>
                            <th className="py-2 pr-2 text-left">Template SMS</th>
                            <th className="py-2 pr-2 text-left">Template WhatsApp</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ALERT_EVENTS.map(ev => {
                            const matrix = ensureAlertsMatrix()
                            const row = matrix[ev] || { active: false, firstReminderHours: 0, repeatEveryHours: 0, maxSends: 0, moment: 'after' }
                            return (
                              <tr 
                                key={ev} 
                                className="border-b border-slate-900 hover:bg-slate-900/60"
                              >
                                <td className="py-2 pl-2 pr-4 sticky left-0 z-10 bg-slate-950">
                                  <span className="text-rose-400 underline font-mono">{ev}</span>
                                </td>
                                <td className="py-2 pr-4">
                                  <ToggleSwitch
                                    disabled={isRoutineReadOnly}
                                    checked={!!row.active}
                                    onChange={() => {
                                      if (isRoutineReadOnly) return
                                      const next = { ...ensureAlertsMatrix(), [ev]: { ...row, active: !row.active } }
                                      setRoutineForm({ ...routineForm, conditions: { ...routineForm.conditions, alertsMatrix: next } })
                                    }}
                                    ariaLabel={`Ativar ${ev}`}
                                  />
                                </td>
                                <td className="py-2 pr-4">
                                  <select
                                    disabled={isRoutineReadOnly}
                                    value={row.moment || 'after'}
                                    onChange={e => {
                                      const next = { ...ensureAlertsMatrix(), [ev]: { ...row, moment: e.target.value } }
                                      setRoutineForm({ ...routineForm, conditions: { ...routineForm.conditions, alertsMatrix: next } })
                                    }}
                                    className="bg-slate-950 border border-slate-800 p-1.5 rounded text-xs text-white outline-none focus:border-white transition"
                                  >
                                    <option value="before">Antes</option>
                                    <option value="after">Depois</option>
                                    <option value="now">No momento</option>
                                  </select>
                                </td>
                                <td className="py-2 pr-4">
                                  <input
                                    type="number"
                                    min="0"
                                    disabled={isRoutineReadOnly}
                                    value={row.firstReminderHours}
                                    onChange={e => {
                                      const v = Math.max(0, Number(e.target.value || 0))
                                      const next = { ...ensureAlertsMatrix(), [ev]: { ...row, firstReminderHours: v } }
                                      setRoutineForm({ ...routineForm, conditions: { ...routineForm.conditions, alertsMatrix: next } })
                                    }}
                                    className="w-24 bg-slate-950 border border-slate-800 p-1.5 rounded text-slate-200 focus:border-white outline-none"
                                  />
                                </td>
                                <td className="py-2 pr-4">
                                  <input
                                    type="number"
                                    min="0"
                                    disabled={isRoutineReadOnly}
                                    value={row.repeatEveryHours}
                                    onChange={e => {
                                      const v = Math.max(0, Number(e.target.value || 0))
                                      const next = { ...ensureAlertsMatrix(), [ev]: { ...row, repeatEveryHours: v } }
                                      setRoutineForm({ ...routineForm, conditions: { ...routineForm.conditions, alertsMatrix: next } })
                                    }}
                                    className="w-24 bg-slate-950 border border-slate-800 p-1.5 rounded text-slate-200 focus:border-white outline-none"
                                  />
                                </td>
                                <td className="py-2 pr-4">
                                  <input
                                    type="number"
                                    min="0"
                                    disabled={isRoutineReadOnly}
                                    value={row.maxSends}
                                    onChange={e => {
                                      const v = Math.max(0, Number(e.target.value || 0))
                                      const next = { ...ensureAlertsMatrix(), [ev]: { ...row, maxSends: v } }
                                      setRoutineForm({ ...routineForm, conditions: { ...routineForm.conditions, alertsMatrix: next } })
                                    }}
                                    className="w-20 bg-slate-950 border border-slate-800 p-1.5 rounded text-slate-200 focus:border-white outline-none"
                                  />
                                </td>
                                <td className="py-2 pr-2">
                                  <select
                                    disabled={isRoutineReadOnly}
                                    value={row.templateId || ''}
                                    onChange={e => {
                                      if (isRoutineReadOnly) return
                                      const next = { ...ensureAlertsMatrix(), [ev]: { ...row, templateId: e.target.value || null } }
                                      setRoutineForm({ ...routineForm, conditions: { ...routineForm.conditions, alertsMatrix: next } })
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    className={`bg-slate-950 border border-slate-800 p-1.5 rounded text-xs text-white outline-none focus:border-white transition max-w-[300px] w-[300px] ${!row.templateId ? 'opacity-50' : ''}`}
                                  >
                                    {(() => {
                                      const list = (templates || [])
                                        .filter(t => t.channel === 'E-mail' && t.is_active !== false)
                                        .slice()
                                        .sort((a, b) => (a.subject || a.name || '').localeCompare(b.subject || b.name || ''))
                                      
                                      if (list.length === 0) return <option value="">Nenhum</option>
                                      return [
                                        <option key="none" value="">Inativo</option>,
                                        ...list.map(t => (
                                          <option key={t.id} value={t.id}>
                                            {t.code ? `[${t.code}] ` : ''}{(t.subject || t.name || 'Template')}
                                          </option>
                                        ))
                                      ]
                                    })()}
                                  </select>
                                </td>
                                <td className="py-2 pr-2">
                                  <select
                                    disabled={isRoutineReadOnly}
                                    value={row.smsTemplateId || ''}
                                    onChange={e => {
                                      if (isRoutineReadOnly) return
                                      const next = { ...ensureAlertsMatrix(), [ev]: { ...row, smsTemplateId: e.target.value || null } }
                                      setRoutineForm({ ...routineForm, conditions: { ...routineForm.conditions, alertsMatrix: next } })
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    className={`bg-slate-950 border border-slate-800 p-1.5 rounded text-xs text-white outline-none focus:border-white transition max-w-[300px] w-[300px] ${!row.smsTemplateId ? 'opacity-50' : ''}`}
                                  >
                                    {(() => {
                                      const list = (templates || [])
                                        .filter(t => t.channel === 'SMS' && t.is_active !== false)
                                        .slice()
                                        .sort((a, b) => (a.subject || a.name || '').localeCompare(b.subject || b.name || ''))
                                      
                                      if (list.length === 0) return <option value="">Nenhum</option>
                                      return [
                                        <option key="none" value="">Inativo</option>,
                                        ...list.map(t => (
                                          <option key={t.id} value={t.id}>
                                            {t.code ? `[${t.code}] ` : ''}{(t.subject || t.name || 'Template')}
                                          </option>
                                        ))
                                      ]
                                    })()}
                                  </select>
                                </td>
                                <td className="py-2 pr-2">
                                  <select
                                    disabled={isRoutineReadOnly}
                                    value={row.whatsappTemplateId || ''}
                                    onChange={e => {
                                      if (isRoutineReadOnly) return
                                      const next = { ...ensureAlertsMatrix(), [ev]: { ...row, whatsappTemplateId: e.target.value || null } }
                                      setRoutineForm({ ...routineForm, conditions: { ...routineForm.conditions, alertsMatrix: next } })
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    className={`bg-slate-950 border border-slate-800 p-1.5 rounded text-xs text-white outline-none focus:border-white transition max-w-[300px] w-[300px] ${!row.whatsappTemplateId ? 'opacity-50' : ''}`}
                                  >
                                    {(() => {
                                      const list = (templates || [])
                                        .filter(t => t.channel === 'WhatsApp' && t.is_active !== false)
                                        .slice()
                                        .sort((a, b) => (a.subject || a.name || '').localeCompare(b.subject || b.name || ''))
                                      
                                      if (list.length === 0) return <option value="">Nenhum</option>
                                      return [
                                        <option key="none" value="">Inativo</option>,
                                        ...list.map(t => (
                                          <option key={t.id} value={t.id}>
                                            {t.code ? `[${t.code}] ` : ''}{(t.subject || t.name || 'Template')}
                                          </option>
                                        ))
                                      ]
                                    })()}
                                  </select>
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (routineForm.type || '') === 'Convite' ? (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="md:col-span-4 rounded-lg border border-teal-700/40 bg-teal-500/10 p-4 text-sm text-slate-200">
                        <span className="font-bold text-teal-300">Elegibilidade:</span> somente usuários com status <strong>Pendente</strong>. O convite não depende de matrícula ativa e os reenvios param assim que o usuário ficar ativo.
                      </div>
                      <div className="bg-slate-950 border border-slate-800 rounded-lg p-4">
                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Primeiro reenvio após (h)</label>
                        <input
                          type="number"
                          min="0"
                          disabled={isRoutineReadOnly}
                          value={routineForm.conditions.inviteFirstSendHours}
                          onChange={e => setRoutineForm({ ...routineForm, conditions: { ...routineForm.conditions, inviteFirstSendHours: Math.max(0, Number(e.target.value || 0)) } })}
                          className="w-full bg-slate-900 border border-slate-700 p-2 rounded text-white outline-none focus:border-white"
                        />
                        <p className="mt-2 text-[11px] text-slate-500">Contado a partir do convite automático inicial.</p>
                      </div>
                      <div className="bg-slate-950 border border-slate-800 rounded-lg p-4">
                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Repetir a cada (h)</label>
                        <input
                          type="number"
                          min="1"
                          disabled={isRoutineReadOnly}
                          value={routineForm.conditions.inviteRepeatEveryHours}
                          onChange={e => setRoutineForm({ ...routineForm, conditions: { ...routineForm.conditions, inviteRepeatEveryHours: Math.max(1, Number(e.target.value || 1)) } })}
                          className="w-full bg-slate-900 border border-slate-700 p-2 rounded text-white outline-none focus:border-white"
                        />
                        <p className="mt-2 text-[11px] text-slate-500">Intervalo mínimo entre envios efetivos.</p>
                      </div>
                      <div className="bg-slate-950 border border-slate-800 rounded-lg p-4">
                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Máximo de envios</label>
                        <input
                          type="number"
                          min="1"
                          step="1"
                          disabled={isRoutineReadOnly}
                          value={routineForm.conditions.inviteMaxSends}
                          onChange={e => setRoutineForm({ ...routineForm, conditions: { ...routineForm.conditions, inviteMaxSends: Math.max(1, Math.floor(Number(e.target.value || 1))) } })}
                          className="w-full bg-slate-900 border border-slate-700 p-2 rounded text-white outline-none focus:border-white"
                        />
                        <p className="mt-2 text-[11px] text-slate-500">Considera somente os reenvios desta rotina.</p>
                      </div>
                      <div className="bg-slate-950 border border-slate-800 rounded-lg p-4">
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Horário e dias</div>
                        <p className="text-sm text-slate-300">A regra é avaliada apenas nos dias e horário definidos acima.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-slate-950 border border-slate-800 rounded-lg p-4">
                        <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 border-b border-slate-800 pb-2">Requisitos</h5>
                        <div className="flex items-center justify-between py-1.5">
                          <span className="text-sm text-slate-300">Enviar apenas se houver progresso</span>
                          <ToggleSwitch disabled={isRoutineReadOnly} checked={!!routineForm.conditions.sendIfProgress} onChange={() => !isRoutineReadOnly && toggleCondition('sendIfProgress')} ariaLabel="Enviar apenas se houver progresso" />
                        </div>
                        <div className="flex items-center justify-between py-1.5">
                          <span className="text-sm text-slate-300">Enviar apenas se houver novos acessos</span>
                          <ToggleSwitch disabled={isRoutineReadOnly} checked={!!routineForm.conditions.sendIfNewAccess} onChange={() => !isRoutineReadOnly && toggleCondition('sendIfNewAccess')} ariaLabel="Enviar apenas se houver novos acessos" />
                        </div>
                        <div className="flex items-center justify-between py-1.5">
                          <span className="text-sm text-slate-300">Enviar apenas se houver alunos pendentes</span>
                          <ToggleSwitch disabled={isRoutineReadOnly} checked={!!routineForm.conditions.sendIfPendingStudents} onChange={() => !isRoutineReadOnly && toggleCondition('sendIfPendingStudents')} ariaLabel="Enviar apenas se houver alunos pendentes" />
                        </div>
                      </div>

                      <div className="bg-slate-950 border border-slate-800 rounded-lg p-4">
                        <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 border-b border-slate-800 pb-2">Atividade</h5>
                        <div className="flex items-center justify-between py-1.5">
                          <span className="text-sm text-slate-300">Não enviar se não houver atividade por 7 dias</span>
                          <ToggleSwitch disabled={isRoutineReadOnly} checked={!!routineForm.conditions.skipIfNoActivity7d} onChange={() => !isRoutineReadOnly && toggleCondition('skipIfNoActivity7d')} ariaLabel="Não enviar se não houver atividade por 7 dias" />
                        </div>
                      </div>

                      <div className="bg-slate-950 border border-slate-800 rounded-lg p-4">
                        <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 border-b border-slate-800 pb-2">Conclusão</h5>
                        <div className="flex items-center justify-between py-1.5">
                          <span className="text-sm text-slate-300">Enviar relatório final ao atingir 100%</span>
                          <ToggleSwitch disabled={isRoutineReadOnly} checked={!!routineForm.conditions.sendFinalOn100} onChange={() => !isRoutineReadOnly && toggleCondition('sendFinalOn100')} ariaLabel="Enviar relatório final ao atingir 100%" />
                        </div>
                        <div className="flex items-center justify-between py-1.5">
                          <span className="text-sm text-slate-300">Pausar relatórios após conclusão</span>
                          <ToggleSwitch disabled={isRoutineReadOnly} checked={!!routineForm.conditions.pauseAfterCompletion} onChange={() => !isRoutineReadOnly && toggleCondition('pauseAfterCompletion')} ariaLabel="Pausar relatórios após conclusão" />
                        </div>
                        <div className="flex items-center justify-between py-1.5">
                          <span className="text-sm text-slate-300">Reativar se surgirem novos cursos</span>
                          <ToggleSwitch disabled={isRoutineReadOnly} checked={!!routineForm.conditions.reactivateOnNewCourses} onChange={() => !isRoutineReadOnly && toggleCondition('reactivateOnNewCourses')} ariaLabel="Reativar se surgirem novos cursos" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {(routineForm.type || '') !== 'Alertas' && (
                  <div className="flex flex-col gap-1.5 md:col-span-6">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">{(routineForm.type || '') === 'Convite' ? 'TEMPLATE DO CONVITE' : 'TEMPLATE-E-MAIL'}</label>
                    <select
                      disabled={isRoutineReadOnly}
                      value={routineForm.templateId}
                      onChange={e => setRoutineForm({ ...routineForm, templateId: e.target.value })}
                      className={`bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition ${isRoutineReadOnly ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      {(() => {
                        const list = (templates || [])
                          .filter(t => t.channel === 'E-mail' && t.is_active !== false)
                          .filter(t => (routineForm.type || '') !== 'Convite' || String(t.meta?.tipo || '').trim() === 'Convite')
                          .slice()
                          .sort((a, b) => (a.subject || a.name || '').localeCompare(b.subject || b.name || ''))
                        if (list.length === 0) return <option value="">Nenhum template disponível</option>
                        return [
                          <option key="default" value="">Selecione um template...</option>,
                          ...list.map(t => (
                            <option key={t.id} value={t.id}>
                              {(t.subject || t.name || 'Template')} {t.code ? `(${t.code})` : ''}
                            </option>
                          ))
                        ]
                      })()}
                    </select>
                  </div>
                )}

                {(routineForm.type || '') !== 'Alertas' && (routineForm.type || '') !== 'Convite' && (
                  <div className="flex flex-col gap-1.5 md:col-span-6">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">TEMPLATE ANEXO</label>
                    <select
                      disabled={isRoutineReadOnly}
                      value={routineForm.templateAttachmentId}
                      onChange={e => setRoutineForm({ ...routineForm, templateAttachmentId: e.target.value })}
                      className={`bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition ${isRoutineReadOnly ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      {(() => {
                        const list = (templates || [])
                          .filter(t => t.channel === 'Relatórios & Anexos' && t.is_active !== false)
                          .slice()
                          .sort((a, b) => (a.subject || a.name || '').localeCompare(b.subject || b.name || ''))
                        if (list.length === 0) return <option value="">Nenhum template disponível</option>
                        return [
                          <option key="none" value="">Nenhum</option>,
                          ...list.map(t => (
                            <option key={t.id} value={t.id}>
                              {(t.subject || t.name || 'Template')} {t.code ? `(${t.code})` : ''}
                            </option>
                          ))
                        ]
                      })()}
                    </select>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t border-slate-800 mt-auto">
              <button onClick={() => { setShowRoutineModal(false); setEditingRoutine(null); setShowRoutineRolesDropdown(false) }} className="px-4 py-2 text-slate-400 font-bold hover:text-white transition">
                {isRoutineReadOnly ? 'Fechar' : 'Cancelar'}
              </button>
              {!isRoutineReadOnly && (
                <button
                  onClick={handleSaveRoutine}
                  style={{ backgroundColor: partnerColor }}
                  className="px-6 py-2 text-white font-bold rounded-lg shadow-lg hover:opacity-90 transition"
                >
                  {editingRoutine ? 'Salvar Alterações' : 'Criar Rotina'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showDeleteRoutineModal && routineToDelete && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md shadow-2xl p-6">
            <h3 className="text-xl font-bold text-white mb-4">Confirmar Exclusão</h3>
            <p className="text-slate-300 mb-6">
              Tem certeza que deseja excluir a rotina <strong>{routineToDelete.title}</strong>?
              <br /><br />
              <span className="text-rose-400 font-bold">Atenção:</span> Esta ação não poderá ser desfeita.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowDeleteRoutineModal(false); setRoutineToDelete(null) }}
                className="px-4 py-2 text-slate-400 font-bold hover:text-white transition"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDeleteRoutine}
                className="px-6 py-2 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-lg shadow-lg transition"
              >
                Confirmar Exclusão
              </button>
            </div>
          </div>
        </div>
      )}

      {showTypeSelector && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
            <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md shadow-2xl flex flex-col">
                <div className="flex justify-between items-center p-6 border-b border-slate-800">
                    <h3 className="text-xl font-bold text-white">Nova Configuração</h3>
                    <button onClick={() => setShowTypeSelector(false)} className="text-slate-500 hover:text-white"><X size={20} /></button>
                </div>
                <div className="p-6 space-y-4">
                    <p className="text-slate-400 text-sm">Selecione o tipo de mensagem que deseja configurar:</p>
                    <div className="flex flex-col gap-2">
                        {transactionalTypes.map(t => (
                            <button
                                key={t}
                                onClick={() => {
                                    // Check if this type already has linked messages
                                    const existing = linkedMessages[linkTemplateChannel]?.[linkTemplateCategory]?.filter(m => m.type === t) || []
                                    const existingIds = existing.map(m => m.id)
                                    
                                    setLinkTemplateForm({ ...linkTemplateForm, type: t })
                                    if (existingIds.length > 0) {
                                        setEditingConfigurationType(t)
                                        setSelectedTemplateIds(existingIds)
                                    } else {
                                        setEditingConfigurationType(null) // Creating new
                                        setSelectedTemplateIds([])
                                    }
                                    setShowTypeSelector(false)
                                    setShowLinkTemplateModal(true)
                                }}
                                className="text-left px-4 py-3 rounded-lg bg-slate-950 border border-slate-800 hover:border-teal-500/50 hover:bg-slate-800 transition text-slate-200 font-bold text-sm"
                            >
                                {t}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
      )}

      {showLinkTemplateModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-2xl shadow-2xl flex flex-col max-h-[85vh]">
            <div className="flex justify-between items-center p-6 border-b border-slate-800">
              <h3 className="text-xl font-bold text-white">{replacingTemplate ? `Editar Mensagem - ${replacingTemplate.title}` : `Nova Mensagem - ${linkTemplateChannel} (${linkTemplateCategory})`}</h3>
              <button onClick={closeLinkTemplateModal} className="text-slate-500 hover:text-white"><X size={20} /></button>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Tipo</label>
                  <select 
                    value={linkTemplateForm.type} 
                    onChange={e => setLinkTemplateForm({...linkTemplateForm, type: e.target.value})}
                    disabled={!!editingConfigurationType || !!linkTemplateForm.type}
                    className={`bg-slate-950 border border-slate-800 p-2.5 rounded-lg text-white outline-none focus:border-white transition text-sm ${!!editingConfigurationType || !!linkTemplateForm.type ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <option value="">Todos</option>
                    {transactionalTypes.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">ID (Código)</label>
                  <input 
                    value={linkTemplateForm.code} 
                    onChange={e => setLinkTemplateForm({...linkTemplateForm, code: e.target.value})}
                    placeholder="Ex: TPL-..."
                    className="bg-slate-950 border border-slate-800 p-2.5 rounded-lg text-white outline-none focus:border-white transition text-sm" 
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Título</label>
                  <input 
                    value={linkTemplateForm.title} 
                    onChange={e => setLinkTemplateForm({...linkTemplateForm, title: e.target.value})}
                    placeholder="Buscar por título..."
                    className="bg-slate-950 border border-slate-800 p-2.5 rounded-lg text-white outline-none focus:border-white transition text-sm" 
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Templates Ativos Encontrados</label>
                <div className="bg-slate-950 border border-slate-800 rounded-lg max-h-[300px] overflow-y-auto">
                  {getFilteredActiveTemplates().length > 0 ? (
                    <div className="divide-y divide-slate-800/50">
                      {getFilteredActiveTemplates().map(t => (
                        <div key={t.id} className="p-3 hover:bg-slate-800/50 transition flex items-center justify-between group">
                          <div className="flex flex-col min-w-0">
                            <span className="text-sm font-bold text-white">{t.subject}</span>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              <span className="text-[10px] bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded uppercase font-bold">{t.channel}</span>
                              <span className="text-[10px] text-slate-500">{t.code}</span>
                              <span className="text-[10px] text-teal-500/80 uppercase">{t.meta?.tipo}</span>
                            </div>
                            <TemplateUsageBadges meta={t.meta} />
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                                onClick={() => {
                                    setEditingTemplate(t)
                                    setTransactionalChannel(t.channel || linkTemplateChannel)
                                    const meta = t.meta || {}
                                    const vars = meta.variaveis || []
                                    setTransactionalForm({
                                    tipo: meta.tipo || 'Comunicado',
                                    header: null,
                                    headerUrl: meta.headerUrl || '',
                                    tornarHeaderPadrao: false,
                                    usarHtml: t.is_html !== undefined ? t.is_html : !!meta.usarHtml,
                                    titulo: t.subject || '',
                                    textoHtml: meta.textoHtml || t.content || '',
                                    botaoHabilitado: !!(meta.botao && (meta.botao.titulo || meta.botao.link)),
                                    botaoTitulo: (meta.botao && meta.botao.titulo) || '',
                                    botaoLink: (meta.botao && meta.botao.link) || '',
                                    observacaoHabilitada: !!meta.observacao,
                                    observacao: meta.observacao || '',
                                    rodape: null,
                                    rodapeUrl: meta.rodapeUrl || '',
                                    tornarRodapePadrao: false,
                                    variaveisHabilitadas: Array.isArray(vars) && vars.length > 0,
                                    variaveis: buildVariaveisFromArray(vars),
                                    atribuirParceiros: Array.isArray(meta.parceirosSAS) && meta.parceirosSAS.length > 0,
                                    parceirosSelecionados: meta.parceirosSAS || [],
                                    anexos: Array.isArray(meta.anexos) ? meta.anexos : [],
                                    anexosAberto: true,
                                    active: t.is_active !== false,
                                    code: t.code || 'TPL-' + Math.random().toString(36).substring(2, 8).toUpperCase(),
                                    isDefault: !!t.meta?.is_default,
                                    usoConvitePadrao: !!meta.usos?.convite_padrao,
                                    usoConviteLink: !!meta.usos?.convite_link,
                                    metaTemplateName: (meta.metaTemplate && meta.metaTemplate.name) || '',
                                    metaTemplateLanguage: (meta.metaTemplate && meta.metaTemplate.language) || 'pt_BR',
                                    metaTemplateParams: Array.isArray(meta.metaTemplate?.params) ? meta.metaTemplate.params : [],
                                    metaTemplateButtonEnabled: !!(meta.metaTemplate?.button && (meta.metaTemplate.button.prefix || meta.metaTemplate.button.paramKey)),
                                    metaTemplateButtonPrefix: (meta.metaTemplate?.button && meta.metaTemplate.button.prefix) || '',
                                    metaTemplateButtonVar: (meta.metaTemplate?.button && meta.metaTemplate.button.paramKey) || '',
                                    })
                                    setShowTransactionalModal(true)
                                }}
                                className="opacity-0 group-hover:opacity-100 transition px-2 py-1.5 bg-slate-800 text-slate-400 text-xs font-bold rounded border border-slate-700 hover:bg-slate-700 hover:text-white"
                                title="Editar Conteúdo"
                            >
                                <Edit2 size={14} />
                            </button>
                            <ToggleSwitch 
                                checked={selectedTemplateIds.includes(t.id)} 
                                onChange={() => {
                                    const next = selectedTemplateIds.includes(t.id)
                                        ? selectedTemplateIds.filter(id => id !== t.id)
                                        : [...selectedTemplateIds, t.id]
                                    setSelectedTemplateIds(next)
                                }} 
                                ariaLabel="Selecionar" 
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center text-slate-500 text-sm">
                      Nenhum template ativo encontrado com os filtros atuais.
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 p-6 border-t border-slate-800 mt-auto">
              <button
                onClick={closeLinkTemplateModal}
                className="px-4 py-2 text-slate-400 font-bold hover:text-white transition"
              >Cancelar</button>
              <button
                onClick={handleSaveLinks}
                style={{ backgroundColor: partnerColor }}
                className="px-4 py-2 text-white font-bold rounded-lg shadow-lg hover:opacity-90 transition"
              >
                {replacingTemplate ? 'Salvar Alterações' : 'Vincular Selecionados'}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'messages' && (
        <div className="animate-fade-in">
          <div className="flex gap-4 border-b border-slate-800 mb-6">
            <button onClick={() => setMessagesSubTab('history')} className={`pb-2 text-xs font-bold uppercase ${messagesSubTab === 'history' ? 'text-white border-b-2 border-orange-500' : 'text-slate-500 hover:text-slate-300'}`}>Histórico</button>
            <button onClick={() => setMessagesSubTab('manual')} className={`pb-2 text-xs font-bold uppercase ${messagesSubTab === 'manual' ? 'text-white border-b-2 border-orange-500' : 'text-slate-500 hover:text-slate-300'}`}>Envios Manuais</button>
          </div>
          
          {messagesSubTab === 'history' && (
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
            <div className="flex flex-col gap-4 mb-4">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Mensagens Enviadas</h4>
                <div className="flex items-center gap-2">
                  {canDeleteMessageLogs && (
                    <button
                      onClick={handleDeleteSelectedSentMessages}
                      disabled={selectedSentMessageIds.length === 0 || deletingSentMessages}
                      className="px-3 py-1.5 rounded-lg font-bold text-white text-xs transition disabled:opacity-50 disabled:cursor-not-allowed bg-rose-600 hover:bg-rose-500 flex items-center gap-1.5"
                    >
                      <Trash2 size={14} />
                      {deletingSentMessages ? 'Excluindo...' : `Excluir Selecionadas${selectedSentMessageIds.length ? ` (${selectedSentMessageIds.length})` : ''}`}
                    </button>
                  )}
                  <button
                    onClick={handleClearMessageFilters}
                    disabled={!hasActiveMessageFilters}
                    title="Limpar filtros"
                    aria-label="Limpar filtros"
                    className="h-8 w-8 rounded-lg border border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800 hover:text-white transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    <FilterX size={14} />
                  </button>
                  <button onClick={fetchSentMessages} className="px-3 py-1.5 rounded-lg font-bold text-white text-xs hover:opacity-90 transition" style={{ backgroundColor: partnerColor }}>
                    Atualizar
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-7 gap-3 bg-slate-950/50 p-3 rounded-lg border border-slate-800">
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] uppercase font-bold text-slate-500">Início</label>
                  <input 
                    type="date" 
                    value={messageFilters.startDate}
                    onChange={e => setMessageFilters({...messageFilters, startDate: e.target.value})}
                    className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-white outline-none focus:border-slate-500 [color-scheme:dark]"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] uppercase font-bold text-slate-500">Fim</label>
                  <input 
                    type="date" 
                    value={messageFilters.endDate}
                    onChange={e => setMessageFilters({...messageFilters, endDate: e.target.value})}
                    className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-white outline-none focus:border-slate-500 [color-scheme:dark]"
                  />
                </div>
                <MultiSelectDropdown
                  label="Status"
                  options={messageStatusOptions}
                  selectedIds={messageFilters.statuses}
                  onChange={(ids) => setMessageFilters(prev => ({ ...prev, statuses: ids }))}
                  dropdownKey="messageStatuses"
                  placeholder="Todos"
                />
                <MultiSelectDropdown
                  label="Canal"
                  options={messageChannelOptions}
                  selectedIds={messageFilters.channels}
                  onChange={(ids) => setMessageFilters(prev => ({ ...prev, channels: ids }))}
                  dropdownKey="messageChannels"
                  placeholder="Todos"
                />
                <MultiSelectDropdown
                  label="Tipo"
                  options={messageTypeOptions}
                  selectedIds={messageFilters.types}
                  onChange={(ids) => setMessageFilters(prev => ({ ...prev, types: ids }))}
                  dropdownKey="messageTypes"
                  placeholder="Todos"
                />
                <MultiSelectDropdown
                  label="Parceiro SAS"
                  options={messagePartnerOptions}
                  selectedIds={messageFilters.partnerIds}
                  onChange={(ids) => setMessageFilters(prev => ({ ...prev, partnerIds: ids }))}
                  dropdownKey="messagePartners"
                  placeholder="Todos"
                />
                <MultiSelectDropdown
                  label="Empresa"
                  options={messageCompanyOptions}
                  selectedIds={messageFilters.companyIds}
                  onChange={(ids) => setMessageFilters(prev => ({ ...prev, companyIds: ids }))}
                  dropdownKey="messageCompanies"
                  placeholder="Todas"
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-slate-950/50 text-slate-400 text-[10px] uppercase font-semibold">
                  <tr>
                    {canDeleteMessageLogs && (
                      <th className="p-3 w-10">
                        <input
                          type="checkbox"
                          checked={allVisibleSentMessagesSelected}
                          onChange={handleToggleSelectAllSentMessages}
                          disabled={filteredSentMessages.length === 0}
                          className="h-4 w-4 rounded border-slate-600 bg-slate-900 text-orange-500 focus:ring-orange-500"
                          aria-label="Selecionar todas as mensagens visíveis"
                        />
                      </th>
                    )}
                    <th className="p-3">Para</th>
                    <th className="p-3">Envio Supabase</th>
                    <th className="p-3">Status Resend</th>
                    <th className="p-3">Título</th>
                    <th className="p-3">Time</th>
                    <th className="p-3">Canal</th>
                    <th className="p-3">Tipo</th>
                    <th className="p-3">Parceiro SAS</th>
                    <th className="p-3">Empresa</th>
                    <th className="p-3 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                  {loadingMessages && (
                    <tr><td colSpan={canDeleteMessageLogs ? 11 : 10} className="p-4 text-slate-500 text-sm">Carregando...</td></tr>
                  )}
                  {!loadingMessages && filteredSentMessages.length === 0 && (
                    <tr><td colSpan={canDeleteMessageLogs ? 11 : 10} className="p-4 text-slate-500 text-sm">Nenhuma mensagem encontrada com os filtros atuais.</td></tr>
                  )}
                  {!loadingMessages && filteredSentMessages.map(m => (
                    <tr key={m.id} className="text-slate-300 hover:bg-slate-800/30 transition text-xs">
                      {canDeleteMessageLogs && (
                        <td className="p-3">
                          <input
                            type="checkbox"
                            checked={selectedSentMessageIds.includes(m.id)}
                            onChange={() => handleToggleSentMessageSelection(m.id)}
                            className="h-4 w-4 rounded border-slate-600 bg-slate-900 text-orange-500 focus:ring-orange-500"
                            aria-label={`Selecionar mensagem para ${m.recipient}`}
                          />
                        </td>
                      )}
                      <td className="p-3">{m.recipient}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border
                          ${m.status === 'Enviado' || m.status === 'Entregue' || m.status === 'Aberto' || m.status === 'Clicado' ? 'bg-sky-500/10 text-sky-400 border-sky-600/40' : ''}
                          ${m.status === 'Falha' ? 'bg-rose-600/20 text-rose-400 border-rose-700/50' : ''}
                        `}>{m.status === 'Falha' ? 'Falha' : 'Enviado'}</span>
                      </td>
                      <td className="p-3">
                        {m.provider_event ? (
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border
                            ${m.provider_event.includes('delivered') ? 'bg-teal-500/10 text-teal-400 border-teal-600/40' : ''}
                            ${m.provider_event.includes('opened') ? 'bg-indigo-500/10 text-indigo-400 border-indigo-600/40' : ''}
                            ${m.provider_event.includes('clicked') ? 'bg-purple-500/10 text-purple-400 border-purple-600/40' : ''}
                            ${m.provider_event.includes('bounced') ? 'bg-rose-500/10 text-rose-400 border-rose-600/40' : ''}
                            ${m.provider_event.includes('complained') ? 'bg-amber-500/10 text-amber-400 border-amber-600/40' : ''}
                            ${m.provider_event.includes('delayed') ? 'bg-yellow-500/10 text-yellow-400 border-yellow-600/40' : ''}
                            ${m.provider_event.includes('sent') ? 'bg-slate-500/10 text-slate-400 border-slate-600/40' : ''}
                          `}>{m.provider_event}</span>
                        ) : (
                          <span className="text-slate-600">-</span>
                        )}
                      </td>
                      <td className="p-3 font-medium text-white">{m.title || '-'}</td>
                      <td className="p-3">{m.created_at ? new Date(m.created_at).toLocaleString('pt-BR') : '-'}</td>
                      <td className="p-3">{m.channel}</td>
                      <td className="p-3">{m.type || '-'}</td>
                      <td className="p-3">{m.partner_name || <span className="text-slate-600">-</span>}</td>
                      <td className="p-3">{m.company_name || <span className="text-slate-600">-</span>}</td>
                      <td className="p-3 text-right">
                        <button 
                          onClick={() => { setSelectedMessage(m); setShowMessageModal(true) }} 
                          className="px-2 py-1 rounded bg-slate-800 text-slate-200 border border-slate-700 hover:bg-slate-700"
                          title="Detalhes"
                        >Ver</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          )}

          {messagesSubTab === 'manual' && (
            <div className="animate-fade-in">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h3 className="text-lg font-bold text-white">Envios Manuais</h3>
                        <p className="text-slate-400 text-sm">Dispare mensagens avulsas para perfis ou listas.</p>
                    </div>
                    <button 
                        onClick={() => setShowManualSendModal(true)}
                        className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-bold transition flex items-center gap-2 shadow-lg shadow-orange-500/20"
                    >
                        <Send size={16} />
                        + Envio
                    </button>
                </div>
                
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-16 flex flex-col items-center justify-center text-center">
                    <div className="w-16 h-16 rounded-full bg-slate-950 border border-slate-800 flex items-center justify-center mb-4">
                        <Send size={32} className="text-slate-600" />
                    </div>
                    <p className="text-slate-500 text-sm">Nenhum envio manual registrado recentemente.</p>
                </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'daily_report' && (
        <div className="flex flex-col gap-6 animate-fade-in">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 shadow-xl">
             <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center text-white">
                    <Settings size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Configurações de Envio</h3>
                    <p className="text-slate-400 text-sm">Defina a frequência e as regras para o envio automático.</p>
                  </div>
                </div>
                
                <button 
                  onClick={() => setIsConfirmingSend(true)}
                  disabled={isSending}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-bold transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-orange-500/20"
                >
                  {isSending ? <RefreshCcw size={16} className="animate-spin" /> : <Send size={16} />} 
                  Enviar agora
                </button>
             </div>
             
             <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
               <div className="p-4 rounded-lg bg-slate-950 border border-slate-800">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-bold text-white flex items-center gap-2">
                      <Calendar size={16} className="text-white" />
                      Frequência
                    </h4>
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${dailyReportConfig.active ? 'text-orange-400' : 'text-slate-500'}`}>
                        {dailyReportConfig.active ? 'Ativa' : 'Pausada'}
                      </span>
                      <ToggleSwitch 
                        checked={dailyReportConfig.active} 
                        onChange={() => setDailyReportConfig({...dailyReportConfig, active: !dailyReportConfig.active})} 
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-2 mb-4">
                    {[{v: 1, l: 'S'}, {v: 2, l: 'T'}, {v: 3, l: 'Q'}, {v: 4, l: 'Q'}, {v: 5, l: 'S'}, {v: 6, l: 'S'}, {v: 0, l: 'D'}].map(d => (
                      <button
                        key={d.v}
                        onClick={() => {
                          const current = dailyReportConfig.dispatchDays
                          const next = current.includes(d.v) ? current.filter(x => x !== d.v) : [...current, d.v]
                          setDailyReportConfig({...dailyReportConfig, dispatchDays: next})
                        }}
                        className={`w-8 h-8 rounded-lg text-xs font-bold transition-colors ${dailyReportConfig.dispatchDays.includes(d.v) ? 'bg-orange-500 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}
                      >
                        {d.l}
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-400">Horário:</span>
                    <input 
                      type="time" 
                      value={dailyReportConfig.dispatchTime}
                      onChange={(e) => setDailyReportConfig({...dailyReportConfig, dispatchTime: e.target.value})}
                      className="bg-slate-900 border border-slate-700 text-white text-sm rounded px-2 py-1 outline-none focus:border-orange-500 [color-scheme:dark]"
                    />
                  </div>
               </div>

               <div className="p-4 rounded-lg bg-slate-950 border border-slate-800">
                  <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                    <User size={16} className="text-white" />
                    Perfis Destinatários
                  </h4>
                  <div className="flex flex-col gap-2 max-h-40 overflow-y-auto pr-2 custom-scrollbar">
                    {[...builtinRoles, ...customRoles].map(role => (
                      <label key={role} className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer hover:text-white">
                        <input 
                          type="checkbox" 
                          checked={dailyReportConfig.roles.includes(role)}
                          onChange={() => {
                            const current = dailyReportConfig.roles
                            const next = current.includes(role) ? current.filter(r => r !== role) : [...current, role]
                            setDailyReportConfig({...dailyReportConfig, roles: next})
                          }}
                          className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-orange-500 focus:ring-orange-500 focus:ring-offset-slate-900"
                        />
                        {role}
                      </label>
                    ))}
                  </div>
               </div>

               <div className="p-4 rounded-lg bg-slate-950 border border-slate-800">
                  <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                    <Settings size={16} className="text-white" />
                    Condições
                  </h4>
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-300">Enviar relatório final ao atingir 100%</span>
                      <ToggleSwitch 
                        checked={dailyReportConfig.sendFinalOn100} 
                        onChange={() => setDailyReportConfig({...dailyReportConfig, sendFinalOn100: !dailyReportConfig.sendFinalOn100})} 
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-300">Pausar relatórios após conclusão</span>
                      <ToggleSwitch 
                        checked={dailyReportConfig.pauseAfterCompletion} 
                        onChange={() => setDailyReportConfig({...dailyReportConfig, pauseAfterCompletion: !dailyReportConfig.pauseAfterCompletion})} 
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-300">Reativar se surgirem novos cursos</span>
                      <ToggleSwitch 
                        checked={dailyReportConfig.reactivateOnNewCourses} 
                        onChange={() => setDailyReportConfig({...dailyReportConfig, reactivateOnNewCourses: !dailyReportConfig.reactivateOnNewCourses})} 
                      />
                    </div>
                  </div>
               </div>
             </div>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 shadow-xl">
             <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-lg font-bold text-white">Relatórios Diários Gerados</h3>
                  <p className="text-slate-400 text-sm mb-4">Histórico de relatórios automáticos processados pelo sistema.</p>
                  
                  <div className="flex flex-wrap items-center gap-4 bg-slate-950/50 p-3 rounded-lg border border-slate-800/50">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Frequência:</span>
                        <div className="flex gap-1">
                          {[{v: 1, l: 'S'}, {v: 2, l: 'T'}, {v: 3, l: 'Q'}, {v: 4, l: 'Q'}, {v: 5, l: 'S'}, {v: 6, l: 'S'}, {v: 0, l: 'D'}].map(d => (
                            <button
                              key={d.v}
                              onClick={() => {
                                const current = reportGenerationConfig.days
                                const next = current.includes(d.v) ? current.filter(x => x !== d.v) : [...current, d.v]
                                setReportGenerationConfig({...reportGenerationConfig, days: next})
                              }}
                              className={`w-6 h-6 rounded text-[10px] font-bold transition-colors ${reportGenerationConfig.days.includes(d.v) ? 'bg-orange-500 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}
                            >
                              {d.l}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="w-px h-8 bg-slate-800 mx-2"></div>

                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Horário:</span>
                        <input 
                          type="time" 
                          value={reportGenerationConfig.time}
                          onChange={(e) => setReportGenerationConfig({...reportGenerationConfig, time: e.target.value})}
                          className="bg-slate-900 border border-slate-700 text-white text-xs rounded px-2 py-1 outline-none focus:border-orange-500 h-8 [color-scheme:dark]"
                        />
                      </div>

                      <div className="w-px h-8 bg-slate-800 mx-2"></div>

                      <div className="flex items-center gap-2">
                         <span className={`text-xs font-bold uppercase tracking-wider ${reportGenerationConfig.active ? 'text-orange-400' : 'text-slate-500'}`}>
                           {reportGenerationConfig.active ? 'Ativo' : 'Pausado'}
                         </span>
                         <ToggleSwitch 
                           checked={reportGenerationConfig.active} 
                           onChange={() => setReportGenerationConfig({...reportGenerationConfig, active: !reportGenerationConfig.active})} 
                         />
                      </div>
                  </div>
                </div>
                <button 
                  onClick={() => setIsConfirmingReport(true)}
                  disabled={isGenerating}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-bold transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGenerating ? <RefreshCcw size={16} className="animate-spin" /> : <RefreshCcw size={16} />} 
                  Gerar Agora
                </button>
             </div>

             <DailyReportsList key={refreshKeyDaily} role={role} partner={partner} userCompanyId={userCompanyId} />

             <ConfirmModal 
               isOpen={isConfirmingReport}
               title="Gerar Relatórios"
               message="Deseja gerar os relatórios agora? Isso pode levar alguns segundos."
               onConfirm={async () => {
                  setIsConfirmingReport(false)
                  setIsGenerating(true)
                  try {
                    const { data, error } = await supabase.functions.invoke('generate-daily-report', {
                        body: { force: true }
                    })
                    if(error) throw error
                    setReportResult(`Sucesso! ${data?.generated || 0} relatórios gerados.`)
                    setRefreshKeyDaily(prev => prev + 1)
                  } catch(e) {
                    console.error(e)
                    setReportResult('Erro ao gerar relatórios: ' + e.message)
                  } finally {
                    setIsGenerating(false)
                  }
               }}
               onCancel={() => setIsConfirmingReport(false)}
             />

             <InfoModal 
               isOpen={!!reportResult}
               title="Resultado da Geração"
               content={reportResult}
               onClose={() => setReportResult(null)}
             />

             <ConfirmModal 
               isOpen={isConfirmingSend}
               title="Enviar Relatórios"
               message="Deseja disparar o envio dos e-mails agora para os perfis configurados?"
               onConfirm={async () => {
                  setIsConfirmingSend(false)
                  setIsSending(true)
                  try {
                    const { data, error } = await supabase.functions.invoke('daily-report-worker', {
                      body: { force: true }
                    })
                    if(error) throw error
                    
                    if (data && data.success === false) {
                        setSendResult(`Atenção: ${data.message}`)
                     } else {
                        // Construir mensagem detalhada de falhas se houver
                        let msg = data?.message || `Sucesso! Relatórios enviados.`;
                        
                        if (data?.results) {
                           const failed = data.results.filter(r => r.status === 'failed');
                           if (failed.length > 0) {
                              const details = failed.map(f => `${f.email}: ${JSON.stringify(f.error)}`).join('\n');
                              msg += `\n\nErros:\n${details}`;
                           }
                           
                           const skipped = data.results.filter(r => r.status === 'skipped');
                           if (skipped.length > 0) {
                              const details = skipped.map(s => `${s.email}: ${s.reason}`).join('\n');
                              msg += `\n\nPulados:\n${details}`;
                           }
                        }
                        
                        setSendResult(msg)
                     }
                  } catch(e) {
                    console.error(e)
                    setSendResult('Erro ao enviar relatórios: ' + e.message)
                  } finally {
                    setIsSending(false)
                  }
               }}
               onCancel={() => setIsConfirmingSend(false)}
             />

             <InfoModal 
               isOpen={!!sendResult}
               title="Resultado do Envio"
               content={sendResult}
               onClose={() => setSendResult(null)}
             />
          </div>
        </div>
      )}

      {activeTab === 'templates' && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 animate-fade-in">
          <div>
            <div className="flex items-center justify-between mb-4 border-b border-slate-800 pb-3">
              <h4 className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]"></span>
                Canal: E-mail
              </h4>
              <button
                onClick={() => { 
                    setTransactionalChannel('E-mail'); 
                    setTransactionalForm({
                        ...initialTransactionalFormState,
                        code: 'TPL-' + Math.random().toString(36).substring(2, 8).toUpperCase()
                    }); 
                    setShowTransactionalModal(true) 
                }}
                className="px-3 py-1.5 rounded-lg font-bold text-white text-xs md:text-sm"
                style={{ backgroundColor: partnerColor }}
              >
                + Template
              </button>
            </div>
            <ColumnFilters channel="E-mail" />
            <div className="flex flex-col gap-6">
              {filteredEmailTemplates.map(t => (
                <div key={t.id} className="bg-slate-900 border border-slate-800 p-6 rounded-xl hover:border-slate-700 transition group relative shadow-lg">
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition flex gap-2">
                    <button
                      onClick={() => {
                        setPreviewTemplate(t)
                        setShowPreviewModal(true)
                      }}
                      className="p-2 bg-slate-950 border border-slate-800 rounded-lg hover:bg-slate-800 text-slate-300 transition"
                      title="Visualizar template"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() => {
                        setEditingTemplate(t)
                        setTransactionalChannel(t.channel || 'E-mail')
                        const meta = t.meta || {}
                        const vars = meta.variaveis || []
                        setTransactionalForm({
                          tipo: meta.tipo || 'Comunicado',
                          header: null,
                          headerUrl: meta.headerUrl || '',
                          tornarHeaderPadrao: false,
                          usarHtml: t.is_html !== undefined ? t.is_html : !!meta.usarHtml,
                          titulo: t.subject || '',
                          textoHtml: meta.textoHtml || t.content || '',
                          botaoHabilitado: !!(meta.botao && (meta.botao.titulo || meta.botao.link)),
                          botaoTitulo: (meta.botao && meta.botao.titulo) || '',
                          botaoLink: (meta.botao && meta.botao.link) || '',
                          observacaoHabilitada: !!meta.observacao,
                          observacao: meta.observacao || '',
                          rodape: null,
                          rodapeUrl: meta.rodapeUrl || '',
                          tornarRodapePadrao: false,
                          variaveisHabilitadas: Array.isArray(vars) && vars.length > 0,
                          variaveis: buildVariaveisFromArray(vars),
                          atribuirParceiros: Array.isArray(meta.parceirosSAS) && meta.parceirosSAS.length > 0,
                          parceirosSelecionados: meta.parceirosSAS || [],
                          anexos: Array.isArray(meta.anexos) ? meta.anexos : [],
                          anexosAberto: true,
                          active: t.is_active !== false,
                          code: t.code || 'TPL-' + Math.random().toString(36).substring(2, 8).toUpperCase(),
                          isDefault: !!t.meta?.is_default,
                          usoConvitePadrao: !!meta.usos?.convite_padrao,
                          usoConviteLink: !!meta.usos?.convite_link,
                        })
                        setShowTransactionalModal(true)
                      }}
                      className="p-2 bg-slate-950 border border-slate-800 rounded-lg hover:bg-slate-800 text-slate-300 transition"
                    ><Edit2 size={16} /></button>

                    <button onClick={() => handleDeleteTemplate(t.id)} className="p-2 bg-slate-950 border border-slate-800 rounded-lg hover:bg-rose-500/10 hover:border-rose-500/50 hover:text-rose-500 text-slate-300 transition"><Trash2 size={16} /></button>
                  </div>
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg bg-blue-500/10 text-blue-400">
                      <Mail size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-lg">
                        <span className="text-blue-400">{t.meta?.tipo || 'Template'}</span>
                        <span className="mx-2 text-slate-700">-</span>
                        {t.subject || t.name}
                      </h3>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <span className="text-xs text-slate-500 uppercase font-bold tracking-wider">E-mail</span>
                        {t.meta?.tipo && (
                          <span className="text-[10px] bg-blue-500/10 text-blue-400 px-2 py-0.5 rounded uppercase font-bold tracking-wider border border-blue-500/20">
                            {t.meta.tipo}
                          </span>
                        )}
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${t.is_active !== false ? 'bg-teal-500/10 text-teal-400' : 'bg-rose-500/10 text-rose-400'}`}>
                          {t.is_active !== false ? 'ATIVO' : 'INATIVO'}
                        </span>
                      </div>
                      <TemplateUsageBadges meta={t.meta} />
                    </div>
                  </div>
                  {t.channel === 'E-mail' && (
                    <div className="flex items-center gap-2 mb-3">
                      <p className="text-slate-400 text-sm"><span className="text-slate-500 font-bold uppercase text-xs mr-2">Assunto:</span> {t.subject}</p>
                      {t.meta?.anexos?.length > 0 && (
                        <div className="flex items-center gap-1 px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-400" title="Possui anexos">
                          <Paperclip size={12} />
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-x-4 gap-y-2 mt-4 pt-4 border-t border-slate-800/50">
                    <div className="flex flex-col">
                      <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Criação</span>
                      <span className="text-xs text-slate-400">{t.created_at ? new Date(t.created_at).toLocaleString('pt-BR') : '-'}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Atualização</span>
                      <span className="text-xs text-slate-400">{t.updated_at ? new Date(t.updated_at).toLocaleString('pt-BR') : '-'}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Criado por</span>
                      <span className="text-xs text-teal-400 font-medium">{adminProfiles[t.created_by] || 'Sistema'}</span>
                    </div>
                  </div>
                </div>
              ))}

              {filteredEmailTemplates.length === 0 && (
                <div className="text-slate-500 text-sm">Nenhum template de e-mail.</div>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-4 border-b border-slate-800 pb-3">
              <h4 className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.5)]"></span>
                Canal: Whatsapp
              </h4>
              <button
                onClick={() => { 
                    setTransactionalChannel('WhatsApp'); 
                    setTransactionalForm({
                        ...initialTransactionalFormState,
                        code: 'TPL-' + Math.random().toString(36).substring(2, 8).toUpperCase()
                    }); 
                    setShowTransactionalModal(true) 
                }}
                className="px-3 py-1.5 rounded-lg font-bold text-white text-xs md:text-sm"
                style={{ backgroundColor: partnerColor }}
              >
                + Template
              </button>
            </div>
            <ColumnFilters channel="WhatsApp" />
            <div className="flex flex-col gap-6">
              {filteredWhatsappTemplates.map(t => (
                <div key={t.id} className="bg-slate-900 border border-slate-800 p-6 rounded-xl hover:border-slate-700 transition group relative shadow-lg">
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition flex gap-2">
                    <button
                      onClick={() => {
                        setPreviewTemplate(t)
                        setShowPreviewModal(true)
                      }}
                      className="p-2 bg-slate-950 border border-slate-800 rounded-lg hover:bg-slate-800 text-slate-300 transition"
                      title="Visualizar mensagem"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() => {
                        setEditingTemplate(t)
                        setTransactionalChannel(t.channel || 'WhatsApp')
                        const meta = t.meta || {}
                        const vars = meta.variaveis || []
                        setTransactionalForm({
                          tipo: meta.tipo || 'Comunicado',
                          header: null,
                          headerUrl: meta.headerUrl || '',
                          tornarHeaderPadrao: false,
                          titulo: t.subject || '',
                          textoHtml: meta.textoHtml || t.content || '',
                          botaoHabilitado: !!(meta.botao && (meta.botao.titulo || meta.botao.link)),
                          botaoTitulo: (meta.botao && meta.botao.titulo) || '',
                          botaoLink: (meta.botao && meta.botao.link) || '',
                          observacaoHabilitada: !!meta.observacao,
                          observacao: meta.observacao || '',
                          rodape: null,
                          rodapeUrl: meta.rodapeUrl || '',
                          tornarRodapePadrao: false,
                          variaveisHabilitadas: Array.isArray(vars) && vars.length > 0,
                          variaveis: buildVariaveisFromArray(vars),
                          atribuirParceiros: Array.isArray(meta.parceirosSAS) && meta.parceirosSAS.length > 0,
                          parceirosSelecionados: meta.parceirosSAS || [],
                          anexos: Array.isArray(meta.anexos) ? meta.anexos : [],
                          anexosAberto: true,
                          active: t.is_active !== false,
                          code: t.code || 'TPL-' + Math.random().toString(36).substring(2, 8).toUpperCase(),
                          isDefault: !!t.meta?.is_default,
                          usoConvitePadrao: !!meta.usos?.convite_padrao,
                          usoConviteLink: !!meta.usos?.convite_link,
                          metaTemplateName: (meta.metaTemplate && meta.metaTemplate.name) || '',
                          metaTemplateLanguage: (meta.metaTemplate && meta.metaTemplate.language) || 'pt_BR',
                          metaTemplateParams: Array.isArray(meta.metaTemplate?.params) ? meta.metaTemplate.params : [],
                          metaTemplateButtonEnabled: !!(meta.metaTemplate?.button && (meta.metaTemplate.button.prefix || meta.metaTemplate.button.paramKey)),
                          metaTemplateButtonPrefix: (meta.metaTemplate?.button && meta.metaTemplate.button.prefix) || '',
                          metaTemplateButtonVar: (meta.metaTemplate?.button && meta.metaTemplate.button.paramKey) || '',
                        })
                        setShowTransactionalModal(true)
                      }}
                      className="p-2 bg-slate-950 border border-slate-800 rounded-lg hover:bg-slate-800 text-slate-300 transition"
                    ><Edit2 size={16} /></button>
                    <button onClick={() => handleDeleteTemplate(t.id)} className="p-2 bg-slate-950 border border-slate-800 rounded-lg hover:bg-rose-500/10 hover:border-rose-500/50 hover:text-rose-500 text-slate-300 transition"><Trash2 size={16} /></button>
                  </div>
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg bg-green-500/10 text-green-400">
                      <MessageCircle size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-lg">
                        <span className="text-green-400">{t.meta?.tipo || 'Template'}</span>
                        <span className="mx-2 text-slate-700">-</span>
                        {t.subject || t.name}
                      </h3>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <span className="text-xs text-slate-500 uppercase font-bold tracking-wider">WhatsApp</span>
                        {t.meta?.tipo && (
                          <span className="text-[10px] bg-green-500/10 text-green-400 px-2 py-0.5 rounded uppercase font-bold tracking-wider border border-green-500/20">
                            {t.meta.tipo}
                          </span>
                        )}
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${t.is_active !== false ? 'bg-teal-500/10 text-teal-400' : 'bg-rose-500/10 text-rose-400'}`}>
                          {t.is_active !== false ? 'ATIVO' : 'INATIVO'}
                        </span>
                      </div>
                      <TemplateUsageBadges meta={t.meta} />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-2 mt-4 pt-4 border-t border-slate-800/50">
                    <div className="flex flex-col">
                      <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Criação</span>
                      <span className="text-xs text-slate-400">{t.created_at ? new Date(t.created_at).toLocaleString('pt-BR') : '-'}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Atualização</span>
                      <span className="text-xs text-slate-400">{t.updated_at ? new Date(t.updated_at).toLocaleString('pt-BR') : '-'}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Criado por</span>
                      <span className="text-xs text-teal-400 font-medium">{adminProfiles[t.created_by] || 'Sistema'}</span>
                    </div>
                  </div>
                </div>
              ))}
              {filteredWhatsappTemplates.length === 0 && (
                <div className="text-slate-500 text-sm">Nenhum template de WhatsApp.</div>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-4 border-b border-slate-800 pb-3">
              <h4 className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.5)]"></span>
                Canal: Relatórios & Anexos
              </h4>
              <button
                onClick={() => { 
                    setTransactionalChannel('Relatórios & Anexos'); 
                    setTransactionalForm({
                        ...initialTransactionalFormState,
                        tipo: 'Relatório',
                        code: 'TPL-' + Math.random().toString(36).substring(2, 8).toUpperCase()
                    }); 
                    setShowTransactionalModal(true) 
                }}
                className="px-3 py-1.5 rounded-lg font-bold text-white text-xs md:text-sm"
                style={{ backgroundColor: partnerColor }}
              >
                + Template
              </button>
            </div>
            <ColumnFilters channel="Relatórios & Anexos" />
            <div className="flex flex-col gap-6">
              {filteredReportsAndAttachmentsTemplates.map(t => (
                <div key={t.id} className="bg-slate-900 border border-slate-800 p-6 rounded-xl hover:border-slate-700 transition group relative shadow-lg">
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition flex gap-2">
                    <button
                      onClick={() => {
                        setPreviewTemplate(t)
                        setShowPreviewModal(true)
                      }}
                      className="p-2 bg-slate-950 border border-slate-800 rounded-lg hover:bg-slate-800 text-slate-300 transition"
                      title="Visualizar template"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() => {
                        setEditingTemplate(t)
                        setTransactionalChannel(t.channel || 'Relatórios & Anexos')
                        const meta = t.meta || {}
                        const vars = meta.variaveis || []
                        setTransactionalForm({
                          tipo: meta.tipo || 'Comunicado',
                          header: null,
                          headerUrl: meta.headerUrl || '',
                          tornarHeaderPadrao: false,
                          usarHtml: t.is_html !== undefined ? t.is_html : !!meta.usarHtml,
                          titulo: t.subject || '',
                          textoHtml: meta.textoHtml || t.content || '',
                          botaoHabilitado: !!(meta.botao && (meta.botao.titulo || meta.botao.link)),
                          botaoTitulo: (meta.botao && meta.botao.titulo) || '',
                          botaoLink: (meta.botao && meta.botao.link) || '',
                          observacaoHabilitada: !!meta.observacao,
                          observacao: meta.observacao || '',
                          rodape: null,
                          rodapeUrl: meta.rodapeUrl || '',
                          tornarRodapePadrao: false,
                          variaveisHabilitadas: Array.isArray(vars) && vars.length > 0,
                          variaveis: buildVariaveisFromArray(vars),
                          atribuirParceiros: Array.isArray(meta.parceirosSAS) && meta.parceirosSAS.length > 0,
                          parceirosSelecionados: meta.parceirosSAS || [],
                          anexos: Array.isArray(meta.anexos) ? meta.anexos : [],
                          anexosAberto: true,
                          active: t.is_active !== false,
                          code: t.code || 'TPL-' + Math.random().toString(36).substring(2, 8).toUpperCase(),
                          isDefault: !!t.meta?.is_default,
                          usoConvitePadrao: !!meta.usos?.convite_padrao,
                          usoConviteLink: !!meta.usos?.convite_link,
                        })
                        setShowTransactionalModal(true)
                      }}
                      className="p-2 bg-slate-950 border border-slate-800 rounded-lg hover:bg-slate-800 text-slate-300 transition"
                    ><Edit2 size={16} /></button>
                    <button onClick={() => handleDeleteTemplate(t.id)} className="p-2 bg-slate-950 border border-slate-800 rounded-lg hover:bg-rose-500/10 hover:border-rose-500/50 hover:text-rose-500 text-slate-300 transition"><Trash2 size={16} /></button>
                  </div>
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg bg-amber-500/10 text-amber-400">
                      <Paperclip size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-lg">
                        <span className="text-amber-400">{t.meta?.tipo || 'Template'}</span>
                        <span className="mx-2 text-slate-700">-</span>
                        {t.subject || t.name}
                      </h3>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <span className="text-xs text-slate-500 uppercase font-bold tracking-wider">Relatórios</span>
                        {t.meta?.tipo && (
                          <span className="text-[10px] bg-amber-500/10 text-amber-400 px-2 py-0.5 rounded uppercase font-bold tracking-wider border border-amber-500/20">
                            {t.meta.tipo}
                          </span>
                        )}
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${t.is_active !== false ? 'bg-teal-500/10 text-teal-400' : 'bg-rose-500/10 text-rose-400'}`}>
                          {t.is_active !== false ? 'PUBLICADO' : 'OCULTO'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <p className="text-slate-400 text-sm"><span className="text-slate-500 font-bold uppercase text-xs mr-2">Assunto:</span> {t.subject}</p>
                    {t.meta?.anexos?.length > 0 && (
                      <div className="flex items-center gap-1 px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-400" title="Possui anexos">
                        <Paperclip size={12} />
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-2 mt-4 pt-4 border-t border-slate-800/50">
                    <div className="flex flex-col">
                      <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Criação</span>
                      <span className="text-xs text-slate-400">{t.created_at ? new Date(t.created_at).toLocaleString('pt-BR') : '-'}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Atualização</span>
                      <span className="text-xs text-slate-400">{t.updated_at ? new Date(t.updated_at).toLocaleString('pt-BR') : '-'}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Criado por</span>
                      <span className="text-xs text-teal-400 font-medium">{adminProfiles[t.created_by] || 'Sistema'}</span>
                    </div>
                  </div>
                </div>
              ))}
              {filteredReportsAndAttachmentsTemplates.length === 0 && (
                <div className="text-slate-500 text-sm">Nenhum template de Relatórios & Anexos.</div>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-4 border-b border-slate-800 pb-3">
              <h4 className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-cyan-500 shadow-[0_0_10px_rgba(6,182,212,0.5)]"></span>
                Canal: SMS
              </h4>
              <button
                onClick={() => {
                  setTransactionalChannel('SMS');
                  setTransactionalForm({
                    ...initialTransactionalFormState,
                    code: 'TPL-' + Math.random().toString(36).substring(2, 8).toUpperCase()
                  });
                  setShowTransactionalModal(true)
                }}
                className="px-3 py-1.5 rounded-lg font-bold text-white text-xs md:text-sm"
                style={{ backgroundColor: partnerColor }}
              >
                + Template
              </button>
            </div>
            <ColumnFilters channel="SMS" />
            <div className="flex flex-col gap-6">
              {filteredSmsTemplates.map(t => (
                <div key={t.id} className="bg-slate-900 border border-slate-800 p-6 rounded-xl hover:border-slate-700 transition group relative shadow-lg">
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition flex gap-2">
                    <button
                      onClick={() => {
                        setPreviewTemplate(t)
                        setShowPreviewModal(true)
                      }}
                      className="p-2 bg-slate-950 border border-slate-800 rounded-lg hover:bg-slate-800 text-slate-300 transition"
                      title="Visualizar mensagem"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() => {
                        setEditingTemplate(t)
                        setTransactionalChannel(t.channel || 'SMS')
                        const meta = t.meta || {}
                        const vars = meta.variaveis || []
                        setTransactionalForm({
                          tipo: meta.tipo || 'Comunicado',
                          header: null,
                          headerUrl: meta.headerUrl || '',
                          tornarHeaderPadrao: false,
                          titulo: t.subject || '',
                          textoHtml: meta.textoHtml || t.content || '',
                          botaoHabilitado: !!(meta.botao && (meta.botao.titulo || meta.botao.link)),
                          botaoTitulo: (meta.botao && meta.botao.titulo) || '',
                          botaoLink: (meta.botao && meta.botao.link) || '',
                          observacaoHabilitada: !!meta.observacao,
                          observacao: meta.observacao || '',
                          rodape: null,
                          rodapeUrl: meta.rodapeUrl || '',
                          tornarRodapePadrao: false,
                          variaveisHabilitadas: Array.isArray(vars) && vars.length > 0,
                          variaveis: buildVariaveisFromArray(vars),
                          atribuirParceiros: Array.isArray(meta.parceirosSAS) && meta.parceirosSAS.length > 0,
                          parceirosSelecionados: meta.parceirosSAS || [],
                          anexos: Array.isArray(meta.anexos) ? meta.anexos : [],
                          anexosAberto: true,
                          active: t.is_active !== false,
                          code: t.code || 'TPL-' + Math.random().toString(36).substring(2, 8).toUpperCase(),
                          isDefault: !!t.meta?.is_default,
                          usoConvitePadrao: !!meta.usos?.convite_padrao,
                          usoConviteLink: !!meta.usos?.convite_link,
                        })
                        setShowTransactionalModal(true)
                      }}
                      className="p-2 bg-slate-950 border border-slate-800 rounded-lg hover:bg-slate-800 text-slate-300 transition"
                    ><Edit2 size={16} /></button>
                    <button onClick={() => handleDeleteTemplate(t.id)} className="p-2 bg-slate-950 border border-slate-800 rounded-lg hover:bg-rose-500/10 hover:border-rose-500/50 hover:text-rose-500 text-slate-300 transition"><Trash2 size={16} /></button>
                  </div>
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg bg-cyan-500/10 text-cyan-400">
                      <MessageCircle size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-lg">
                        <span className="text-cyan-400">{t.meta?.tipo || 'Template'}</span>
                        <span className="mx-2 text-slate-700">-</span>
                        {t.subject || t.name}
                      </h3>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <span className="text-xs text-slate-500 uppercase font-bold tracking-wider">SMS</span>
                        {t.meta?.tipo && (
                          <span className="text-[10px] bg-cyan-500/10 text-cyan-400 px-2 py-0.5 rounded uppercase font-bold tracking-wider border border-cyan-500/20">
                            {t.meta.tipo}
                          </span>
                        )}
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${t.is_active !== false ? 'bg-teal-500/10 text-teal-400' : 'bg-rose-500/10 text-rose-400'}`}>
                          {t.is_active !== false ? 'ATIVO' : 'INATIVO'}
                        </span>
                      </div>
                      <TemplateUsageBadges meta={t.meta} />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-2 mt-4 pt-4 border-t border-slate-800/50">
                    <div className="flex flex-col">
                      <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Criação</span>
                      <span className="text-xs text-slate-400">{t.created_at ? new Date(t.created_at).toLocaleString('pt-BR') : '-'}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Atualização</span>
                      <span className="text-xs text-slate-400">{t.updated_at ? new Date(t.updated_at).toLocaleString('pt-BR') : '-'}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Criado por</span>
                      <span className="text-xs text-teal-400 font-medium">{adminProfiles[t.created_by] || 'Sistema'}</span>
                    </div>
                  </div>
                </div>
              ))}
              {filteredSmsTemplates.length === 0 && (
                <div className="text-slate-500 text-sm">Nenhum template de SMS.</div>
              )}
            </div>
          </div>

          {filteredEmailTemplates.length === 0 && filteredWhatsappTemplates.length === 0 && filteredSmsTemplates.length === 0 && filteredReportsAndAttachmentsTemplates.length === 0 && (
            <div className="md:col-span-2 xl:col-span-4 text-center py-20 text-slate-500">
              <FileText size={48} className="mx-auto mb-4 opacity-20" />
              <p>Nenhum template criado ainda.</p>
            </div>
          )}
        </div>
      )}

      {showTransactionalModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-6xl shadow-2xl max-h-[85vh] flex flex-col">
            <div className="flex justify-between items-center p-6 border-b border-slate-800">
              <h3 className="text-xl font-bold text-white">
                {editingTemplate 
                  ? `Editar Template${transactionalChannel === 'Relatórios & Anexos' ? ' - Relatórios & Anexos' : ' Transacional'}` 
                  : `Novo Template${transactionalChannel === 'Relatórios & Anexos' ? ' - Relatórios & Anexos' : ' Transacional'}`}
              </h3>
              <button onClick={() => setShowTransactionalModal(false)} className="text-slate-500 hover:text-white"><X size={20} /></button>
            </div>
            <div className="px-6 py-4 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                <div className="md:col-span-12">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <ToggleSwitch checked={transactionalForm.atribuirParceiros} onChange={() => setTransactionalForm({ ...transactionalForm, atribuirParceiros: !transactionalForm.atribuirParceiros })} ariaLabel="Atribuir Parceiro SAS" />
                      <span className={`${transactionalForm.atribuirParceiros ? 'text-teal-400' : 'text-slate-300'} text-sm font-bold`}>Atribuir Parceiro SAS</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <ToggleSwitch checked={transactionalForm.isDefault} onChange={() => setTransactionalForm({ ...transactionalForm, isDefault: !transactionalForm.isDefault })} ariaLabel={transactionalChannel === 'Relatórios & Anexos' ? 'Destaque' : 'Padrão'} />
                            <span className={`${transactionalForm.isDefault ? 'text-amber-400' : 'text-slate-500'} text-sm font-bold`}>
                              {transactionalChannel === 'Relatórios & Anexos' 
                                ? (transactionalForm.isDefault ? 'Destaque' : 'Normal') 
                                : (transactionalForm.isDefault ? 'Padrão' : 'Não Padrão')}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <ToggleSwitch checked={transactionalForm.active} onChange={() => setTransactionalForm({ ...transactionalForm, active: !transactionalForm.active })} ariaLabel={transactionalChannel === 'Relatórios & Anexos' ? 'Publicado' : 'Ativo/Inativo'} />
                            <span className={`${transactionalForm.active ? 'text-teal-400' : 'text-slate-500'} text-sm font-bold`}>
                              {transactionalChannel === 'Relatórios & Anexos' 
                                ? (transactionalForm.active ? 'Publicado' : 'Oculto') 
                                : (transactionalForm.active ? 'Ativo' : 'Inativo')}
                            </span>
                        </div>
                        {transactionalForm.code && (
                            <div className="flex items-center gap-1 bg-slate-800 px-3 py-1 rounded border border-slate-700">
                                <span className="text-xs text-slate-500 uppercase font-bold">Código:</span>
                                <span className="text-sm font-mono text-white">{transactionalForm.code}</span>
                            </div>
                        )}
                    </div>
                  </div>
                  {transactionalForm.atribuirParceiros && (
                    <div className="space-y-2">
                      {transactionalForm.parceirosSelecionados.length > 0 && (
                        <div className="inline-flex items-center gap-2 bg-slate-800 text-slate-200 text-xs rounded-full px-3 py-1">
                          {transactionalForm.parceirosSelecionados.length} parceiro(s) selecionado(s)
                        </div>
                      )}
                      <div className="relative inline-block">
                        <button onClick={() => setShowPartnerDropdown(v => !v)} className="px-4 py-2 rounded-lg border border-slate-700 bg-slate-950 text-slate-200 hover:border-white/40 transition min-w-[260px] text-left">
                          {transactionalForm.parceirosSelecionados.length > 0 ? 'Gerenciar seleção...' : 'Selecionar parceiros...'}
                        </button>
                        {showPartnerDropdown && (
                          <div className="absolute z-10 mt-2 w-72 max-h-60 overflow-auto bg-slate-900 border border-slate-700 rounded-lg shadow-xl p-2" onMouseLeave={() => setShowPartnerDropdown(false)}>
                            {partners.map(p => {
                              const selected = transactionalForm.parceirosSelecionados.includes(p.id)
                              return (
                                <div key={p.id} className={`flex items-center justify-between p-2 rounded cursor-pointer ${selected ? 'bg-teal-500/10' : 'hover:bg-slate-800'}`} onClick={() => {
                                  const exists = transactionalForm.parceirosSelecionados.includes(p.id)
                                  const next = exists ? transactionalForm.parceirosSelecionados.filter(id => id !== p.id) : [...transactionalForm.parceirosSelecionados, p.id]
                                  setTransactionalForm({ ...transactionalForm, parceirosSelecionados: next })
                                }}>
                                  <span className="text-sm text-slate-200">{p.name}</span>
                                  <ToggleSwitch checked={selected} onChange={(e) => {
                                    e.stopPropagation()
                                    const exists = transactionalForm.parceirosSelecionados.includes(p.id)
                                    const next = exists ? transactionalForm.parceirosSelecionados.filter(id => id !== p.id) : [...transactionalForm.parceirosSelecionados, p.id]
                                    setTransactionalForm({ ...transactionalForm, parceirosSelecionados: next })
                                  }} ariaLabel={`Selecionar ${p.name}`} />
                                </div>
                              )
                            })}
                            {partners.length === 0 && (
                              <div className="text-xs text-slate-500 p-2">Nenhum parceiro cadastrado.</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1.5 md:col-span-4">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Tipo</label>
                  <select value={transactionalForm.tipo} onChange={e => {
                    const novoTipo = e.target.value
                    setTransactionalForm(prev => ({
                      ...prev,
                      tipo: novoTipo,
                      // As opções de uso só existem para o tipo 'Convite'.
                      usoConvitePadrao: novoTipo === 'Convite' ? prev.usoConvitePadrao : false,
                      usoConviteLink: novoTipo === 'Convite' ? prev.usoConviteLink : false,
                    }))
                  }} className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition">
                    {[...new Set([...(transactionalChannel === 'Relatórios & Anexos' ? reportTypes : transactionalTypes), ...(transactionalForm.tipo ? [transactionalForm.tipo] : [])])].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>

                {['E-mail', 'WhatsApp', 'SMS'].includes(transactionalChannel) && transactionalForm.tipo === 'Convite' && (
                  <div className="md:col-span-12 rounded-xl border border-slate-700/60 bg-slate-950/60 p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-slate-200">Opções de uso</span>
                      <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Onde este template será usado</span>
                    </div>
                    <p className="text-[11px] text-slate-400 leading-relaxed">
                      Marque os fluxos de convite em que este template deve ser disparado. Com as duas opções marcadas, o mesmo template atende ao Convite Padrão e ao Convite Link+código, respeitando as premissas de disparo de cada caso.
                    </p>
                    <div className="flex flex-wrap items-center gap-6">
                      <div className="flex items-center gap-2">
                        <ToggleSwitch checked={transactionalForm.usoConvitePadrao} onChange={() => setTransactionalForm({ ...transactionalForm, usoConvitePadrao: !transactionalForm.usoConvitePadrao })} ariaLabel="Usar em Convite Padrão" />
                        <span className={`${transactionalForm.usoConvitePadrao ? 'text-teal-400' : 'text-slate-300'} text-sm font-bold`}>Convite Padrão</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <ToggleSwitch checked={transactionalForm.usoConviteLink} onChange={() => setTransactionalForm({ ...transactionalForm, usoConviteLink: !transactionalForm.usoConviteLink })} ariaLabel="Usar em Convite Link+código" />
                        <span className={`${transactionalForm.usoConviteLink ? 'text-teal-400' : 'text-slate-300'} text-sm font-bold`}>Convite Link+código</span>
                      </div>
                    </div>
                  </div>
                )}

                {transactionalChannel === 'WhatsApp' && (
                  <div className="md:col-span-12 rounded-xl border border-green-500/30 bg-green-500/5 p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-green-400">Modelo de template da Meta</span>
                      <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">WhatsApp Cloud API</span>
                    </div>
                    <p className="text-[11px] text-slate-400 leading-relaxed">
                      O envio utiliza exclusivamente o template aprovado no WhatsApp Manager. Informe o nome exato e o idioma; a ordem abaixo corresponde a {'{{1}}'}, {'{{2}}'}, ... no corpo do template.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Nome do template (Meta)</label>
                        <input
                          value={transactionalForm.metaTemplateName || ''}
                          onChange={e => setTransactionalForm({ ...transactionalForm, metaTemplateName: e.target.value })}
                          placeholder="ex.: convite_matricula_v1"
                          className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition font-mono text-sm"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Idioma (código)</label>
                        <input
                          value={transactionalForm.metaTemplateLanguage || ''}
                          onChange={e => setTransactionalForm({ ...transactionalForm, metaTemplateLanguage: e.target.value })}
                          placeholder="pt_BR"
                          className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition font-mono text-sm"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Ordem dos parâmetros</label>
                      {(() => {
                        const keys = getMetaTemplateParamKeys()
                        if (keys.length === 0) {
                          return <span className="text-xs text-slate-500">Habilite variáveis do sistema abaixo para mapeá-las nas posições do template.</span>
                        }
                        return (
                          <div className="flex flex-col gap-2">
                            {keys.map((key, idx) => {
                              const v = SYSTEM_VARIABLES.find(x => x.k === key)
                              return (
                                <div key={key} className="flex items-center gap-3 bg-slate-950 border border-slate-800 rounded-lg px-3 py-2">
                                  <span className="font-mono text-xs font-bold text-green-400 w-10">{`{{${idx + 1}}}`}</span>
                                  <span className="flex-1 min-w-0 text-sm text-slate-200 truncate">{v?.label || key}</span>
                                  <span className="font-mono text-[10px] text-slate-500 hidden sm:inline">{v?.token || ''}</span>
                                  <div className="flex gap-1">
                                    <button type="button" disabled={idx === 0} onClick={() => moveMetaTemplateParam(idx, -1)} className={`p-1 rounded border ${idx === 0 ? 'border-slate-800 text-slate-600 cursor-not-allowed' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}`} title="Mover para cima">
                                      <ChevronUp size={14} />
                                    </button>
                                    <button type="button" disabled={idx === keys.length - 1} onClick={() => moveMetaTemplateParam(idx, 1)} className={`p-1 rounded border ${idx === keys.length - 1 ? 'border-slate-800 text-slate-600 cursor-not-allowed' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}`} title="Mover para baixo">
                                      <ChevronDown size={14} />
                                    </button>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        )
                      })()}
                    </div>
                    <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3 space-y-3">
                      <div className="flex items-center gap-2">
                        <ToggleSwitch
                          checked={!!transactionalForm.metaTemplateButtonEnabled}
                          onChange={() => setTransactionalForm({ ...transactionalForm, metaTemplateButtonEnabled: !transactionalForm.metaTemplateButtonEnabled })}
                          ariaLabel="Template possui botão de URL"
                        />
                        <span className={`${transactionalForm.metaTemplateButtonEnabled ? 'text-teal-400' : 'text-slate-300'} text-sm font-bold`}>Botão de ação (URL)</span>
                      </div>
                      <p className="text-[11px] text-slate-400 leading-relaxed">
                        Ative quando o template aprovado possuir botão de URL com variável. Informe o prefixo fixo do link e a variável que preenche a parte dinâmica — sem isso a Meta retorna o erro #131008.
                      </p>
                      {transactionalForm.metaTemplateButtonEnabled && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Prefixo do botão (URL)</label>
                            <input
                              value={transactionalForm.metaTemplateButtonPrefix || ''}
                              onChange={e => setTransactionalForm({ ...transactionalForm, metaTemplateButtonPrefix: e.target.value })}
                              placeholder="https://acesso.sofera.com.br/courses/"
                              className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition font-mono text-sm"
                            />
                          </div>
                          <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Variável do botão</label>
                            <select
                              value={transactionalForm.metaTemplateButtonVar || ''}
                              onChange={e => setTransactionalForm({ ...transactionalForm, metaTemplateButtonVar: e.target.value })}
                              className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition text-sm"
                            >
                              <option value="">Selecione a variável...</option>
                              {SYSTEM_VARIABLES.map(v => (
                                <option key={v.k} value={v.k}>{v.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="md:col-span-2 flex flex-col gap-1">
                            <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Composição do link</span>
                            <span className="font-mono text-xs text-teal-300 break-all">
                              {`${transactionalForm.metaTemplateButtonPrefix || ''}${(SYSTEM_VARIABLES.find(x => x.k === transactionalForm.metaTemplateButtonVar)?.token) || ''}` || '—'}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {transactionalChannel === 'E-mail' && (
                  <div className="flex flex-col gap-1.5 md:col-span-12">
                    <div className="flex items-center gap-2">
                      <ToggleSwitch checked={transactionalForm.usarHtml} onChange={() => setTransactionalForm({ ...transactionalForm, usarHtml: !transactionalForm.usarHtml })} ariaLabel="Usar HTML" />
                      <span className={`${transactionalForm.usarHtml ? 'text-teal-400' : 'text-slate-300'} text-sm font-bold`}>Usar HTML</span>
                    </div>
                  </div>
                )}
                <div className="flex flex-col gap-1.5 md:col-span-8">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Título ({TitleMax} max)</label>
                  <input 
                    value={transactionalForm.titulo} 
                    onChange={e => setTransactionalForm({ ...transactionalForm, titulo: e.target.value.slice(0, TitleMax) })} 
                    className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition" 
                    placeholder={
                      transactionalChannel === 'Relatórios & Anexos' 
                        ? 'Descreva o título do template' 
                        : ((transactionalChannel === 'WhatsApp' || transactionalChannel === 'SMS') ? 'Assunto da mensagem' : 'Assunto do e-mail')
                    } 
                    maxLength={TitleMax} 
                  />
                  <span className="text-[10px] text-slate-500">{transactionalForm.titulo.length}/{TitleMax}</span>
                </div>

                {transactionalChannel === 'E-mail' && !transactionalForm.usarHtml && (
                  <>
                    <div className="flex flex-col gap-2 md:col-span-6">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Header (imagem)</label>
                      <div className="flex items-center gap-3 flex-wrap">
                        <input type="file" accept="image/*" onChange={e => {
                          const f = e.target.files?.[0]
                          if (!f) return
                          const url = URL.createObjectURL(f)
                          setTransactionalForm({ ...transactionalForm, header: f, headerUrl: url })
                        }} className="text-slate-400" />
                        <div className="flex items-center gap-2">
                          <ToggleSwitch checked={transactionalForm.tornarHeaderPadrao} onChange={() => setTransactionalForm({ ...transactionalForm, tornarHeaderPadrao: !transactionalForm.tornarHeaderPadrao })} ariaLabel="Tornar header padrão" />
                          <span className={`${transactionalForm.tornarHeaderPadrao ? 'text-teal-400' : 'text-slate-400'} text-sm font-bold`}>Tornar Padrão</span>
                        </div>
                      </div>
                      {(transactionalForm.headerUrl || defaultEmailHeader) && (
                        <img src={transactionalForm.headerUrl || defaultEmailHeader} alt="Header preview" className="max-h-20 object-contain rounded border border-slate-800" />
                      )}
                    </div>
                    <div className="flex flex-col gap-2 md:col-span-6">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Rodapé (imagem)</label>
                      <div className="flex items-center gap-3 flex-wrap">
                        <input type="file" accept="image/*" onChange={e => {
                          const f = e.target.files?.[0]
                          if (!f) return
                          const url = URL.createObjectURL(f)
                          setTransactionalForm({ ...transactionalForm, rodape: f, rodapeUrl: url })
                        }} className="text-slate-400" />
                        <div className="flex items-center gap-2">
                          <ToggleSwitch checked={transactionalForm.tornarRodapePadrao} onChange={() => setTransactionalForm({ ...transactionalForm, tornarRodapePadrao: !transactionalForm.tornarRodapePadrao })} ariaLabel="Tornar rodapé padrão" />
                          <span className={`${transactionalForm.tornarRodapePadrao ? 'text-teal-400' : 'text-slate-400'} text-sm font-bold`}>Tornar Padrão</span>
                        </div>
                      </div>
                      {(transactionalForm.rodapeUrl || defaultEmailFooter) && (
                        <img src={transactionalForm.rodapeUrl || defaultEmailFooter} alt="Rodapé preview" className="max-h-16 object-contain rounded border border-slate-800" />
                      )}
                    </div>
                  </>
                )}

                {transactionalForm.usarHtml ? (
                  <div className="flex flex-col gap-2 md:col-span-12">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Código HTML</label>
                    <textarea 
                      ref={emailHtmlTextareaRef}
                      value={transactionalForm.textoHtml} 
                      onChange={e => setTransactionalForm({ ...transactionalForm, textoHtml: e.target.value })} 
                      className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition font-mono text-xs min-h-[400px]"
                      placeholder="Cole seu código HTML aqui..."
                    />
                  </div>
                ) : (
                  (transactionalChannel === 'WhatsApp' || transactionalChannel === 'SMS') ? (
                    <div className="flex flex-col gap-2 md:col-span-12">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Mensagem ({transactionalChannel})</label>
                      <textarea 
                        ref={whatsappTextareaRef}
                        value={transactionalForm.textoHtml} 
                        onChange={e => setTransactionalForm({ ...transactionalForm, textoHtml: e.target.value })} 
                        className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition font-mono text-sm min-h-[200px]"
                        placeholder={transactionalChannel === 'SMS' ? 'Digite a mensagem SMS' : 'Digite a mensagem do WhatsApp (use *negrito*, _itálico_, ~tachado~)'}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault()
                          const token = e.dataTransfer.getData('text/variable-token') || e.dataTransfer.getData('text/plain')
                          if (token) {
                            const input = e.target
                            const start = input.selectionStart
                            const end = input.selectionEnd
                            const text = input.value
                            const newText = text.substring(0, start) + token + text.substring(end)
                            setTransactionalForm({ ...transactionalForm, textoHtml: newText })
                            // Restore focus and selection (optional, but good UX)
                            setTimeout(() => {
                              input.focus()
                              input.setSelectionRange(start + token.length, start + token.length)
                            }, 0)
                          }
                        }}
                      />
                      {transactionalChannel === 'WhatsApp' ? (
                        <div className="flex gap-4 text-xs text-slate-500 font-mono bg-slate-950/50 p-2 rounded border border-slate-800/50">
                          <span className="flex items-center gap-1"><span className="text-slate-300 font-bold">*negrito*</span></span>
                          <span className="flex items-center gap-1"><span className="text-slate-300 italic">_itálico_</span></span>
                          <span className="flex items-center gap-1"><span className="text-slate-300 line-through">~tachado~</span></span>
                          <span className="flex items-center gap-1"><span className="text-slate-300 bg-slate-800 px-1 rounded">```código```</span></span>
                          <span className="flex items-center gap-1 ml-auto text-slate-400">Use Enter para quebra de linha</span>
                        </div>
                      ) : (
                        <div className="text-xs text-slate-500 font-mono bg-slate-950/50 p-2 rounded border border-slate-800/50">
                          Use Enter para quebra de linha.
                        </div>
                      )}
                      {transactionalChannel === 'WhatsApp' && (
                        <div className="mt-2">
                          {/* Busca de emojis */}
                          <div className="relative mb-2">
                            <input
                              type="text"
                              value={emojiSearch}
                              onChange={(e) => setEmojiSearch(e.target.value)}
                              placeholder="Buscar emoji por nome..."
                              className="w-full px-3 py-1.5 text-sm bg-slate-950/70 border border-slate-700 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500"
                            />
                            {emojiSearch.trim() && (
                              <button
                                type="button"
                                onClick={() => setEmojiSearch('')}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 text-xs"
                                aria-label="Limpar busca"
                              >
                                ✕
                              </button>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2 p-2 bg-slate-950/50 rounded border border-slate-800/50 max-h-[150px] overflow-y-auto">
                            {/* Ícone do WhatsApp */}
                            <span
                              className="cursor-pointer hover:scale-125 transition text-lg select-none flex items-center justify-center"
                              draggable
                              onDragStart={(e) => { e.dataTransfer.setData('text/plain', '💬'); e.dataTransfer.effectAllowed = 'copy' }}
                              onClick={() => insertAtCursor('💬')}
                              title="WhatsApp - Clique para inserir ou arraste"
                            >
                              <svg width="20" height="20" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M16.0001 2.66675C8.64014 2.66675 2.66675 8.64014 2.66675 16.0001C2.66675 18.3734 3.2934 20.6401 4.4534 22.6134L2.66675 29.3334L9.60014 27.6134C11.5335 28.6934 13.7201 29.3334 16.0001 29.3334C23.3601 29.3334 29.3335 23.3601 29.3335 16.0001C29.3335 8.64014 23.3601 2.66675 16.0001 2.66675Z" fill="#25D366"/>
                                <path fillRule="evenodd" clipRule="evenodd" d="M23.52 19.4667C23.24 19.08 21.7867 18.4933 21.4667 18.36C21.1467 18.2267 20.88 18.16 20.6133 18.4267C20.3467 18.6933 19.7067 19.44 19.52 19.6533C19.3333 19.8667 19.1467 19.8933 18.8 19.72C18.4533 19.5467 17.1333 19.1067 15.5733 17.7067C14.36 16.6 13.52 15.2 13.2 14.6267C12.9333 14.16 13.16 13.92 13.36 13.72C13.56 13.52 13.8133 13.2 14.0133 12.9333C14.2133 12.6667 14.2667 12.4533 14.4 12.1067C14.5333 11.76 14.4667 11.4667 14.3333 11.2C14.2 10.9333 13.2 8.45333 13 7.97333C12.8133 7.54667 12.4533 7.36 12.0667 7.36H11.2C10.8 7.36 10.24 7.53333 9.81333 7.94667C9.38667 8.36 8.62667 9.09333 8.62667 10.8533C8.62667 12.6133 9.90667 14.3067 10.0933 14.56C10.28 14.8133 12.1067 17.5733 14.8933 18.8933C15.76 19.32 16.48 19.5867 17.0667 19.7733C17.96 20.0667 18.76 20.0267 19.4133 19.96C20.0933 19.8933 21.16 19.4 21.52 18.72C21.88 18.04 21.88 17.56 21.8 17.4267C21.72 17.2933 21.4667 17.1867 21.1733 17.0533L23.52 19.4667Z" fill="white"/>
                              </svg>
                            </span>
                            {(() => {
                              const q = emojiSearch.trim().toLowerCase()
                              const list = q
                                ? EMOJI_LIST.filter(item =>
                                    item.n.toLowerCase().includes(q) ||
                                    item.k.toLowerCase().includes(q)
                                  )
                                : EMOJI_LIST
                              if (list.length === 0) {
                                return <span className="text-xs text-slate-500 py-2 px-1">Nenhum emoji encontrado.</span>
                              }
                              return list.map(item => (
                                <span
                                  key={item.e}
                                  className="cursor-pointer hover:scale-125 transition text-lg select-none"
                                  draggable
                                  onDragStart={(e) => { e.dataTransfer.setData('text/plain', item.e); e.dataTransfer.effectAllowed = 'copy' }}
                                  onClick={() => insertAtCursor(item.e)}
                                  title={`${item.n} — clique para inserir ou arraste`}
                                >
                                  {item.e}
                                </span>
                              ))
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2 md:col-span-12">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Texto</label>
                      <RichEditor value={transactionalForm.textoHtml} onChange={(html) => setTransactionalForm({ ...transactionalForm, textoHtml: html })} />
                    </div>
                  )
                )}

                <div className="md:col-span-12 space-y-6">
                  {!transactionalForm.usarHtml && (
                    <>
                      <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <ToggleSwitch checked={transactionalForm.botaoHabilitado} onChange={() => setTransactionalForm({ ...transactionalForm, botaoHabilitado: !transactionalForm.botaoHabilitado })} ariaLabel="Habilitar botão manual" />
                      <span className={`${transactionalForm.botaoHabilitado ? 'text-teal-400' : 'text-slate-300'} text-sm font-bold`}>{(transactionalChannel === 'WhatsApp' || transactionalChannel === 'SMS') ? 'Link Manual' : 'Botão Manual'}</span>
                    </div>
                    {transactionalForm.botaoHabilitado && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-1.5">
                          <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Título do Botão</label>
                          <input value={transactionalForm.botaoTitulo} onChange={e => setTransactionalForm({ ...transactionalForm, botaoTitulo: e.target.value })} className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition" />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Link do Botão</label>
                          <input value={transactionalForm.botaoLink} onChange={e => setTransactionalForm({ ...transactionalForm, botaoLink: e.target.value })} className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition" placeholder="https://" />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <ToggleSwitch checked={transactionalForm.observacaoHabilitada} onChange={() => setTransactionalForm({ ...transactionalForm, observacaoHabilitada: !transactionalForm.observacaoHabilitada })} ariaLabel="Habilitar observação" />
                      <span className={`${transactionalForm.observacaoHabilitada ? 'text-teal-400' : 'text-slate-300'} text-sm font-bold`}>Observação</span>
                    </div>
                    {transactionalForm.observacaoHabilitada && (
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Observação ({ObsMax} max)</label>
                        <textarea value={transactionalForm.observacao} onChange={e => setTransactionalForm({ ...transactionalForm, observacao: e.target.value.slice(0, ObsMax) })} rows={3} className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition resize-none" maxLength={ObsMax} />
                        <span className="text-[10px] text-slate-500">{transactionalForm.observacao.length}/{ObsMax}</span>
                      </div>
                    )}
                  </div>
                    </>
                  )}

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <ToggleSwitch checked={transactionalForm.variaveisHabilitadas} onChange={() => {
                        const next = !transactionalForm.variaveisHabilitadas
                        setTransactionalForm({
                          ...transactionalForm,
                          variaveisHabilitadas: next,
                          variaveis: next ? (transactionalForm.variaveis || buildVariaveisFromArray([])) : buildVariaveisFromArray([]),
                        })
                      }} ariaLabel="Habilitar variáveis" />
                      <span className={`${transactionalForm.variaveisHabilitadas ? 'text-teal-400' : 'text-slate-300'} text-sm font-bold`}>Variáveis (sistema)</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-h-[240px] overflow-y-auto pr-1">
                      {SYSTEM_VARIABLES.map(v => {
                        const enabled = !!transactionalForm.variaveisHabilitadas && !!transactionalForm.variaveis?.[v.k]
                        return (
                          <div key={v.k} className={`border rounded-lg p-3 flex items-center justify-between gap-3 ${enabled ? 'bg-teal-500/10 border-teal-500/30' : 'bg-slate-950 border-slate-800'}`}>
                            <div className="min-w-0 flex-1">
                              <div
                                className={`${enabled ? 'text-teal-400' : 'text-slate-300'} text-sm font-bold ${enabled ? 'cursor-pointer hover:underline' : 'cursor-not-allowed'} truncate`}
                                draggable={enabled}
                                onDragStart={(e) => { const t = v.token; e.dataTransfer.setData('text/variable-token', t); e.dataTransfer.setData('text/plain', t); e.dataTransfer.effectAllowed = 'copy' }}
                                onClick={() => {
                                  if (enabled) insertAtCursor(v.token)
                                }}
                                title={enabled ? 'Clique para inserir ou arraste' : 'Habilite para usar'}
                              >
                                {v.label}
                              </div>
                              <div className="text-[10px] font-mono text-slate-500 break-all">
                                {v.tokens.join(' ')}
                              </div>
                            </div>
                            <ToggleSwitch checked={!!transactionalForm.variaveis?.[v.k]} onChange={() => {
                              const newVal = !transactionalForm.variaveis?.[v.k]
                              setTransactionalForm({
                                ...transactionalForm,
                                variaveisHabilitadas: newVal ? true : transactionalForm.variaveisHabilitadas,
                                variaveis: { ...(transactionalForm.variaveis || buildVariaveisFromArray([])), [v.k]: newVal },
                              })
                            }} ariaLabel={`Selecionar ${v.label}`} />
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  {transactionalChannel === 'E-mail' && (
                    <div className="space-y-2 md:col-span-12">
                      <div className="mt-2 p-4 rounded-xl border-2 border-dashed border-slate-700 bg-slate-950/50 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-bold text-slate-300">Anexos do e-mail</span>
                          <span className="text-xs text-slate-500">Máx 2 arquivos (PDF, JPG, PNG, DOCX)</span>
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <label className="px-3 py-2 rounded-lg bg-slate-800 text-slate-200 text-sm cursor-pointer hover:bg-slate-700">
                            Escolher arquivos
                            <input type="file" multiple accept={acceptAttr} className="hidden" onChange={(e) => validateAndSetAttachments(e.target.files)} />
                          </label>
                          {transactionalForm.anexos && transactionalForm.anexos.length > 0 && (
                            <div className="flex flex-col gap-2 w-full">
                              {transactionalForm.anexos.map((a, idx) => (
                                <div key={idx} className="flex items-center justify-between bg-slate-900 border border-slate-700 rounded-lg px-3 py-2">
                                  <div className="flex items-center gap-2">
                                    <div className="text-sm text-slate-300">
                                      {a.name} <span className="text-xs text-slate-500">({(a.size / 1024 / 1024).toFixed(2)} MB)</span>
                                    </div>
                                    {a.url && (
                                      <a href={a.url} download={a.name} className="text-teal-400 hover:text-teal-300 text-xs font-bold flex items-center gap-1" title="Baixar anexo">
                                        <Paperclip size={12} />
                                      </a>
                                    )}
                                  </div>
                                  <button className="text-slate-400 hover:text-rose-400 text-xs font-bold" onClick={() => setTransactionalForm({ ...transactionalForm, anexos: transactionalForm.anexos.filter((_, i) => i !== idx) })}>Remover</button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-slate-800">
              <button
                onClick={() => {
                  setShowTransactionalModal(false)
                  setEditingTemplate(null)
                }}
                className="px-4 py-2 text-slate-400 font-bold hover:text-white transition"
              >Cancelar</button>
              <button onClick={handleTransactionalSave} style={{ backgroundColor: partnerColor }} className="px-6 py-2 rounded-lg text-white font-bold shadow-lg hover:opacity-90 transition">Salvar Template</button>
            </div>
          </div>
        </div>
      )}

      {showPreviewModal && previewTemplate && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-6xl shadow-2xl max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-slate-800">
              <h3 className="text-lg font-bold text-white">
                {getPreviewModalTitle(previewTemplate)}
              </h3>
              <div className="flex gap-2">
                <button onClick={() => setShowPreviewModal(false)} className="text-slate-400 hover:text-white"><X size={20} /></button>
              </div>
            </div>
            <div className={`p-4 overflow-auto bg-slate-800 ${(previewTemplate.channel === 'WhatsApp' || previewTemplate.channel === 'SMS') ? 'flex justify-center items-center min-h-[500px]' : ''}`}>
              {(previewTemplate.channel === 'WhatsApp' || previewTemplate.channel === 'SMS') ? (
                <div className="w-full max-w-sm bg-[#E5DDD5] p-4 rounded-lg shadow-lg" style={{ backgroundImage: 'url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png")' }}>
                  <div className="bg-white rounded-lg shadow-sm p-2 relative">
                    {previewTemplate.meta?.headerUrl && (
                      <div className="mb-2">
                         <img src={previewTemplate.meta.headerUrl} alt="Header" className="w-full rounded-lg object-cover max-h-48" />
                      </div>
                    )}
                    
                    <div 
                      className="text-sm text-slate-900 whitespace-pre-wrap leading-snug break-words font-[Helvetica,Arial,sans-serif]"
                      dangerouslySetInnerHTML={{ __html: formatWhatsAppText(previewTemplate.content || previewTemplate.meta?.textoHtml, previewTemplate) }}
                    />
                    
                    {previewTemplate.meta?.rodapeUrl && (
                      <div className="mt-2">
                         <img src={previewTemplate.meta.rodapeUrl} alt="Footer" className="w-full rounded-lg object-cover max-h-32" />
                      </div>
                    )}

                    <div className="text-[10px] text-slate-400 text-right mt-1 flex justify-end items-center gap-1">
                      {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      <span className="text-blue-500">✓✓</span>
                    </div>
                  </div>
                  
                  {previewTemplate.meta?.botao && (previewTemplate.meta.botao.titulo || previewTemplate.meta.botao.link) && (
                     <div className="mt-2 bg-white rounded-lg text-center py-2 text-sky-500 font-medium cursor-pointer shadow-sm text-sm hover:bg-slate-50 transition">
                        {applyPreviewVariables(previewTemplate, previewTemplate.meta.botao.titulo) || 'Acessar Link'}
                     </div>
                  )}
                </div>
              ) : (
                <iframe title="preview" style={{ width: '100%', height: '70vh', background: 'white', borderRadius: '12px' }} srcDoc={buildTransactionalHtml(previewTemplate)} />
              )}
            </div>
          </div>
        </div>
      )}
      {showManualSendModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-6xl shadow-2xl flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center p-6 border-b border-slate-800">
              <h3 className="text-xl font-bold text-white">Novo Envio Manual</h3>
              <button onClick={() => setShowManualSendModal(false)} className="text-slate-500 hover:text-white"><X size={20} /></button>
            </div>

            <div className="p-6 space-y-6 overflow-y-auto">
              {loadingManualOptions ? (
                <div className="flex justify-center py-10"><RefreshCcw className="animate-spin text-slate-500" /></div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Filters Section */}
                  <div className="md:col-span-2 bg-slate-950/50 p-4 rounded-xl border border-slate-800 space-y-4">
                    <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-2">Destinatários</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <MultiSelectDropdown 
                            label="PERFIS"
                            dropdownKey="roles"
                            options={roleOptions.map(r => ({ id: r, label: r }))}
                            selectedIds={manualSendForm.roles}
                            onChange={(ids) => setManualSendForm({ ...manualSendForm, roles: ids })}
                            placeholder="Todos os perfis"
                        />

                        <MultiSelectDropdown 
                            label="EMPRESA"
                            dropdownKey="companies"
                            options={companies.map(c => ({ id: c.id, label: c.name }))}
                            selectedIds={manualSendForm.companyIds}
                            onChange={(ids) => setManualSendForm({ ...manualSendForm, companyIds: ids })}
                            placeholder="Todas"
                        />

                        <MultiSelectDropdown 
                            label="PARCEIRO SAS"
                            dropdownKey="partners"
                            options={partners.map(p => ({ id: p.id, label: p.name }))}
                            selectedIds={manualSendForm.partnerIds}
                            onChange={(ids) => setManualSendForm({ ...manualSendForm, partnerIds: ids })}
                            placeholder="Todos"
                        />

                        <MultiSelectDropdown 
                            label="TURMA"
                            dropdownKey="classes"
                            options={classes.filter(c => manualSendForm.companyIds.length === 0 || manualSendForm.companyIds.includes(c.company_id)).map(c => ({ id: c.id, label: c.name }))}
                            selectedIds={manualSendForm.classIds}
                            onChange={(ids) => setManualSendForm({ ...manualSendForm, classIds: ids })}
                            placeholder="Todas"
                        />

                        <MultiSelectDropdown 
                            label="CURSO"
                            dropdownKey="courses"
                            options={courses.map(c => ({ id: c.id, label: c.name }))}
                            selectedIds={manualSendForm.courseIds}
                            onChange={(ids) => setManualSendForm({ ...manualSendForm, courseIds: ids })}
                            placeholder="Todos"
                        />

                        <MultiSelectDropdown 
                            label="STATUS DO CURSO"
                            dropdownKey="statuses"
                            options={['Em Andamento', 'Concluído', 'Não Iniciado', 'Aprovado', 'Reprovado'].map(s => ({ id: s, label: s }))}
                            selectedIds={manualSendForm.courseStatuses}
                            onChange={(ids) => setManualSendForm({ ...manualSendForm, courseStatuses: ids })}
                            placeholder="Qualquer"
                        />
                    </div>
                  </div>

                  {/* Message Content */}
                  <div className="md:col-span-2 space-y-4">
                      <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider">Conteúdo da Mensagem</h4>
                      
                      <div>
                          <label className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1 block">Assunto do E-mail</label>
                          <input 
                              type="text"
                              value={manualSendForm.emailSubject}
                              onChange={e => setManualSendForm({...manualSendForm, emailSubject: e.target.value})}
                              className="w-full bg-slate-950 border border-slate-800 rounded-lg p-3 text-sm text-white outline-none focus:border-slate-500 transition mb-4"
                              placeholder="Digite o assunto do e-mail..."
                          />

                          <label className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1 block">Texto Livre (Opcional)</label>
                          <textarea 
                              value={manualSendForm.messageText}
                              onChange={e => setManualSendForm({...manualSendForm, messageText: e.target.value})}
                              rows={6}
                              className="w-full bg-slate-950 border border-slate-800 rounded-lg p-3 text-sm text-white outline-none focus:border-slate-500 transition resize-none min-h-[150px]"
                              placeholder="Digite uma mensagem personalizada se desejar..."
                          />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* WhatsApp Template */}
                          <div className="bg-slate-950/30 p-4 rounded-xl border border-slate-800">
                              <div className="flex items-center gap-2 mb-3">
                                  <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center text-green-500">
                                      <MessageCircle size={18} />
                                  </div>
                                  <span className="font-bold text-white text-sm">WhatsApp</span>
                              </div>
                              <select 
                                  value={manualSendForm.whatsappTemplateId}
                                  onChange={e => setManualSendForm({...manualSendForm, whatsappTemplateId: e.target.value})}
                                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-slate-500 mb-2"
                              >
                                  <option value="">Sem template vinculado</option>
                                  {(templates || []).filter(t => t.channel === 'WhatsApp' && t.is_active !== false).map(t => (
                                      <option key={t.id} value={t.id}>{t.name || t.subject}</option>
                                  ))}
                              </select>
                              <p className="text-xs text-slate-500">Selecione um template para enviar via WhatsApp.</p>
                          </div>

                          {/* Email Template */}
                          <div className="bg-slate-950/30 p-4 rounded-xl border border-slate-800">
                              <div className="flex items-center gap-2 mb-3">
                                  <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-500">
                                      <Mail size={18} />
                                  </div>
                                  <span className="font-bold text-white text-sm">E-mail</span>
                              </div>
                              <select 
                                  value={manualSendForm.emailTemplateId}
                                  onChange={e => setManualSendForm({...manualSendForm, emailTemplateId: e.target.value})}
                                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-slate-500 mb-2"
                              >
                                  <option value="">Sem template vinculado</option>
                                  {(templates || []).filter(t => t.channel === 'E-mail' && t.is_active !== false).map(t => (
                                      <option key={t.id} value={t.id}>{t.name || t.subject}</option>
                                  ))}
                              </select>
                              <p className="text-xs text-slate-500">Selecione um template para enviar via E-mail.</p>
                          </div>
                      </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 p-6 border-t border-slate-800 mt-auto bg-slate-900 rounded-b-xl">
              <button
                onClick={() => setShowManualSendModal(false)}
                className="px-4 py-2 text-slate-400 font-bold hover:text-white transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleManualSend}
                className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-lg shadow-lg transition flex items-center gap-2"
              >
                <Send size={16} />
                Enviar Mensagens
              </button>
            </div>
          </div>
        </div>
      )}
      {showMessageModal && selectedMessage && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-2xl shadow-2xl max-h-[85vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-slate-800">
              <h3 className="text-lg font-bold text-white">Detalhes do Envio</h3>
              <button onClick={() => setShowMessageModal(false)} className="text-slate-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-4 space-y-3 overflow-auto">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="bg-slate-950 border border-slate-800 rounded p-3">
                  <div className="text-slate-500 font-bold uppercase">Para</div>
                  <div className="text-white">{selectedMessage.recipient}</div>
                </div>
                <div className="bg-slate-950 border border-slate-800 rounded p-3">
                  <div className="text-slate-500 font-bold uppercase">Provider Event</div>
                  <div className="text-white">{selectedMessage.provider_event || '-'}</div>
                </div>
                <div className="bg-slate-950 border border-slate-800 rounded p-3">
                  <div className="text-slate-500 font-bold uppercase">Provider</div>
                  <div className="text-white">{selectedMessage.provider || '-'}</div>
                </div>
                <div className="bg-slate-950 border border-slate-800 rounded p-3">
                  <div className="text-slate-500 font-bold uppercase">Provider Message ID</div>
                  <div className="text-white font-mono text-[11px] break-all">{selectedMessage.provider_message_id || '-'}</div>
                </div>
                <div className="bg-slate-950 border border-slate-800 rounded p-3 col-span-2">
                  <div className="text-slate-500 font-bold uppercase">Título</div>
                  <div className="text-white">{selectedMessage.title || '-'}</div>
                </div>
                <div className="bg-slate-950 border border-slate-800 rounded p-3 col-span-2">
                  <div className="text-slate-500 font-bold uppercase">Payload (Bruto)</div>
                  <pre className="text-slate-300 text-[11px] whitespace-pre-wrap break-words">{selectedMessage.provider_payload ? JSON.stringify(selectedMessage.provider_payload, null, 2) : '-'}</pre>
                </div>
                <div className="bg-slate-950 border border-slate-800 rounded p-3 col-span-2">
                  <div className="text-slate-500 font-bold uppercase">Erro (Detalhes)</div>
                  <pre className="text-rose-300 text-[11px] whitespace-pre-wrap break-words">
                    {selectedMessage.error
                      ? (typeof selectedMessage.error === 'string'
                        ? selectedMessage.error
                        : JSON.stringify(selectedMessage.error, null, 2))
                      : '-'}
                  </pre>
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-slate-800 flex justify-end">
              <button onClick={() => setShowMessageModal(false)} className="px-4 py-2 rounded bg-slate-800 text-white font-bold hover:bg-slate-700">Fechar</button>
            </div>
          </div>
        </div>
      )}

      {showEmailSupportModal && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-xl shadow-2xl">
            <div className="flex justify-between items-center p-6 border-b border-slate-800">
              <div>
                <h3 className="text-xl font-bold text-white">Editar E-mail de Suporte</h3>
                <p className="text-sm text-slate-400 mt-1">
                  Defina o endereço usado nas comunicações e templates com <code className="font-mono text-slate-300">{'{{email_suporte}}'}</code>.
                </p>
              </div>
              <button onClick={() => setShowEmailSupportModal(false)} className="text-slate-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="rounded-lg border border-slate-800 bg-slate-950 p-4 flex items-start gap-3">
                <div className="mt-0.5 text-orange-400">
                  <Mail size={18} />
                </div>
                <div>
                  <div className="text-sm font-semibold text-white">Canal de suporte por e-mail</div>
                  <div className="text-xs text-slate-400 mt-1">
                    Use um endereço monitorado pela equipe. Esse valor substitui o e-mail fixo usado nas automações quando estiver preenchido.
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">E-mail de suporte</label>
                <input
                  type="email"
                  value={supportEmailDraft}
                  onChange={(event) => {
                    setSupportEmailDraft(event.target.value)
                    setEmailStatus('Pendente')
                  }}
                  placeholder="suporte@empresa.com.br"
                  className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition"
                />
                <p className="text-[11px] text-slate-500">
                  Exemplo de uso: links <code className="font-mono text-slate-300">mailto:</code> em templates e placeholder <code className="font-mono text-slate-300">{'{{email_suporte}}'}</code>.
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-slate-800 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setSupportEmailDraft(supportEmail)
                  setShowEmailSupportModal(false)
                }}
                className="px-4 py-2 text-slate-400 font-bold hover:text-white transition"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSaveEmailConfig}
                disabled={loadingEmailConfig}
                style={{ backgroundColor: partnerColor }}
                className={`px-5 py-2 rounded-lg text-white font-bold flex items-center gap-2 shadow-lg hover:opacity-90 transition ${loadingEmailConfig ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                <Check size={18} /> Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'evaluations' && (
        <AdminEvaluations partner={partner} role={role} />
      )}

      {activeTab === 'notices' && (
        <AdminNotices partner={partner} role={role} />
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={async () => {
          const fn = confirmModal.onConfirm
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })
          if (typeof fn === 'function') await fn()
        }}
        onCancel={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })}
      />

      {forcedDetailsModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setForcedDetailsModal({ isOpen: false, routineId: null, routineTitle: '', details: null })} />
          <div className="relative w-full max-w-6xl rounded-2xl border border-slate-800 bg-slate-900 shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-800 px-5 py-4">
              <div className="min-w-0">
                <div className="text-lg font-semibold text-white">Nenhum usuário elegível agora</div>
                <div className="mt-1 text-sm text-slate-400 truncate">{forcedDetailsModal.routineTitle}</div>
              </div>
              <button
                type="button"
                onClick={() => setForcedDetailsModal({ isOpen: false, routineId: null, routineTitle: '', details: null })}
                className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white hover:border-slate-500"
              >
                Fechar
              </button>
            </div>

            <div className="max-h-[80vh] overflow-auto px-5 py-4">
              <div className="text-sm text-slate-200">
                Base (após filtros):{' '}
                <span className="font-semibold text-white">
                  {typeof forcedDetailsModal.details?.baseUsersCount === 'number' ? forcedDetailsModal.details.baseUsersCount : 0}
                </span>
              </div>

              {Array.isArray(forcedDetailsModal.details?.notes) && forcedDetailsModal.details.notes.filter(Boolean).length > 0 && (
                <div className="mt-3 rounded-xl border border-amber-900/40 bg-amber-950/20 p-3 text-sm text-amber-100">
                  <div className="font-semibold">Observações</div>
                  <ul className="mt-2 list-disc pl-5">
                    {forcedDetailsModal.details.notes.filter(Boolean).map((n, idx) => (
                      <li key={idx} className="text-amber-100/90">{n}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-4 rounded-2xl border border-slate-800 bg-slate-950/40">
                <div className="overflow-x-auto">
                  <table className="min-w-[980px] w-full text-left text-sm">
                    <thead className="sticky top-0 bg-slate-950">
                      <tr className="text-slate-400">
                        <th className="px-4 py-3 font-semibold">Evento</th>
                        <th className="px-4 py-3 font-semibold">Momento</th>
                        <th className="px-4 py-3 font-semibold">1º (h)</th>
                        <th className="px-4 py-3 font-semibold">Repetir (h)</th>
                        <th className="px-4 py-3 font-semibold">Máx</th>
                        <th className="px-4 py-3 font-semibold">E-mail</th>
                        <th className="px-4 py-3 font-semibold">WhatsApp</th>
                        <th className="px-4 py-3 font-semibold">Tentativas (Canais)</th>
                        <th className="px-4 py-3 font-semibold">Motivo</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                      {(Array.isArray(forcedDetailsModal.details?.events) ? forcedDetailsModal.details.events : []).map((ev, idx) => {
                        const email = ev?.email || {}
                        const wa = ev?.whatsapp || {}
                        const attempts = forcedAttemptsByEvent[String(ev?.event || '')] || []
                        const skipReason = ev?.skipReason || ''
                        const rowMuted = skipReason && String(skipReason).length > 0
                        return (
                          <tr key={idx} className={rowMuted ? 'bg-slate-950/30 text-slate-300' : 'text-slate-200'}>
                            <td className="px-4 py-3 font-mono text-xs text-slate-200">{ev?.event || '-'}</td>
                            <td className="px-4 py-3 text-xs">{ev?.moment || '-'}</td>
                            <td className="px-4 py-3 text-xs">{typeof ev?.firstReminderHours === 'number' ? ev.firstReminderHours : '-'}</td>
                            <td className="px-4 py-3 text-xs">{typeof ev?.repeatEveryHours === 'number' ? ev.repeatEveryHours : '-'}</td>
                            <td className="px-4 py-3 text-xs">{typeof ev?.maxSends === 'number' ? ev.maxSends : '-'}</td>
                            <td className="px-4 py-3 text-xs">
                              <div className="flex flex-col gap-0.5">
                                <div>Cand: <span className="text-white">{email?.candidates ?? 0}</span></div>
                                <div>Eleg: <span className="text-white">{email?.eligible ?? 0}</span></div>
                                <div>Fila: <span className="text-white">{email?.queued ?? 0}</span></div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-xs">
                              <div className="flex flex-col gap-0.5">
                                <div>Cand: <span className="text-white">{wa?.candidates ?? 0}</span></div>
                                <div>Sem WA: <span className="text-white">{wa?.missingWhatsapp ?? 0}</span></div>
                                <div>Eleg: <span className="text-white">{wa?.eligible ?? 0}</span></div>
                                <div>Fila: <span className="text-white">{wa?.queued ?? 0}</span></div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-xs">
                              {attempts.length > 0 ? (
                                <div className="flex flex-col gap-1">
                                  {attempts.slice(0, 5).map((a, i) => (
                                    <div key={`${String(ev?.event || 'event')}-${i}`} className="text-slate-300">
                                      <span className="text-white">{a?.channel || '-'}</span>
                                      {' · '}
                                      <span>{a?.status || '-'}</span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-slate-500">Sem histórico</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-xs text-slate-400">{skipReason || '-'}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      <InfoModal 
        isOpen={feedbackModal.isOpen}
        title={feedbackModal.title}
        content={feedbackModal.content}
        onClose={() => setFeedbackModal({ ...feedbackModal, isOpen: false })}
      />
    </div>
  )
}
